import {
  AlertTriangle,
  ArrowRight,
  ArrowLeft,
  Bell,
  BellOff,
  Camera,
  CalendarClock,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  Cross,
  Download,
  Eye,
  FileText,
  Folder,
  HeartPulse,
  ImageIcon,
  KeyRound,
  LayoutDashboard,
  Lock,
  LogOut,
  MoreHorizontal,
  Pencil,
  Pill,
  Plus,
  Settings,
  Share2,
  ShieldAlert,
  ShieldCheck,
  ShoppingCart,
  Siren,
  Smartphone,
  Stethoscope,
  Syringe,
  Trash2,
  Upload,
  UserRound,
  X,
  type LucideIcon,
} from "lucide-react";
import { parsePhoneNumberFromString, type CountryCode } from "libphonenumber-js";
import { QRCodeSVG } from "qrcode.react";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type Dispatch, type FormEvent, type ReactNode, type SetStateAction } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { graceDaysRemaining } from "../account/client";
import type { AccountProfile, AccountProfilePatch, EmergencyContact, HealthInsurance, HealthProfileExportSection, PreferredHospital, PreferredHospitals, PrimaryDoctor, ProfileAddress } from "../account/client";
import { findPhoneCountry, phoneCountries, type PhoneCountry } from "../account/countries";
import { useAccountLifecycle } from "../account/useAccountLifecycle";
import { useAccountProfile } from "../account/useAccountProfile";
import { formatSecretForDisplay } from "../auth/cognitoIdp";
import { useAuth } from "../auth/useAuth";
import { useCognitoSecurity, type TotpEnrolment } from "../auth/useCognitoSecurity";
import { BrandedLoader } from "../components/BrandedLoader";
import { MfaStatusIndicator } from "../components/MfaStatusIndicator";
import { PasswordField } from "../components/PasswordField";
import { assignLocation } from "../lib/browserNavigation";
import { PdfDocumentViewer } from "../documents/PdfDocumentViewer";
import { validateDocumentFile, type DocumentFile, type DocumentFolder } from "../documents/client";
import { useDocuments, type ShareProgress } from "../documents/useDocuments";
import { useDocumentUpload } from "../documents/useDocumentUpload";
import { EmergencyCard } from "../emergencyAccess/EmergencyCard";
import type { EmergencyCard as EmergencyCardData, EmergencyContactField as EmergencyAccessContactField, EmergencyIdentityField as EmergencyAccessIdentityField } from "../emergencyAccess/client";
import { useEmergencyAccess } from "../emergencyAccess/useEmergencyAccess";
import type { Allergy, AllergyCategory, AllergyInput, AllergySeverity, AllergyStatus, Appointment, AppointmentInput, AppointmentMode, AppointmentStatus, AppointmentType, ExtraReminderOption, HealthCondition, HealthConditionInput, HealthConditionStatus, Vaccination, VaccinationInput } from "../healthRecords/client";
import { useAppointments } from "../healthRecords/useAppointments";
import { useClinicalRecords } from "../healthRecords/useClinicalRecords";
import { useHealthOverview } from "../healthRecords/useHealthOverview";
import { useVaccinations } from "../healthRecords/useVaccinations";
import { cn } from "../lib/utils";
import type { DoseEventStatus, Medication, MedicationInput, MedicationScheduleSlot, MedicationStatus, MedicationType, UpcomingDose } from "../medications/client";
import { useMedications } from "../medications/useMedications";
import { useMedicationList } from "../medications/useMedicationList";
import { useMedicationOverview } from "../medications/useMedicationOverview";
import { useUpcomingDoses } from "../medications/useUpcomingDoses";
import type { MedicineOrder, MedicineOrderStatus } from "../medicineOrders/client";
import { useMedicineOrders } from "../medicineOrders/useMedicineOrders";
import { useMedicineOrdersFolder } from "../medicineOrders/useMedicineOrdersFolder";
import { describePrescriptionUploadStage } from "../medicineOrders/uploadStage";
import { openCareRemindersDialog } from "../notifications/careRemindersDialogStore";

const workspaceNav: Array<{ label: string; to: string; icon: LucideIcon; activeFor?: string[]; end?: boolean }> = [
  { label: "Home", to: "/app", icon: LayoutDashboard, activeFor: ["/app/dashboard", "/app/upcoming-care"], end: true },
  {
    label: "Health",
    to: "/app/health-records",
    icon: HeartPulse,
    activeFor: ["/app/allergies", "/app/known-health-conditions", "/app/vaccinations", "/app/medications", "/app/appointments"],
  },
  { label: "Emergency", to: "/app/emergency", icon: Cross },
  {
    label: "More",
    to: "/app/more",
    icon: MoreHorizontal,
    activeFor: ["/app/profile", "/app/settings", "/app/documents", "/app/orders"],
  },
];

const addActions: Array<{ label: string; to: string; icon: LucideIcon }> = [
  { label: "Medication", to: "/app/medications/new", icon: Pill },
  { label: "Vaccination", to: "/app/vaccinations/new", icon: Syringe },
  { label: "Allergy", to: "/app/allergies/new", icon: AlertTriangle },
  { label: "Health Condition", to: "/app/known-health-conditions/new", icon: HeartPulse },
  { label: "Insurance", to: "/app/profile/insurance", icon: ShieldCheck },
  { label: "Emergency Contact", to: "/app/emergency?edit=contacts", icon: UserRound },
];

const healthProfileExportOptions: Array<{ id: HealthProfileExportSection; label: string }> = [
  { id: "personal_details", label: "Personal Details" },
  { id: "health_insurance", label: "Health Insurance" },
  { id: "allergies", label: "Allergies" },
  { id: "known_health_conditions", label: "Known Health Conditions" },
  { id: "vaccinations", label: "Vaccinations" },
  { id: "active_medications", label: "Active Medications" },
  { id: "primary_doctor", label: "Primary Doctor" },
  { id: "emergency_contacts", label: "Emergency Contacts" },
  { id: "preferred_hospitals", label: "Preferred Hospitals" },
];

const bloodGroups = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const sexAssignedAtBirthOptions = [
  { value: "", label: "Not added yet" },
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: "intersex", label: "Intersex" },
  { value: "unknown", label: "Unknown" },
  { value: "prefer_not_to_say", label: "Prefer not to say" },
];

const placeTypeOptions = [
  { value: "", label: "Select place type" },
  { value: "hospital", label: "Hospital" },
  { value: "clinic", label: "Clinic" },
  { value: "vaccination_centre", label: "Vaccination Centre" },
  { value: "pharmacy", label: "Pharmacy" },
  { value: "other", label: "Other" },
];

const currencyOptions = [
  { value: "", label: "Select currency" },
  { value: "INR", label: "INR — Indian Rupee" },
  { value: "USD", label: "USD — US Dollar" },
  { value: "EUR", label: "EUR — Euro" },
  { value: "GBP", label: "GBP — British Pound" },
  { value: "AUD", label: "AUD — Australian Dollar" },
  { value: "CAD", label: "CAD — Canadian Dollar" },
  { value: "AED", label: "AED — UAE Dirham" },
  { value: "SGD", label: "SGD — Singapore Dollar" },
  { value: "JPY", label: "JPY — Japanese Yen" },
  { value: "CHF", label: "CHF — Swiss Franc" },
  { value: "CNY", label: "CNY — Chinese Yuan" },
  { value: "NZD", label: "NZD — New Zealand Dollar" },
  { value: "ZAR", label: "ZAR — South African Rand" },
];

const paymentModeOptions = [
  { value: "", label: "Select payment method" },
  { value: "Cash", label: "Cash" },
  { value: "Credit Card", label: "Credit Card" },
  { value: "Debit Card", label: "Debit Card" },
  { value: "UPI", label: "UPI" },
  { value: "Bank Transfer", label: "Bank Transfer" },
  { value: "Insurance", label: "Insurance" },
  { value: "Other", label: "Other" },
];

// A short, generic dropdown -- "doctor_visit" deliberately covers dentist, eye doctor, and
// specialists alike rather than a top-level type per specialty. Vaccination is excluded: that
// already has its own dedicated flow (see the Vaccinations pages/hooks elsewhere in this file).
const appointmentTypeOptions: Array<{ value: AppointmentType; label: string }> = [
  { value: "doctor_visit", label: "Doctor Visit" },
  { value: "test_diagnostic", label: "Test / Diagnostic" },
  { value: "therapy_treatment", label: "Therapy / Treatment" },
  { value: "follow_up", label: "Follow-up" },
  { value: "health_checkup", label: "Health Checkup" },
  { value: "other", label: "Other" },
];

const appointmentModeOptions: Array<{ value: AppointmentMode; label: string }> = [
  { value: "in_person", label: "In Person" },
  { value: "video", label: "Video" },
  { value: "phone", label: "Phone" },
];

// The one optional advance reminder's lead-time presets -- a short enum, not a freeform offset, to
// keep the form simple. Must exactly match medopilot-health-record-service's EXTRA_REMINDER_OPTIONS.
const extraReminderOptions: Array<{ value: ExtraReminderOption | ""; label: string }> = [
  { value: "", label: "No advance reminder" },
  { value: "2_days_before", label: "2 days before" },
  { value: "1_day_before", label: "1 day before" },
  { value: "2_hours_before", label: "2 hours before" },
  { value: "1_hour_before", label: "1 hour before" },
  { value: "30_minutes_before", label: "30 minutes before" },
];

function appointmentTypeLabel(value: AppointmentType) {
  return appointmentTypeOptions.find((option) => option.value === value)?.label ?? "Appointment";
}

function appointmentModeLabel(value: AppointmentMode) {
  return appointmentModeOptions.find((option) => option.value === value)?.label ?? "In Person";
}

function extraReminderLabel(value: ExtraReminderOption) {
  return extraReminderOptions.find((option) => option.value === value)?.label ?? null;
}

function getClaim(profile: Record<string, unknown> | undefined, keys: string[]) {
  for (const key of keys) {
    const value = profile?.[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return null;
}

function getAccountSummary(user: ReturnType<typeof useAuth>["user"]) {
  const profile = user?.profile as Record<string, unknown> | undefined;
  const fullName = getClaim(profile, ["name"]);
  const givenName = getClaim(profile, ["given_name"]);
  const familyName = getClaim(profile, ["family_name"]);
  const email = getClaim(profile, ["email", "preferred_username", "cognito:username", "username"]);
  const composedName = [givenName, familyName].filter(Boolean).join(" ");
  const displayName = fullName ?? (composedName || email) ?? "MedoPilot member";

  return {
    displayName,
    email,
    initials: displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "M",
  };
}

function getProfileDisplayName(profile: { first_name?: string | null; last_name?: string | null } | null | undefined) {
  const name = [profile?.first_name, profile?.last_name].map((part) => part?.trim()).filter(Boolean).join(" ");
  return name || null;
}

function getFirstNameFromSession(user: ReturnType<typeof useAuth>["user"]) {
  const profile = user?.profile as Record<string, unknown> | undefined;
  const givenName = getClaim(profile, ["given_name"]);
  if (givenName) {
    return givenName.split(/\s+/)[0] || givenName;
  }

  const fullName = getClaim(profile, ["name"]);
  return fullName?.split(/\s+/)[0] ?? null;
}

function WorkspaceBrand() {
  return (
    <a href="/app" className="flex min-w-0 items-center gap-3" aria-label="MedoPilot Care Hub home">
      <img src="/medopilot-icon.png" alt="" className="size-11 shrink-0" width={44} height={44} />
      <span className="min-w-0">
        <span className="brand-wordmark block text-[1.28rem] text-foreground">MedoPilot</span>
        <span className="mt-1 block text-xs font-semibold leading-none text-muted-foreground">Your Health Companion</span>
      </span>
    </a>
  );
}

function isWorkspaceNavActive(pathname: string, item: (typeof workspaceNav)[number], isActive: boolean) {
  return Boolean(
    isActive ||
      item.activeFor?.some((path) => pathname === path || pathname.startsWith(`${path}/`)),
  );
}

function AddActionMenu({ variant = "desktop" }: { variant?: "desktop" | "mobile" }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (menuRef.current?.contains(event.target as Node)) {
        return;
      }

      setOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handleAction = (to: string) => {
    setOpen(false);
    void navigate(to);
  };

  if (variant === "mobile") {
    return (
      <div ref={menuRef} className="relative -mt-7 flex flex-col items-center">
        <button
          type="button"
          onClick={() => setOpen((nextOpen) => !nextOpen)}
          className="grid size-14 place-items-center rounded-full border border-white bg-primary text-primary-foreground shadow-[0_18px_34px_-18px_rgba(10,111,216,0.9)] transition hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/20"
          aria-label="Add new health information"
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <Plus className="size-6" aria-hidden="true" />
        </button>
        <span className="mt-1 text-[0.68rem] font-semibold text-primary">Add</span>
        {open ? (
          <div
            className="absolute bottom-[calc(100%+0.75rem)] left-1/2 z-50 w-[min(20rem,calc(100vw-2rem))] -translate-x-1/2 rounded-2xl border border-primary/10 bg-white p-2 shadow-lift"
            role="menu"
          >
            {addActions.map((action) => (
              <button
                key={action.label}
                type="button"
                onClick={() => handleAction(action.to)}
                className="flex min-h-12 w-full items-center gap-3 rounded-xl px-3 text-left text-sm font-semibold text-foreground transition hover:bg-surface focus:outline-none focus:ring-4 focus:ring-primary/10"
                role="menuitem"
              >
                <action.icon className="size-4 shrink-0 text-primary" aria-hidden="true" />
                {action.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((nextOpen) => !nextOpen)}
        className="flex w-full items-center gap-3 rounded-2xl border border-border bg-white px-3.5 py-3 text-sm font-semibold text-primary shadow-soft transition hover:border-primary/30 hover:bg-primary/5 focus:outline-none focus:ring-4 focus:ring-primary/15"
        aria-label="Add new health information"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Plus className="size-4 shrink-0" aria-hidden="true" />
        Add New
      </button>
      {open ? (
        <div className="absolute left-0 right-0 top-[calc(100%+0.5rem)] z-40 rounded-2xl border border-border bg-white p-2 shadow-lift" role="menu">
          {addActions.map((action) => (
            <button
              key={action.label}
              type="button"
              onClick={() => handleAction(action.to)}
              className="flex min-h-10 w-full items-center gap-3 rounded-xl px-3 text-left text-sm font-semibold text-muted-foreground transition hover:bg-secondary hover:text-foreground"
              role="menuitem"
            >
              <action.icon className="size-4 shrink-0 text-primary" aria-hidden="true" />
              {action.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function WorkspaceNav({ onNavigate, variant = "desktop" }: { onNavigate?: () => void; variant?: "desktop" | "mobile" }) {
  const location = useLocation();
  const navItems = workspaceNav;

  if (variant === "mobile") {
    const [homeItem, healthItem, emergencyItem, moreItem] = navItems;

    return (
      <nav
        aria-label="Care Hub"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-primary/10 bg-white/95 px-3 pb-[calc(0.5rem+env(safe-area-inset-bottom))] pt-2 shadow-[0_-18px_44px_-34px_rgba(15,23,42,0.55)] backdrop-blur lg:hidden"
      >
        <div className="mx-auto grid max-w-md grid-cols-5 items-end gap-1">
          {[homeItem, healthItem].map((item) => (
            <MobileNavLink key={item.to} item={item} onNavigate={onNavigate} />
          ))}
          <AddActionMenu variant="mobile" />
          {[emergencyItem, moreItem].map((item) => (
            <MobileNavLink key={item.to} item={item} onNavigate={onNavigate} />
          ))}
        </div>
      </nav>
    );
  }

  return (
    <nav aria-label="Care Hub" className="grid gap-1">
      {navItems.slice(0, 2).map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          className={({ isActive }) => {
            const isRelatedActive = isWorkspaceNavActive(location.pathname, item, isActive);
            return cn(
              "flex items-center gap-3 rounded-2xl px-3.5 py-3 text-sm font-semibold transition",
              isRelatedActive
                ? "bg-primary text-primary-foreground shadow-[0_18px_34px_-24px_rgba(7,95,198,0.9)]"
                : "text-muted-foreground hover:bg-secondary hover:text-foreground",
            );
          }}
        >
          <item.icon className="size-4 shrink-0" aria-hidden="true" />
          {item.label}
        </NavLink>
      ))}
      <div className="py-1">
        <AddActionMenu />
      </div>
      {navItems.slice(2).map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          className={({ isActive }) => {
            const isRelatedActive = isWorkspaceNavActive(location.pathname, item, isActive);
            return cn(
              "flex items-center gap-3 rounded-2xl px-3.5 py-3 text-sm font-semibold transition",
              isRelatedActive
                ? "bg-primary text-primary-foreground shadow-[0_18px_34px_-24px_rgba(7,95,198,0.9)]"
                : "text-muted-foreground hover:bg-secondary hover:text-foreground",
            );
          }}
        >
          <item.icon className="size-4 shrink-0" aria-hidden="true" />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

function MobileNavLink({ item, onNavigate }: { item: (typeof workspaceNav)[number]; onNavigate?: () => void }) {
  const location = useLocation();
  const isHome = item.label === "Home";

  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) => {
        const active = isWorkspaceNavActive(location.pathname, item, isActive);
        return cn(
          "flex min-h-14 flex-col items-center justify-center gap-1 rounded-2xl px-1 text-[0.68rem] font-semibold transition focus:outline-none focus:ring-4 focus:ring-primary/10",
          active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-surface hover:text-foreground",
        );
      }}
    >
      {isHome ? (
        <span
          className="size-6 shrink-0 scale-125 bg-current"
          style={{
            mask: "url('/medopilot-icon.png') center / contain no-repeat",
            WebkitMask: "url('/medopilot-icon.png') center / contain no-repeat",
          }}
          aria-hidden="true"
        />
      ) : (
        <item.icon className="size-5 shrink-0" aria-hidden="true" />
      )}
      <span>{item.label}</span>
    </NavLink>
  );
}

function TopBarProfileMenu() {
  const auth = useAuth();
  const { profile } = useAccountProfile();
  const navigate = useNavigate();
  const account = useMemo(() => getAccountSummary(auth.user), [auth.user]);
  const displayName = getProfileDisplayName(profile) ?? account.displayName;
  const medopilotId = profile?.medopilot_user_id ?? null;
  const [open, setOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (menuRef.current?.contains(event.target as Node)) {
        return;
      }

      setOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const goToProfile = () => {
    setOpen(false);
    setPreviewOpen(false);
    void navigate("/app/profile");
  };

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onPointerEnter={(event) => {
          if (event.pointerType === "mouse") {
            setPreviewOpen(true);
          }
        }}
        onPointerLeave={() => setPreviewOpen(false)}
        onFocus={() => setPreviewOpen(true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setPreviewOpen(false);
          }
        }}
        onClick={() => {
          setPreviewOpen(false);
          setOpen((nextOpen) => !nextOpen);
        }}
        className="grid size-10 place-items-center rounded-full bg-white text-primary shadow-soft transition hover:shadow-premium focus:outline-none focus:ring-4 focus:ring-primary/10"
        aria-label="Profile menu"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <UserRound className="size-5" aria-hidden="true" />
      </button>
      {previewOpen && !open ? (
        <div
          className="pointer-events-none absolute right-0 top-[calc(100%+0.5rem)] z-40 hidden w-64 rounded-2xl border border-border bg-white p-3 text-foreground shadow-lift md:block"
          role="status"
        >
          <AccountIdentitySummary displayName={displayName} email={account.email} medopilotId={medopilotId} />
        </div>
      ) : null}
      {open ? (
        <div className="absolute right-0 top-[calc(100%+0.5rem)] z-50 w-72 rounded-2xl border border-border bg-white p-2 text-foreground shadow-lift" role="menu">
          <div className="border-b border-border px-3 py-3">
            <AccountIdentitySummary displayName={displayName} email={account.email} medopilotId={medopilotId} />
          </div>
          <button
            type="button"
            onClick={goToProfile}
            className="mt-2 flex min-h-10 w-full items-center gap-3 rounded-xl px-3 text-left text-sm font-semibold text-muted-foreground transition hover:bg-secondary hover:text-foreground focus:outline-none focus:ring-4 focus:ring-primary/10"
            role="menuitem"
          >
            <UserRound className="size-4 text-primary" aria-hidden="true" />
            My Profile
          </button>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setPreviewOpen(false);
              void navigate("/app/account-security");
            }}
            className="flex min-h-10 w-full items-center gap-3 rounded-xl px-3 text-left text-sm font-semibold text-muted-foreground transition hover:bg-secondary hover:text-foreground focus:outline-none focus:ring-4 focus:ring-primary/10"
            role="menuitem"
          >
            <Lock className="size-4 text-primary" aria-hidden="true" />
            Account &amp; Security
          </button>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setPreviewOpen(false);
              void auth.signOut();
            }}
            className="flex min-h-10 w-full items-center gap-3 rounded-xl px-3 text-left text-sm font-semibold text-muted-foreground transition hover:bg-secondary hover:text-foreground focus:outline-none focus:ring-4 focus:ring-primary/10"
            role="menuitem"
          >
            <LogOut className="size-4 text-primary" aria-hidden="true" />
            Sign Out
          </button>
        </div>
      ) : null}
    </div>
  );
}

function AccountIdentitySummary({ displayName, email, medopilotId }: { displayName: string; email: string | null; medopilotId: string | null }) {
  return (
    <div className="min-w-0">
      <p className="text-[0.65rem] font-medium uppercase tracking-[0.14em] text-primary">MedoPilot Account</p>
      <p className="mt-1 truncate text-sm font-semibold text-foreground">{displayName}</p>
      <p className="mt-0.5 truncate text-xs text-muted-foreground">{email ?? "Signed in"}</p>
      {medopilotId ? <p className="mt-1 truncate text-xs font-medium text-muted-foreground">MedoPilot ID {medopilotId}</p> : null}
    </div>
  );
}

function AccountMenu({ onNavigate, variant = "sidebar" }: { onNavigate?: () => void; variant?: "sidebar" | "hero" }) {
  const auth = useAuth();
  const { profile } = useAccountProfile();
  const account = useMemo(() => getAccountSummary(auth.user), [auth.user]);
  const displayName = getProfileDisplayName(profile) ?? account.displayName;
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "M";
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (menuRef.current?.contains(event.target as Node)) {
        return;
      }

      setOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handleNavigate = () => {
    setOpen(false);
    onNavigate?.();
  };

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((nextOpen) => !nextOpen)}
        className={cn(
          "w-full rounded-2xl p-3 text-left transition focus:outline-none focus:ring-4 focus:ring-primary/10",
          variant === "hero"
            ? "border border-white/20 bg-white/12 text-white shadow-[0_18px_40px_-30px_rgba(0,0,0,0.65)] backdrop-blur hover:bg-white/18"
            : "border border-border bg-white shadow-soft hover:border-primary/25",
        )}
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="flex min-w-0 items-center gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[linear-gradient(135deg,#075fc6,#4438d1)] text-sm font-semibold text-white">
            {initials}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold">{displayName}</span>
            {variant === "hero" ? <span className="block truncate text-xs font-normal text-blue-50/80">{account.email ?? "Signed in"}</span> : null}
          </span>
          <ChevronDown className={cn("ml-auto size-4 shrink-0 transition", variant === "hero" ? "text-blue-50/80" : "text-muted-foreground", open ? "rotate-180" : "")} aria-hidden="true" />
        </span>
      </button>
      {open ? (
        <div
          className={cn(
            "absolute left-0 right-0 z-40 rounded-2xl border border-border bg-white p-2 text-foreground shadow-lift",
            variant === "hero" ? "top-[calc(100%+0.5rem)]" : "bottom-[calc(100%+0.5rem)]",
          )}
          role="menu"
        >
          <div className="border-b border-border px-3 py-2">
            <p className="truncate text-sm font-semibold">{displayName}</p>
            <p className="mt-1 truncate text-xs font-normal text-muted-foreground">{account.email ?? "Signed in"}</p>
          </div>
          <Link
            to="/app/profile"
            onClick={handleNavigate}
            className="mt-2 flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold text-muted-foreground transition hover:bg-secondary hover:text-foreground"
            role="menuitem"
          >
            <UserRound className="size-4" aria-hidden="true" />
            View Profile
          </Link>
          <Link
            to="/app/account-security"
            onClick={handleNavigate}
            className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold text-muted-foreground transition hover:bg-secondary hover:text-foreground"
            role="menuitem"
          >
            <Lock className="size-4" aria-hidden="true" />
            Account &amp; Security
          </Link>
          <Link
            to="/app/profile/insurance"
            onClick={handleNavigate}
            className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold text-muted-foreground transition hover:bg-secondary hover:text-foreground"
            role="menuitem"
          >
            <ShieldCheck className="size-4" aria-hidden="true" />
            Health Insurance
          </Link>
          <Link
            to="/app/profile/export"
            onClick={handleNavigate}
            className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold text-muted-foreground transition hover:bg-secondary hover:text-foreground"
            role="menuitem"
          >
            <Download className="size-4" aria-hidden="true" />
            Download Health Profile
          </Link>
          <Link
            to="/app/settings"
            onClick={handleNavigate}
            className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold text-muted-foreground transition hover:bg-secondary hover:text-foreground"
            role="menuitem"
          >
            <Settings className="size-4" aria-hidden="true" />
            Settings
          </Link>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              void auth.signOut();
            }}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-semibold text-muted-foreground transition hover:bg-secondary hover:text-foreground"
            role="menuitem"
          >
            <LogOut className="size-4" aria-hidden="true" />
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}

function WorkspaceHeader() {
  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background/90 px-4 py-3 backdrop-blur md:px-6 lg:px-8">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 lg:hidden">
          <WorkspaceBrand />
        </div>
        <div className="hidden min-w-0 lg:block">
          <p className="text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-primary">MedoPilot Care Hub</p>
          <p className="mt-1 text-sm text-muted-foreground">Your health record, treatment context and care details in one calm place.</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <TopBarProfileMenu />
        </div>
      </div>
    </header>
  );
}

function WorkspaceSidebar() {
  return (
    <aside className="hidden h-screen w-[17.5rem] shrink-0 overflow-hidden border-r border-border bg-white/90 px-4 py-5 backdrop-blur lg:flex lg:flex-col">
      <div
        className="pointer-events-none absolute inset-0 opacity-80"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(circle at 20% 0%, rgba(10,111,216,0.12), transparent 12rem), radial-gradient(circle at 100% 78%, rgba(79,70,229,0.09), transparent 14rem)",
        }}
      />
      <div className="relative flex min-h-0 flex-1 flex-col">
        <WorkspaceBrand />
        <div className="mt-8">
          <WorkspaceNav />
        </div>
        <div className="mt-auto pt-6">
          <AccountMenu />
        </div>
      </div>
    </aside>
  );
}

export function WorkspaceShell() {
  // A normal profile read reports when the account is suspended pending deletion. In that case the
  // whole workspace is replaced by the dedicated suspended-account experience -- the only actions
  // there are restoring the account or signing out.
  const { suspended } = useAccountProfile();

  if (suspended) {
    return <SuspendedAccountExperience />;
  }

  return (
    <div className="min-h-dvh overflow-x-hidden bg-[linear-gradient(135deg,#f8fbff,#eef4fb_58%,#f9fbff)] text-foreground lg:h-dvh lg:overflow-hidden">
      <div className="flex min-w-0 items-start lg:h-full lg:items-stretch">
        <WorkspaceSidebar />
        <div className="min-w-0 max-w-full flex-1 lg:flex lg:h-full lg:flex-col lg:overflow-y-auto">
          <WorkspaceHeader />
          <main id="main" className="min-w-0 max-w-full px-4 py-6 pb-[calc(6.5rem+env(safe-area-inset-bottom))] md:px-6 lg:px-8 lg:py-8">
            <Outlet />
          </main>
        </div>
      </div>
      <WorkspaceNav variant="mobile" />
    </div>
  );
}

function WorkspacePageHeader({ eyebrow, title, body }: { eyebrow: string; title: string; body?: string }) {
  return (
    <div className="min-w-0 max-w-full overflow-hidden">
      <p className="text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-primary">{eyebrow}</p>
      <h1 className="mt-2 w-[22rem] max-w-full break-words text-2xl font-semibold leading-tight text-foreground sm:w-auto sm:text-3xl">
        {title}
      </h1>
      {body ? (
        <p className="mt-3 w-[22rem] max-w-full break-words text-sm leading-6 text-muted-foreground sm:w-auto sm:max-w-3xl">
          {body}
        </p>
      ) : null}
    </div>
  );
}

function formatDate(value: string | null) {
  if (!value) {
    return null;
  }

  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(date);
}

function formatDateTime(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function formatTime(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  const [hourText, minuteText] = value.split(":");
  const hour = Number(hourText);
  const minute = Number(minuteText);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return value;
  }

  const suffix = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${String(minute).padStart(2, "0")} ${suffix}`;
}

function formatFileSize(bytes: number) {
  if (bytes >= 1000 * 1000 * 1000) {
    const value = bytes / (1000 * 1000 * 1000);
    return `${value >= 10 ? value.toFixed(0) : value.toFixed(1)} GB`;
  }

  if (bytes >= 1000 * 1000) {
    const value = bytes / (1000 * 1000);
    return `${value >= 10 ? value.toFixed(0) : value.toFixed(1)} MB`;
  }

  if (bytes >= 1000) {
    const value = bytes / 1000;
    return `${value >= 10 ? value.toFixed(0) : value.toFixed(1)} KB`;
  }

  return `${bytes} ${bytes === 1 ? "Byte" : "Bytes"}`;
}

function documentContentTypeLabel(contentType: string) {
  if (contentType === "application/pdf") {
    return "PDF";
  }

  if (contentType === "image/jpeg") {
    return "JPEG";
  }

  if (contentType === "image/png") {
    return "PNG";
  }

  return "File";
}

function DocumentFileIcon({ contentType, muted = false }: { contentType: string; muted?: boolean }) {
  const Icon = contentType.startsWith("image/") ? ImageIcon : FileText;
  return (
    <span
      role="img"
      aria-label={`${documentContentTypeLabel(contentType)} file icon`}
      className={cn("grid size-10 shrink-0 place-items-center rounded-2xl", muted ? "bg-muted text-muted-foreground" : "bg-primary/10 text-primary")}
    >
      <Icon className="size-4" aria-hidden="true" />
    </span>
  );
}

function filenameExtensionForContentType(contentType: string) {
  if (contentType === "image/png") {
    return ".png";
  }

  if (contentType === "image/jpeg") {
    return ".jpg";
  }

  if (contentType === "application/pdf") {
    return ".pdf";
  }

  return "";
}

function filenameHasExtension(filename: string) {
  return /\.[^./\\]+$/.test(filename);
}

function uploadFilenameForFile(file: File, customFilename: string) {
  const trimmed = customFilename.trim();
  if (!trimmed) {
    return file.name;
  }

  if (filenameHasExtension(trimmed)) {
    return trimmed;
  }

  return `${trimmed}${filenameExtensionForContentType(file.type)}`;
}

function labelFromOptions(value: string | null | undefined, options: Array<{ value: string; label: string }>) {
  return options.find((option) => option.value === value)?.label ?? null;
}

function countryNameFromCode(countryCode: string | null | undefined) {
  if (!countryCode) {
    return null;
  }

  const country = findPhoneCountry(countryCode);
  return country ? `${country.flag} ${country.name}` : countryCode;
}

function getEditablePhoneNumber(phone: string | null, countryCode: string | null) {
  if (!phone) {
    return "";
  }

  const parsedPhone = parsePhoneNumberFromString(phone, (countryCode ?? undefined) as CountryCode | undefined);
  return parsedPhone?.nationalNumber ?? phone.replace(/^\+\d+\s*/, "");
}

function ProfileDetail({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="min-w-0 border-b border-border/70 py-3 last:border-b-0">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
      <p className="mt-1.5 min-h-6 whitespace-pre-line break-words text-sm font-semibold text-foreground">{value?.trim() || "Not added yet"}</p>
    </div>
  );
}

function formatAddress(address: ProfileAddress | null | undefined) {
  if (!address) {
    return null;
  }

  return [
    address.line1,
    address.line2,
    [address.city, address.region, address.postal_code].filter(Boolean).join(", "),
    countryNameFromCode(address.country_code) ?? address.country_code,
  ]
    .filter(Boolean)
    .join("\n");
}

function hasHealthInsurance(insurance: HealthInsurance | null | undefined) {
  return Boolean(
    insurance &&
      (
        insurance.insurance_company ||
        insurance.policy_name ||
        insurance.policy_number ||
        insurance.insurance_company_contact_email ||
        insurance.insurance_company_contact_phone
      ),
  );
}

function healthInsuranceDraftFromProfile(insurance: HealthInsurance | null | undefined): HealthInsuranceDraft {
  return {
    insurance_company: insurance?.insurance_company ?? "",
    policy_name: insurance?.policy_name ?? "",
    policy_number: insurance?.policy_number ?? "",
    insurance_company_contact_email: insurance?.insurance_company_contact_email ?? "",
    insurance_company_contact_phone: insurance?.insurance_company_contact_phone ?? "",
  };
}

function healthInsurancePayloadFromDraft(draft: HealthInsuranceDraft): HealthInsurance | null {
  const insurance = {
    insurance_company: draft.insurance_company.trim() || null,
    policy_name: draft.policy_name.trim() || null,
    policy_number: draft.policy_number.trim() || null,
    insurance_company_contact_email: draft.insurance_company_contact_email.trim() || null,
    insurance_company_contact_phone: draft.insurance_company_contact_phone.trim() || null,
  };

  return Object.values(insurance).some(Boolean) ? insurance : null;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function formatDateOfBirthWithAge(value: string | null | undefined) {
  const formattedDate = formatDate(value ?? null);
  if (!value) {
    return null;
  }

  const birthDate = new Date(`${value}T00:00:00`);
  if (Number.isNaN(birthDate.getTime())) {
    return formattedDate ?? value;
  }

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDelta = today.getMonth() - birthDate.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }

  return age >= 0 ? `${formattedDate ?? value} · ${age} years` : formattedDate ?? value;
}

function hasAddressDraft(address: AddressDraft) {
  return Object.values(address).some((value) => value.trim());
}

function addressDraftFromProfile(address: ProfileAddress | null | undefined): AddressDraft {
  return {
    line1: address?.line1 ?? "",
    line2: address?.line2 ?? "",
    city: address?.city ?? "",
    region: address?.region ?? "",
    postal_code: address?.postal_code ?? "",
    country_code: address?.country_code ?? "",
  };
}

function addressPayloadFromDraft(address: AddressDraft): ProfileAddress | null {
  if (!hasAddressDraft(address)) {
    return null;
  }

  return {
    line1: address.line1.trim() || null,
    line2: address.line2.trim() || null,
    city: address.city.trim() || null,
    region: address.region.trim() || null,
    postal_code: address.postal_code.trim() || null,
    country_code: address.country_code || null,
  };
}

function emergencyContactDraftsFromProfile(contacts: EmergencyContact[] | null | undefined): EmergencyContactDraft[] {
  const drafts = [...(contacts ?? [])]
    .sort((left, right) => left.priority - right.priority)
    .slice(0, 3)
    .map((contact, index) => ({
      name: contact.name,
      relationship: contact.relationship,
      primary_mobile_number: contact.primary_mobile_number,
      alternate_mobile_number: contact.alternate_mobile_number ?? "",
      whatsapp_number: contact.whatsapp_number ?? "",
      address: formatAddress(contact.address) ?? "",
      priority: String(contact.priority || index + 1),
    }));

  while (drafts.length < 3) {
    drafts.push({
      name: "",
      relationship: "",
      primary_mobile_number: "",
      alternate_mobile_number: "",
      whatsapp_number: "",
      address: "",
      priority: String(drafts.length + 1),
    });
  }

  return drafts;
}

function doctorDraftFromProfile(doctor: PrimaryDoctor | null | undefined): PrimaryDoctorDraft {
  return {
    name: doctor?.name ?? "",
    speciality: doctor?.speciality ?? "",
    hospital_clinic_name: doctor?.hospital_clinic_name ?? "",
    mobile_number: doctor?.mobile_number ?? "",
    emergency_contact_number: doctor?.emergency_contact_number ?? "",
    address: formatAddress(doctor?.address) ?? "",
    email: doctor?.email ?? "",
    preferred_for_emergency: doctor?.preferred_for_emergency ?? false,
  };
}

function doctorPayloadFromDraft(doctor: PrimaryDoctorDraft): PrimaryDoctor | null {
  const hasDoctor = Object.entries(doctor).some(([key, value]) => key !== "preferred_for_emergency" && typeof value === "string" && value.trim()) || doctor.preferred_for_emergency;
  if (!hasDoctor) {
    return null;
  }

  return {
    name: doctor.name.trim() || null,
    speciality: doctor.speciality.trim() || null,
    hospital_clinic_name: doctor.hospital_clinic_name.trim() || null,
    mobile_number: doctor.mobile_number.trim() || null,
    emergency_contact_number: doctor.emergency_contact_number.trim() || null,
    address: doctor.address.trim() ? { line1: doctor.address.trim(), line2: null, city: null, region: null, postal_code: null, country_code: null } : null,
    email: doctor.email.trim() || null,
    preferred_for_emergency: doctor.preferred_for_emergency,
  };
}

function hospitalDraftFromProfile(hospital: PreferredHospital | null | undefined): HospitalDraft {
  return {
    name: hospital?.name ?? "",
    emergency_number: hospital?.emergency_number ?? "",
    address: formatAddress(hospital?.address) ?? "",
    ambulance_contact_number: hospital?.ambulance_contact_number ?? "",
  };
}

function hospitalPayloadFromDraft(hospital: HospitalDraft): PreferredHospital | null {
  const hasHospital = Object.values(hospital).some((value) => value.trim());
  if (!hasHospital) {
    return null;
  }

  if (!hospital.name.trim() || !hospital.emergency_number.trim() || !hospital.address.trim()) {
    return null;
  }

  return {
    name: hospital.name.trim(),
    emergency_number: hospital.emergency_number.trim(),
    address: { line1: hospital.address.trim(), line2: null, city: null, region: null, postal_code: null, country_code: null },
    ambulance_contact_number: hospital.ambulance_contact_number.trim() || null,
  };
}

function hospitalsDraftFromProfile(hospitals: PreferredHospitals | null | undefined): PreferredHospitalsDraft {
  return {
    primary: hospitalDraftFromProfile(hospitals?.primary),
    alternate: hospitalDraftFromProfile(hospitals?.alternate),
  };
}

function hospitalsPayloadFromDraft(hospitals: PreferredHospitalsDraft): PreferredHospitals | null {
  const primary = hospitalPayloadFromDraft(hospitals.primary);
  const alternate = hospitalPayloadFromDraft(hospitals.alternate);
  return primary || alternate ? { primary, alternate } : null;
}

function emergencyContactsPayloadFromDrafts(contacts: EmergencyContactDraft[]): { contacts: EmergencyContact[]; error: string | null } {
  const normalizedEmergencyContacts: EmergencyContact[] = [];
  const usedPriorities = new Set<number>();
  for (const contact of contacts) {
    const hasContact = [
      contact.name,
      contact.relationship,
      contact.primary_mobile_number,
      contact.alternate_mobile_number,
      contact.whatsapp_number,
      contact.address,
    ].some((value) => value.trim());
    if (!hasContact) {
      continue;
    }

    const priority = Number(contact.priority);
    if (!contact.name.trim() || !contact.relationship.trim() || !contact.primary_mobile_number.trim()) {
      return { contacts: [], error: "Each emergency contact needs a name, relationship and primary mobile number." };
    }

    if (!Number.isInteger(priority) || priority < 1 || priority > 3 || usedPriorities.has(priority)) {
      return { contacts: [], error: "Emergency contact priorities must be unique values from 1 to 3." };
    }

    usedPriorities.add(priority);
    normalizedEmergencyContacts.push({
      name: contact.name.trim(),
      relationship: contact.relationship.trim(),
      primary_mobile_number: contact.primary_mobile_number.trim(),
      alternate_mobile_number: profileOptionalText(contact.alternate_mobile_number),
      whatsapp_number: profileOptionalText(contact.whatsapp_number),
      address: contact.address.trim() ? { line1: contact.address.trim(), line2: null, city: null, region: null, postal_code: null, country_code: null } : null,
      priority,
    });
  }

  return { contacts: normalizedEmergencyContacts, error: null };
}

function preferredHospitalsPayloadFromDraft(hospitals: PreferredHospitalsDraft): { hospitals: PreferredHospitals | null; error: string | null } {
  const nextPrimaryHospital = hospitalPayloadFromDraft(hospitals.primary);
  const hasAlternateHospitalDraft = Object.values(hospitals.alternate).some((value) => value.trim());
  if (hasAlternateHospitalDraft && !nextPrimaryHospital) {
    return { hospitals: null, error: "Add a primary preferred hospital before adding an alternate hospital." };
  }

  return { hospitals: hospitalsPayloadFromDraft(hospitals), error: null };
}

function profileOptionalText(value: string) {
  return value.trim() || null;
}

function CountryCombobox({
  buttonClassName,
  disabled,
  label,
  onChange,
  placeholder = "Search countries",
  showDialCode = true,
  value,
}: {
  buttonClassName?: string;
  disabled?: boolean;
  label: string;
  onChange: (country: PhoneCountry) => void;
  placeholder?: string;
  showDialCode?: boolean;
  value: PhoneCountry;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const comboRef = useRef<HTMLDivElement | null>(null);

  const filteredCountries = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return phoneCountries.slice(0, 12);
    }

    return phoneCountries.filter((country) => country.searchText.includes(normalizedQuery)).slice(0, 24);
  }, [query]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (comboRef.current?.contains(event.target as Node)) {
        return;
      }

      setOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={comboRef} className="relative">
      <p className="block text-sm font-semibold text-foreground">{label}</p>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((nextOpen) => !nextOpen)}
        className={cn(
          "mt-2 flex min-h-12 w-full items-center gap-3 rounded-2xl border border-border bg-white px-4 py-3 text-left text-sm font-semibold transition hover:border-primary/30 focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:cursor-not-allowed disabled:opacity-60",
          buttonClassName,
        )}
        aria-label={`${label}, ${value.name}${showDialCode ? ` ${value.dialCode}` : ""}`}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="text-lg" aria-hidden="true">
          {value.flag}
        </span>
        <span className="min-w-0 flex-1 truncate">
          {value.name} {showDialCode ? <span className="text-muted-foreground">{value.dialCode}</span> : null}
        </span>
        <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      </button>
      {open ? (
        <div className="absolute left-0 right-0 top-[calc(100%+0.5rem)] z-20 rounded-2xl border border-border bg-white p-2 shadow-lift">
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="h-11 w-full rounded-xl border border-border bg-surface px-3 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
            placeholder={placeholder}
            autoComplete="off"
            aria-label={`${label} search`}
          />
          <div className="mt-2 max-h-64 overflow-y-auto" role="listbox" aria-label="Country options">
            {filteredCountries.length ? (
              filteredCountries.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => {
                    onChange(country);
                    setQuery("");
                    setOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm transition hover:bg-secondary",
                    country.code === value.code ? "bg-secondary font-semibold text-foreground" : "text-muted-foreground",
                  )}
                  role="option"
                  aria-selected={country.code === value.code}
                >
                  <span className="text-lg" aria-hidden="true">
                    {country.flag}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{country.name}</span>
                  {showDialCode ? <span className="font-semibold text-foreground">{country.dialCode}</span> : null}
                </button>
              ))
            ) : (
              <p className="px-3 py-4 text-sm text-muted-foreground">No countries found.</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function getLatestVaccinations(vaccinations: Vaccination[]) {
  return [...vaccinations].sort((left, right) => right.administered_date.localeCompare(left.administered_date));
}

function getUpcomingVaccinations(vaccinations: Vaccination[]) {
  const today = new Date().toISOString().slice(0, 10);
  return [...vaccinations]
    .filter((record) => record.next_dose_due_date && record.next_dose_due_date >= today)
    .sort((left, right) => (left.next_dose_due_date ?? "").localeCompare(right.next_dose_due_date ?? ""));
}

function hasLaterVaccinationForNextDose(record: Vaccination, vaccinations: Vaccination[]) {
  if (!record.next_dose_due_date) {
    return false;
  }

  const normalizedName = record.vaccine_name.trim().toLowerCase();
  return vaccinations.some(
    (candidate) =>
      candidate.vaccination_id !== record.vaccination_id &&
      candidate.vaccine_name.trim().toLowerCase() === normalizedName &&
      candidate.administered_date >= record.next_dose_due_date!,
  );
}

function getOverdueVaccinations(vaccinations: Vaccination[]) {
  const today = new Date().toISOString().slice(0, 10);
  return [...vaccinations]
    .filter(
      (record) =>
        record.next_dose_due_date &&
        record.next_dose_due_date < today &&
        !hasLaterVaccinationForNextDose(record, vaccinations),
    )
    .sort((left, right) => (left.next_dose_due_date ?? "").localeCompare(right.next_dose_due_date ?? ""));
}

function appointmentScheduledAtKey(record: Appointment) {
  return [record.scheduled_date, record.scheduled_time].filter(Boolean).join("T");
}

// Compared against the device's own local wall-clock "now" (not UTC) -- the same simplification
// vaccination's own due-date bucketing already makes above (calendar-only, no cross-timezone
// instant math). Correct for the overwhelmingly common case of an appointment booked in the
// viewer's own timezone; resolving each appointment's own IANA timezone against the current
// instant precisely would be unnecessary complexity for a simple Upcoming/Past split.
function localNowKey() {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

function getUpcomingAppointments(appointments: Appointment[]) {
  const nowKey = localNowKey();
  return [...appointments]
    .filter((record) => record.status === "scheduled" && appointmentScheduledAtKey(record) >= nowKey)
    .sort((left, right) => appointmentScheduledAtKey(left).localeCompare(appointmentScheduledAtKey(right)));
}

function getPastAppointments(appointments: Appointment[]) {
  const nowKey = localNowKey();
  return [...appointments]
    .filter((record) => record.status !== "archived" && (record.status === "cancelled" || appointmentScheduledAtKey(record) < nowKey))
    .sort((left, right) => appointmentScheduledAtKey(right).localeCompare(appointmentScheduledAtKey(left)));
}

function getArchivedAppointments(appointments: Appointment[]) {
  return [...appointments]
    .filter((record) => record.status === "archived")
    .sort((left, right) => appointmentScheduledAtKey(right).localeCompare(appointmentScheduledAtKey(left)));
}

function BackAction({ compact = false, label, to }: { compact?: boolean; label: string; to: string }) {
  return (
    <Link
      to={to}
      className={cn(
        "inline-flex items-center gap-2 rounded-xl border border-border bg-white text-sm font-medium text-muted-foreground shadow-soft transition hover:border-primary/30 hover:text-foreground focus:outline-none focus:ring-4 focus:ring-primary/10",
        compact ? "min-h-8 px-2.5 py-1.5 text-xs" : "min-h-9 px-3 py-2",
      )}
    >
      <ArrowLeft className={compact ? "size-3.5" : "size-4"} aria-hidden="true" />
      {label}
    </Link>
  );
}

function DashboardQuickAction({
  displayLabel,
  icon: Icon,
  label,
  tone = "blue",
  to,
}: {
  displayLabel?: string;
  icon: LucideIcon;
  label: string;
  tone?: "blue" | "emerald" | "amber" | "rose" | "lavender";
  to: string;
}) {
  const toneClass = {
    amber: "bg-amber-50 text-amber-700",
    blue: "bg-blue-50 text-primary",
    emerald: "bg-emerald-50 text-emerald-700",
    lavender: "bg-indigo-50 text-indigo-700",
    rose: "bg-rose-50 text-rose-700",
  }[tone];

  return (
    <Link
      to={to}
      aria-label={label}
      className="group flex min-h-[4.5rem] min-w-0 flex-col items-center justify-center gap-1.5 px-1.5 py-2 text-center text-[0.72rem] font-medium leading-[1.15] text-foreground transition hover:bg-surface/70 focus:outline-none focus:ring-4 focus:ring-primary/10 motion-reduce:transition-none sm:text-xs"
    >
      <span className={cn("grid size-8 shrink-0 place-items-center rounded-full transition group-hover:scale-[1.04] motion-reduce:transition-none", toneClass)}>
        <Icon className="size-3.5" aria-hidden="true" />
      </span>
      <span className="w-full text-balance break-words">{displayLabel ?? label}</span>
    </Link>
  );
}

type UpcomingCareItem = {
  category: string;
  dueSortKey: string;
  detail: string;
  icon: LucideIcon;
  status: "due" | "upcoming" | "overdue";
  title: string;
  to: string;
};

type ClinicalTone = "blue" | "emerald" | "amber" | "rose" | "lavender";

function vaccinationDoseLabel(record: Vaccination) {
  if (record.dose_label) {
    return record.dose_label;
  }

  if (record.dose_number) {
    return record.series_total ? `Dose ${record.dose_number} of ${record.series_total}` : `Dose ${record.dose_number}`;
  }

  return null;
}

function upcomingVaccinationCareItem(record: Vaccination): UpcomingCareItem {
  const detail = [
    "Next dose",
    vaccinationDoseLabel(record),
    record.next_dose_due_date ? `Due ${formatDate(record.next_dose_due_date) ?? record.next_dose_due_date}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return {
    category: "Vaccination",
    dueSortKey: record.next_dose_due_date ?? "",
    detail,
    icon: Syringe,
    status: "upcoming",
    title: record.vaccine_name,
    to: `/app/vaccinations/${encodeURIComponent(record.vaccination_id)}`,
  };
}

function upcomingAppointmentCareItem(record: Appointment): UpcomingCareItem {
  return {
    category: "Appointment",
    dueSortKey: appointmentScheduledAtKey(record),
    detail: [appointmentTypeLabel(record.appointment_type), formatDate(record.scheduled_date), formatTime(record.scheduled_time)].filter(Boolean).join(" · "),
    icon: Stethoscope,
    status: "upcoming",
    title: record.title,
    to: `/app/appointments/${encodeURIComponent(record.appointment_id)}`,
  };
}

function overdueVaccinationCareItem(record: Vaccination): UpcomingCareItem {
  const detail = [
    "Next dose",
    vaccinationDoseLabel(record),
    record.next_dose_due_date ? `Due ${formatDate(record.next_dose_due_date) ?? record.next_dose_due_date}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return {
    category: "Vaccination",
    dueSortKey: record.next_dose_due_date ?? "",
    detail,
    icon: AlertTriangle,
    status: "overdue",
    title: record.vaccine_name,
    to: `/app/vaccinations/${encodeURIComponent(record.vaccination_id)}`,
  };
}

function daypartLabel(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  return value.charAt(0).toUpperCase() + value.slice(1);
}

function medicationStatusLabel(status: MedicationStatus | null | undefined) {
  return {
    active: "Active",
    completed: "Completed",
    stopped: "Stopped",
  }[String(status)] ?? "Active";
}

function allergyCategoryLabel(category: AllergyCategory | null | undefined) {
  return {
    medication: "Medication / Drug",
    food: "Food",
    environmental: "Environmental",
    other: "Other",
  }[String(category)] ?? "Other";
}

function allergySeverityLabel(severity: AllergySeverity | null | undefined) {
  return {
    mild: "Mild",
    moderate: "Moderate",
    severe: "Severe",
    life_threatening: "Life-threatening",
    unknown: "Unknown",
  }[String(severity)] ?? "Unknown";
}

function clinicalStatusLabel(status: AllergyStatus | HealthConditionStatus | null | undefined) {
  return {
    active: "Active",
    inactive: "Inactive",
    resolved: "Resolved",
    entered_in_error: "Entered in error",
  }[String(status)] ?? "Active";
}

function getImportantAllergies(allergies: Allergy[]) {
  const severityRank: Record<AllergySeverity, number> = {
    life_threatening: 4,
    severe: 3,
    moderate: 2,
    mild: 1,
    unknown: 0,
  };
  return [...allergies]
    .filter((allergy) => allergy.status === "active")
    .sort((left, right) => severityRank[right.severity] - severityRank[left.severity] || left.substance_name.localeCompare(right.substance_name));
}

function getActiveConditions(conditions: HealthCondition[]) {
  return [...conditions]
    .filter((condition) => condition.status === "active")
    .sort((left, right) => left.condition_name.localeCompare(right.condition_name));
}

function medicationTypeLabel(type: MedicationType | null | undefined) {
  return {
    prescribed_course: "Prescribed",
    long_term_prescribed: "Long Term",
    self_managed: "Self Managed",
    supplement: "Supplement",
  }[String(type)] ?? "Medication";
}

function dueLabel(dose: UpcomingDose) {
  if (dose.due_time) {
    return `Due ${formatTime(dose.due_time) ?? dose.due_time}`;
  }

  if (dose.daypart) {
    return `Due ${daypartLabel(dose.daypart)}`;
  }

  return dose.due_date ? `Due ${formatDate(dose.due_date) ?? dose.due_date}` : "Due soon";
}

function upcomingMedicationCareItem(dose: UpcomingDose): UpcomingCareItem {
  return {
    category: dose.category,
    dueSortKey: dose.due_at ?? [dose.due_date, dose.due_time ?? dose.daypart ?? ""].filter(Boolean).join("T"),
    detail: [dose.dose_text, dueLabel(dose)].filter(Boolean).join(" · "),
    icon: Pill,
    status: "due",
    title: dose.medication_name,
    to: `/app/medications/${encodeURIComponent(dose.medication_id)}`,
  };
}

function getLatestMedications(medications: Medication[]) {
  return [...medications].sort((left, right) => (right.updated_at ?? "").localeCompare(left.updated_at ?? ""));
}

function getNearestUpcomingMedicationDoses(doses: UpcomingDose[]) {
  const nearestByMedication = new Map<string, UpcomingDose>();

  for (const dose of doses) {
    const current = nearestByMedication.get(dose.medication_id);
    const currentKey = current?.due_at ?? [current?.due_date, current?.due_time ?? current?.daypart ?? ""].filter(Boolean).join("T");
    const nextKey = dose.due_at ?? [dose.due_date, dose.due_time ?? dose.daypart ?? ""].filter(Boolean).join("T");
    if (!current || nextKey.localeCompare(currentKey) < 0) {
      nearestByMedication.set(dose.medication_id, dose);
    }
  }

  return [...nearestByMedication.values()].sort((left, right) => {
    const leftKey = left.due_at ?? [left.due_date, left.due_time ?? left.daypart ?? ""].filter(Boolean).join("T");
    const rightKey = right.due_at ?? [right.due_date, right.due_time ?? right.daypart ?? ""].filter(Boolean).join("T");
    return leftKey.localeCompare(rightKey);
  });
}

function useUpcomingCareItems() {
  // Health-record-owned data (vaccinations + appointments) comes from one GET /v1/overview load;
  // upcoming medication doses come from one GET /v1/doses/upcoming (these surfaces never need the
  // medication list). Dedicated Vaccinations/Appointments pages keep their own hooks.
  const { appointments, error: overviewError, vaccinations } = useHealthOverview();
  const { error: medicationsError, upcomingDoses } = useUpcomingDoses();
  const upcomingVaccinations = useMemo(() => getUpcomingVaccinations(vaccinations), [vaccinations]);
  const overdueVaccinations = useMemo(() => getOverdueVaccinations(vaccinations), [vaccinations]);
  const upcomingAppointments = useMemo(() => getUpcomingAppointments(appointments), [appointments]);
  const upcomingCareItems = useMemo(() => {
    const medicationItems = getNearestUpcomingMedicationDoses(upcomingDoses).map(upcomingMedicationCareItem);
    const overdueVaccinationItems = overdueVaccinations.map(overdueVaccinationCareItem);
    const vaccinationItems = upcomingVaccinations.map(upcomingVaccinationCareItem);
    const appointmentItems = upcomingAppointments.map(upcomingAppointmentCareItem);
    return [...overdueVaccinationItems, ...medicationItems, ...vaccinationItems, ...appointmentItems].sort((left, right) => {
      if (left.status === "overdue" && right.status !== "overdue") {
        return -1;
      }
      if (right.status === "overdue" && left.status !== "overdue") {
        return 1;
      }
      return left.dueSortKey.localeCompare(right.dueSortKey);
    });
  }, [overdueVaccinations, upcomingDoses, upcomingVaccinations, upcomingAppointments]);

  return { error: overviewError ?? medicationsError, upcomingCareItems };
}

function UpcomingCareCard({ item }: { item: UpcomingCareItem }) {
  const Icon = item.icon;
  const status = {
    due: { label: "Due", className: "bg-amber-50 text-amber-700" },
    overdue: { label: "Overdue", className: "bg-rose-50 text-rose-700" },
    upcoming: { label: "Upcoming", className: "bg-blue-50 text-primary" },
  }[item.status];

  return (
    <Link
      to={item.to}
      className="group block min-w-0 border-b border-border/70 py-3 transition last:border-b-0 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10 motion-reduce:transition-none"
      aria-label={`Open ${item.category.toLowerCase()} upcoming care for ${item.title}`}
    >
      <span className="flex min-w-0 items-center gap-2.5">
        <span className={cn("grid size-9 shrink-0 place-items-center rounded-xl", status.className)}>
          <Icon className="size-3.5" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[0.95rem] font-semibold leading-5 text-foreground">
            {item.category}: {item.title}
          </span>
          <span className="mt-0.5 block text-[0.86rem] leading-5 text-muted-foreground">{item.detail}</span>
        </span>
        <span className={cn("hidden shrink-0 rounded-full px-2 py-0.5 text-[0.68rem] font-semibold sm:inline-flex", status.className)}>
          {status.label}
        </span>
        <ArrowRight className="size-3.5 shrink-0 text-primary transition group-hover:translate-x-0.5" aria-hidden="true" />
      </span>
    </Link>
  );
}

const homeSectionHeadingClass = "text-[0.68rem] font-medium uppercase tracking-[0.16em] text-primary";

// Home-only notice for the ACCOUNT-LEVEL Care Reminders preference being off (profile.
// care_reminders_enabled === false) -- never a push/device/PWA-install state. Compact and
// collapsed by default via native <details>, so it stays out of the way of Upcoming Care and
// Quick Links. The action reuses the existing Settings route; it never toggles the preference
// from Home.
function CareRemindersOffNotice({ careRemindersEnabled }: { careRemindersEnabled: boolean | undefined }) {
  if (careRemindersEnabled !== false) {
    return null;
  }

  return (
    <details className="group min-w-0 rounded-[1.25rem] border border-amber-200 bg-amber-50 px-3.5 py-3">
      <summary className="flex min-w-0 cursor-pointer list-none items-center gap-3 rounded-xl outline-none marker:hidden focus-visible:ring-4 focus-visible:ring-amber-500/20">
        <span className="grid size-9 shrink-0 place-items-center rounded-2xl bg-amber-100 text-amber-700">
          <BellOff className="size-4" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1 text-[0.95rem] font-semibold leading-5 text-amber-900">Care Reminders are Off</span>
        <ChevronDown
          className="size-4 shrink-0 text-amber-700 transition group-open:rotate-180 motion-reduce:transition-none"
          aria-hidden="true"
        />
      </summary>
      <div className="mt-3 border-t border-amber-200 pt-3">
        <p className="text-sm leading-6 text-amber-800">
          You won't receive notifications until you turn Care Reminders back on.
        </p>
        <button
          type="button"
          onClick={() => openCareRemindersDialog()}
          className="mt-3 inline-flex min-h-10 items-center gap-2 rounded-full border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-800 transition hover:bg-amber-100 focus:outline-none focus:ring-4 focus:ring-amber-500/20"
        >
          Manage Care Reminders
          <ArrowRight className="size-4" aria-hidden="true" />
        </button>
      </div>
    </details>
  );
}

function clinicalToneClass(tone: ClinicalTone) {
  return {
    amber: "bg-amber-50 text-amber-700",
    blue: "bg-blue-50 text-primary",
    emerald: "bg-emerald-50 text-emerald-700",
    lavender: "bg-indigo-50 text-indigo-700",
    rose: "bg-rose-50 text-rose-700",
  }[tone];
}

// The premium MedoPilot navy/indigo page Hero, matching the treatment established on Profile,
// Emergency Health Info and Care Hub Settings. Used for top-level destination pages that
// previously had a plain text header -- not for create/edit/detail screens, focused workflows,
// or the Home dashboard.
const heroActionClassName =
  "inline-flex min-h-10 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-white px-4 py-2 text-sm font-semibold text-primary shadow-soft transition hover:bg-blue-50 focus:outline-none focus:ring-4 focus:ring-white/20";

// Secondary hero action -- an outline-on-navy pill for lower-emphasis actions that sit next to a
// primary heroActionClassName button (e.g. "New Folder" alongside "Upload File").
const heroSecondaryActionClassName =
  "inline-flex min-h-10 items-center justify-center gap-2 whitespace-nowrap rounded-full border border-white/30 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/15 focus:outline-none focus:ring-4 focus:ring-white/20";

function PageHero({
  action,
  avatar,
  description,
  icon: Icon,
  title,
}: {
  action?: ReactNode;
  // An identity element (e.g. a user's initials avatar) rendered in place of the icon chip.
  avatar?: ReactNode;
  description?: string;
  icon?: LucideIcon;
  title: string;
}) {
  return (
    <section className="relative overflow-hidden rounded-[1.5rem] bg-[linear-gradient(145deg,#061b43,#0b3474_55%,#3f35d2)] px-4 py-5 text-white shadow-lift sm:px-6 sm:py-6">
      <div
        className="pointer-events-none absolute inset-0 opacity-70"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(circle at 90% 6%, rgba(134,211,255,0.22), transparent 12rem), radial-gradient(circle at 4% 100%, rgba(99,102,241,0.24), transparent 18rem)",
        }}
      />
      <div className="relative flex min-w-0 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3.5">
          {avatar ? (
            avatar
          ) : Icon ? (
            <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-white/12 text-blue-50">
              <Icon className="size-5" aria-hidden="true" />
            </span>
          ) : null}
          <div className="min-w-0">
            <h1 className="text-[clamp(1.5rem,6vw,2rem)] font-semibold leading-tight">{title}</h1>
            {description ? <p className="mt-1.5 text-sm leading-6 text-blue-50/80">{description}</p> : null}
          </div>
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
    </section>
  );
}

function pluralize(count: number, singular: string, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function HealthSummaryStrip({ items }: { items: Array<{ label: string; tone?: ClinicalTone }> }) {
  return (
    <section aria-label="Health summary" className="flex min-w-0 gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {items.map((item) => (
        <span key={item.label} className={cn("inline-flex min-h-9 shrink-0 items-center rounded-full px-3 py-1.5 text-xs font-semibold", clinicalToneClass(item.tone ?? "blue"))}>
          {item.label}
        </span>
      ))}
    </section>
  );
}

function HealthNavRow({
  icon: Icon,
  label,
  summary,
  tone = "blue",
  to,
}: {
  icon: LucideIcon;
  label: string;
  summary: string;
  tone?: ClinicalTone;
  to: string;
}) {
  return (
    <Link
      to={to}
      className="group flex min-h-16 min-w-0 items-center gap-3 border-b border-border/70 py-3.5 transition last:border-b-0 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10 motion-reduce:transition-none"
    >
      <span className={cn("grid size-10 shrink-0 place-items-center rounded-2xl", clinicalToneClass(tone))}>
        <Icon className="size-4" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[0.95rem] font-semibold leading-5 text-foreground">{label}</span>
        <span className="mt-1 block text-sm text-muted-foreground">{summary}</span>
      </span>
      <ArrowRight className="size-4 shrink-0 text-primary transition group-hover:translate-x-0.5 motion-reduce:transition-none" aria-hidden="true" />
    </Link>
  );
}

export function DashboardPage() {
  const auth = useAuth();
  const { profile } = useAccountProfile();
  const { error: upcomingCareError, upcomingCareItems } = useUpcomingCareItems();
  const firstName = profile?.first_name?.trim() || getFirstNameFromSession(auth.user);
  const upcomingCareSummary =
    upcomingCareItems.length > 0
      ? pluralize(upcomingCareItems.length, "upcoming item")
      : "No upcoming care recorded";

  return (
    <div className="space-y-3.5">
      <section className="min-w-0">
        <h1 className="min-w-0 truncate text-xl font-semibold leading-tight text-foreground sm:text-[1.35rem]">
          {firstName ? `Hello, ${firstName}!` : "Hello!"} <span aria-hidden="true">👋</span>
        </h1>
      </section>

      <CareRemindersOffNotice careRemindersEnabled={profile?.care_reminders_enabled} />

      <section className="min-w-0">
        <h2 className={homeSectionHeadingClass}>UPCOMING CARE</h2>
        <Link
          to="/app/upcoming-care"
          className="group mt-2.5 flex min-h-16 min-w-0 items-center gap-3 rounded-[1.25rem] bg-white px-3.5 py-3 shadow-soft transition hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10 motion-reduce:transition-none"
          aria-label={`Open Upcoming Care, ${upcomingCareSummary}`}
        >
          <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-amber-50 text-amber-700">
            <CalendarClock className="size-4" aria-hidden="true" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[0.95rem] font-semibold leading-5 text-foreground">{upcomingCareSummary}</span>
          </span>
          <ArrowRight className="size-4 shrink-0 text-primary transition group-hover:translate-x-0.5 motion-reduce:transition-none" aria-hidden="true" />
        </Link>
        {upcomingCareError ? (
          <p className="mt-5 rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">
            {upcomingCareError}
          </p>
        ) : null}
      </section>

      <section>
        <h2 className={homeSectionHeadingClass}>QUICK LINKS</h2>
        <div className="mt-2.5 grid grid-cols-3 divide-x divide-y divide-border/80 overflow-hidden rounded-[1.15rem] bg-white shadow-soft sm:max-w-3xl sm:grid-cols-6 sm:divide-y-0">
          <DashboardQuickAction to="/app/documents?upload=file" icon={Upload} label="Upload Document" displayLabel="Upload Document" tone="blue" />
          <DashboardQuickAction to="/app/documents" icon={Folder} label="My Files" displayLabel="My Files" tone="blue" />
          <DashboardQuickAction to="/app/orders" icon={ShoppingCart} label="Order Medicines" displayLabel="Order Medicines" tone="amber" />
          <DashboardQuickAction to="/app/medications/new" icon={Pill} label="Add Medication" displayLabel="Medication" tone="blue" />
          <DashboardQuickAction to="/app/appointments" icon={Stethoscope} label="Appointments" displayLabel="Appointments" tone="blue" />
          <DashboardQuickAction to="/app/health-records" icon={HeartPulse} label="Add Health Record" displayLabel="Health Record" tone="lavender" />
          <DashboardQuickAction to="/app/emergency" icon={Cross} label="Update Emergency Info" displayLabel="Emergency Info" tone="rose" />
          <DashboardQuickAction to="/app/profile/export" icon={Download} label="Download Health Profile" displayLabel="Health Profile" tone="emerald" />
        </div>
      </section>
    </div>
  );
}

export function UpcomingCarePage() {
  const { error, upcomingCareItems } = useUpcomingCareItems();

  return (
    <div className="space-y-4">
      <section className="min-w-0">
        <BackAction to="/app" label="Home" compact />
        <h1 className="mt-3 text-[1.35rem] font-semibold leading-tight text-foreground sm:text-[1.5rem]">Upcoming Care</h1>
        <p className="mt-1 text-sm leading-6 text-muted-foreground">Medication doses, vaccine follow-ups, and upcoming appointments that may need attention.</p>
      </section>

      {error ? (
        <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">
          {error}
        </p>
      ) : null}

      {upcomingCareItems.length ? (
        <section aria-label="Upcoming care items" className="rounded-[1.25rem] bg-white px-3.5 shadow-soft">
          {upcomingCareItems.map((item) => (
            <UpcomingCareCard key={`${item.category}-${item.to}-${item.dueSortKey}`} item={item} />
          ))}
        </section>
      ) : (
        <section className="rounded-[1.25rem] bg-white px-3.5 py-3 shadow-soft">
          <div className="flex items-center gap-2.5">
            <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-emerald-50 text-emerald-700">
              <ShieldCheck className="size-4" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <h2 className="text-[0.95rem] font-semibold leading-5">No upcoming care recorded</h2>
              <p className="mt-0.5 text-[0.86rem] leading-5 text-muted-foreground">Future doses and schedules will appear here.</p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export function HealthRecordsPage() {
  // One aggregate load for the four health-record domains (see useHealthOverview); the dedicated
  // Vaccinations/Allergies/Known Health Conditions/Appointments pages still use their own hooks.
  const {
    allergies,
    appointments,
    conditions,
    error: overviewError,
    status: overviewStatus,
    vaccinations,
  } = useHealthOverview();
  const { error: medicationsError, medications, status: medicationsStatus, upcomingDoses } = useMedicationOverview();
  const activeAllergies = useMemo(() => getImportantAllergies(allergies), [allergies]);
  const activeConditions = useMemo(() => getActiveConditions(conditions), [conditions]);
  const activeMedications = useMemo(() => medications.filter((record) => record.status === "active"), [medications]);
  const upcomingVaccinations = useMemo(() => getUpcomingVaccinations(vaccinations), [vaccinations]);
  const overdueVaccinations = useMemo(() => getOverdueVaccinations(vaccinations), [vaccinations]);
  const upcomingAppointments = useMemo(() => getUpcomingAppointments(appointments), [appointments]);
  const isLoading = overviewStatus === "loading" || medicationsStatus === "loading";
  const appointmentsSummary = upcomingAppointments.length ? `${upcomingAppointments.length} Upcoming` : "No Upcoming Appointments";
  const medicationSummary = upcomingDoses.length
    ? `${upcomingDoses.length} Upcoming`
    : `${activeMedications.length} Active`;
  const vaccinesDueCount = upcomingVaccinations.length + overdueVaccinations.length;
  const vaccinationSummary = overdueVaccinations.length
    ? `${overdueVaccinations.length} Overdue`
    : upcomingVaccinations.length
      ? `${upcomingVaccinations.length} Upcoming`
      : "No Upcoming Doses";
  const healthSummaryItems = [
    { label: pluralize(activeMedications.length, "Active Medication"), tone: "blue" as const },
    { label: pluralize(vaccinesDueCount, "Vaccine Due", "Vaccines Due"), tone: overdueVaccinations.length ? "rose" as const : "amber" as const },
    { label: pluralize(activeAllergies.length, "Active Allergy", "Active Allergies"), tone: activeAllergies.length ? "rose" as const : "emerald" as const },
    { label: pluralize(activeConditions.length, "Active Condition"), tone: "lavender" as const },
  ];

  return (
    <div className="space-y-4 lg:space-y-5">
      <PageHero icon={HeartPulse} title="Health" description="Clinical records and medication details." />


      {overviewError || medicationsError ? (
        <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">
          {overviewError ?? medicationsError}
        </p>
      ) : null}

      {isLoading ? <BrandedLoader compact label="Preparing health records" /> : null}

      <HealthSummaryStrip items={healthSummaryItems} />

      <section className="rounded-[1.5rem] bg-white px-4 shadow-soft sm:px-5 lg:max-w-3xl">
        <HealthNavRow to="/app/medications" icon={Pill} label="Medications & Supplements" summary={medicationSummary} tone="blue" />
        <HealthNavRow to="/app/vaccinations" icon={Syringe} label="Vaccinations" summary={vaccinationSummary} tone={overdueVaccinations.length ? "rose" : "amber"} />
        <HealthNavRow to="/app/appointments" icon={Stethoscope} label="Appointments" summary={appointmentsSummary} tone="blue" />
        <HealthNavRow to="/app/allergies" icon={AlertTriangle} label="Allergies" summary={`${activeAllergies.length} Active`} tone={activeAllergies.length ? "rose" : "emerald"} />
        <HealthNavRow to="/app/known-health-conditions" icon={HeartPulse} label="Known Health Conditions" summary={`${activeConditions.length} Active`} tone="lavender" />
      </section>
    </div>
  );
}

const documentUploadStatusLabels = {
  ready: "Ready to Upload",
  preparing: "Preparing Upload",
  uploading: "Uploading Document",
  finalizing: "Finalizing Upload",
  success: "Upload Complete",
  error: "Needs Attention",
};

const documentUploadItemStatusLabels = {
  waiting: "Waiting",
  uploading: "Uploading",
  finalizing: "Finalizing",
  complete: "Complete",
  failed: "Failed",
};

function isDocumentUploadActive(status: ReturnType<typeof useDocumentUpload>["status"]) {
  return status === "preparing" || status === "uploading" || status === "finalizing";
}

type FileManagerView = "files" | "trash";
type UploadSource = "camera" | "file";
type DocumentActionStatus = "idle" | "bulkDeleting" | "bulkMoving" | "deleting" | "emptyingTrash" | "moving" | "renaming";
type FolderActionStatus = "idle" | "deleting" | "saving";

type DocumentActionMenuPlacement = {
  documentId: string;
  left: number;
  top: number;
};

type FolderActionMenuPlacement = {
  folderId: string;
  left: number;
  top: number;
};

const BULK_DOCUMENT_ACTION_CONCURRENCY = 3;
type SelectableHealthVaultItemType = "file" | "folder";

type FolderDestinationOption = {
  folderId: string;
  label: string;
  depth: number;
};

function normalizeFolderParentId(folder: DocumentFolder) {
  return folder.parent_folder_id?.trim() || null;
}

function isFolderInParent(folder: DocumentFolder, parentFolderId: string | null | undefined) {
  const parentId = parentFolderId && parentFolderId !== "root" ? parentFolderId : null;
  return normalizeFolderParentId(folder) === parentId;
}

function sortFoldersByName(folders: DocumentFolder[]) {
  return [...folders].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
}

function childFoldersFor(parentFolderId: string | null | undefined, folders: DocumentFolder[]) {
  return sortFoldersByName(folders.filter((folder) => isFolderInParent(folder, parentFolderId)));
}

function folderAncestryFor(folderId: string | null | undefined, folders: DocumentFolder[]) {
  if (!folderId || folderId === "root") {
    return [];
  }

  const byId = new Map(folders.map((folder) => [folder.folder_id, folder]));
  const ancestry: DocumentFolder[] = [];
  const visited = new Set<string>();
  let currentId: string | null = folderId;

  while (currentId && !visited.has(currentId)) {
    visited.add(currentId);
    const folder = byId.get(currentId);
    if (!folder) {
      break;
    }
    ancestry.unshift(folder);
    currentId = normalizeFolderParentId(folder);
  }

  return ancestry;
}

function logicalFolderPathSegments(folderId: string | null | undefined, folders: DocumentFolder[]) {
  if (!folderId || folderId === "root") {
    return ["Your Files"];
  }

  const ancestry = folderAncestryFor(folderId, folders);
  return ancestry.length ? ancestry.map((folder) => folder.name) : ["Your Files"];
}

function logicalFolderPathLabel(segments: string[]) {
  return `/${segments.join("/")}`;
}

function folderDestinationOptions(folders: DocumentFolder[]) {
  const options: FolderDestinationOption[] = [];
  const visited = new Set<string>();

  const appendChildren = (parentFolderId: string | null, depth: number, prefixSegments: string[]) => {
    for (const folder of childFoldersFor(parentFolderId, folders)) {
      if (visited.has(folder.folder_id)) {
        continue;
      }
      visited.add(folder.folder_id);
      const segments = [...prefixSegments, folder.name];
      options.push({
        folderId: folder.folder_id,
        label: logicalFolderPathLabel(segments),
        depth,
      });
      appendChildren(folder.folder_id, depth + 1, segments);
    }
  };

  appendChildren(null, 0, []);
  return options;
}

function selectionKey(type: SelectableHealthVaultItemType, id: string) {
  return `${type}:${id}`;
}

async function runBoundedDocumentActions<TItem, TResult>(
  items: TItem[],
  concurrency: number,
  action: (item: TItem) => Promise<TResult>,
) {
  const results: Array<TResult | null> = Array.from({ length: items.length }, () => null);
  let nextIndex = 0;

  const worker = async () => {
    for (;;) {
      const index = nextIndex;
      nextIndex += 1;
      if (index >= items.length) {
        return;
      }
      results[index] = await action(items[index]);
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, () => worker()));
  return results;
}

function computeMenuPlacement(button: HTMLButtonElement, menuWidth: number, menuHeight: number) {
  const rect = button.getBoundingClientRect();
  const viewportPadding = 12;
  const gap = 8;
  const belowTop = rect.bottom + gap;
  const hasRoomBelow = belowTop + menuHeight <= window.innerHeight - viewportPadding;
  const top = hasRoomBelow ? belowTop : Math.max(viewportPadding, rect.top - menuHeight - gap);
  const left = Math.min(
    Math.max(viewportPadding, rect.right - menuWidth),
    Math.max(viewportPadding, window.innerWidth - menuWidth - viewportPadding),
  );

  return { left, top };
}

function renameExtensionHint(contentType: string) {
  if (contentType === "application/pdf") {
    return "Keep the .pdf extension.";
  }
  if (contentType === "image/png") {
    return "Keep the .png extension.";
  }
  return "Keep the .jpg or .jpeg extension.";
}

function RenameDocumentDialog({
  document,
  error,
  onClose,
  onSubmit,
  onTitleChange,
  status,
  title,
}: {
  document: DocumentFile;
  error: string | null;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onTitleChange: (value: string) => void;
  status: DocumentActionStatus;
  title: string;
}) {
  const busy = status === "renaming";

  return (
    <div className="fixed inset-0 z-50 grid place-items-end bg-slate-950/35 px-0 py-0 backdrop-blur-sm sm:place-items-center sm:px-4 sm:py-8">
      <form
        onSubmit={onSubmit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rename-document-title"
        className="w-full rounded-t-[1.5rem] bg-white p-5 shadow-[0_30px_100px_-45px_rgba(15,23,42,0.75)] sm:max-w-md sm:rounded-[1.5rem]"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 id="rename-document-title" className="text-lg font-semibold text-foreground">Rename File</h2>
            <p className="mt-1 truncate text-sm font-medium text-muted-foreground">{renameExtensionHint(document.content_type)}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid size-9 shrink-0 place-items-center rounded-full border border-border bg-white text-muted-foreground transition hover:border-primary/30 hover:text-foreground focus:outline-none focus:ring-4 focus:ring-primary/10"
            aria-label="Close Rename File"
            disabled={busy}
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>
        <label htmlFor="rename-document-input" className="mt-5 block text-sm font-semibold text-foreground">
          Filename
        </label>
        <input
          id="rename-document-input"
          value={title}
          onChange={(event) => onTitleChange(event.target.value)}
          maxLength={160}
          className="mt-2 min-h-11 w-full rounded-2xl border border-border bg-white px-4 py-2.5 text-sm font-medium text-foreground outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
          disabled={busy}
          autoFocus
        />
        {error ? <p className="mt-3 text-sm font-medium text-destructive">{error}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-10 items-center justify-center rounded-full border border-border bg-white px-4 text-sm font-semibold text-foreground transition hover:border-primary/30 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="inline-flex min-h-10 items-center justify-center rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-soft transition hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
            disabled={busy}
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

function DeleteDocumentDialog({
  document,
  error,
  onClose,
  onConfirm,
  status,
}: {
  document: DocumentFile;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
  status: DocumentActionStatus;
}) {
  const busy = status === "deleting";

  return (
    <div className="fixed inset-0 z-50 grid place-items-end bg-slate-950/35 px-0 py-0 backdrop-blur-sm sm:place-items-center sm:px-4 sm:py-8">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-document-title"
        className="w-full rounded-t-[1.5rem] bg-white p-5 shadow-[0_30px_100px_-45px_rgba(15,23,42,0.75)] sm:max-w-md sm:rounded-[1.5rem]"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="delete-document-title" className="text-lg font-semibold text-foreground">Delete File?</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              This moves {document.original_filename} to Trash. You can restore it while Trash retention is available.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid size-9 shrink-0 place-items-center rounded-full border border-border bg-white text-muted-foreground transition hover:border-primary/30 hover:text-foreground focus:outline-none focus:ring-4 focus:ring-primary/10"
            aria-label="Close Delete File"
            disabled={busy}
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>
        {error ? <p className="mt-3 text-sm font-medium text-destructive">{error}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-10 items-center justify-center rounded-full border border-border bg-white px-4 text-sm font-semibold text-foreground transition hover:border-primary/30 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex min-h-10 items-center justify-center rounded-full bg-destructive px-4 text-sm font-semibold text-white shadow-soft transition hover:bg-destructive/90 focus:outline-none focus:ring-4 focus:ring-destructive/20 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
            disabled={busy}
          >
            Delete
          </button>
        </div>
      </section>
    </div>
  );
}

function BulkDeleteDocumentsDialog({
  count,
  error,
  onClose,
  onConfirm,
  status,
}: {
  count: number;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
  status: DocumentActionStatus;
}) {
  const busy = status === "bulkDeleting";

  return (
    <div className="fixed inset-0 z-50 grid place-items-end bg-slate-950/35 px-0 py-0 backdrop-blur-sm sm:place-items-center sm:px-4 sm:py-8">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-delete-documents-title"
        className="w-full rounded-t-[1.5rem] bg-white p-5 shadow-[0_30px_100px_-45px_rgba(15,23,42,0.75)] sm:max-w-md sm:rounded-[1.5rem]"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="bulk-delete-documents-title" className="text-lg font-semibold text-foreground">Delete Selected Files?</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              This deletes {count} selected {count === 1 ? "item" : "items"}. Files move to Trash. Folders are deleted only when empty.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid size-9 shrink-0 place-items-center rounded-full border border-border bg-white text-muted-foreground transition hover:border-primary/30 hover:text-foreground focus:outline-none focus:ring-4 focus:ring-primary/10"
            aria-label="Close Delete Selected Files"
            disabled={busy}
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>
        {error ? <p className="mt-3 text-sm font-medium text-destructive">{error}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-10 items-center justify-center rounded-full border border-border bg-white px-4 text-sm font-semibold text-foreground transition hover:border-primary/30 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex min-h-10 items-center justify-center rounded-full bg-destructive px-4 text-sm font-semibold text-white shadow-soft transition hover:bg-destructive/90 focus:outline-none focus:ring-4 focus:ring-destructive/20 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
            disabled={busy}
          >
            {busy ? "Deleting" : "Delete"}
          </button>
        </div>
      </section>
    </div>
  );
}

function FolderNameDialog({
  error,
  folder,
  folders,
  name,
  onClose,
  onNameChange,
  onParentFolderChange,
  onSubmit,
  parentFolderId,
  status,
}: {
  error: string | null;
  folder?: DocumentFolder;
  folders: DocumentFolder[];
  name: string;
  onClose: () => void;
  onNameChange: (value: string) => void;
  onParentFolderChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  parentFolderId: string;
  status: FolderActionStatus;
}) {
  const busy = status === "saving";
  const title = folder ? "Rename Folder" : "Create Folder";
  const destinationOptions = folderDestinationOptions(folders);

  return (
    <div className="fixed inset-0 z-50 grid place-items-end bg-slate-950/35 px-0 py-0 backdrop-blur-sm sm:place-items-center sm:px-4 sm:py-8">
      <form
        onSubmit={onSubmit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="folder-name-title"
        className="w-full rounded-t-[1.5rem] bg-white p-5 shadow-[0_30px_100px_-45px_rgba(15,23,42,0.75)] sm:max-w-md sm:rounded-[1.5rem]"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 id="folder-name-title" className="text-lg font-semibold text-foreground">{title}</h2>
            <p className="mt-1 text-sm font-medium text-muted-foreground">Folder names stay private in Your Files.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid size-9 shrink-0 place-items-center rounded-full border border-border bg-white text-muted-foreground transition hover:border-primary/30 hover:text-foreground focus:outline-none focus:ring-4 focus:ring-primary/10"
            aria-label={`Close ${title}`}
            disabled={busy}
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>
        <label htmlFor="folder-name-input" className="mt-5 block text-sm font-semibold text-foreground">
          Folder Name
        </label>
        <input
          id="folder-name-input"
          value={name}
          onChange={(event) => onNameChange(event.target.value)}
          maxLength={80}
          className="mt-2 min-h-11 w-full rounded-2xl border border-border bg-white px-4 py-2.5 text-sm font-medium text-foreground outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
          disabled={busy}
          autoFocus
        />
        {!folder ? (
          <>
            <label htmlFor="folder-parent-input" className="mt-4 block text-sm font-semibold text-foreground">
              Location
            </label>
            <select
              id="folder-parent-input"
              value={parentFolderId}
              onChange={(event) => onParentFolderChange(event.target.value)}
              className="mt-2 min-h-11 w-full rounded-2xl border border-border bg-white px-4 py-2.5 text-sm font-medium text-foreground outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              disabled={busy}
            >
              <option value="root">/</option>
              {destinationOptions.map((option) => (
                <option key={option.folderId} value={option.folderId}>
                  {`${"\u00a0".repeat(option.depth * 2)}${option.label}`}
                </option>
              ))}
            </select>
          </>
        ) : null}
        {error ? <p className="mt-3 text-sm font-medium text-destructive">{error}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-10 items-center justify-center rounded-full border border-border bg-white px-4 text-sm font-semibold text-foreground transition hover:border-primary/30 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="inline-flex min-h-10 items-center justify-center rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-soft transition hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
            disabled={busy}
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

function DeleteFolderDialog({
  error,
  folder,
  onClose,
  onConfirm,
  status,
}: {
  error: string | null;
  folder: DocumentFolder;
  onClose: () => void;
  onConfirm: () => void;
  status: FolderActionStatus;
}) {
  const busy = status === "deleting";

  return (
    <div className="fixed inset-0 z-50 grid place-items-end bg-slate-950/35 px-0 py-0 backdrop-blur-sm sm:place-items-center sm:px-4 sm:py-8">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-folder-title"
        className="w-full rounded-t-[1.5rem] bg-white p-5 shadow-[0_30px_100px_-45px_rgba(15,23,42,0.75)] sm:max-w-md sm:rounded-[1.5rem]"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="delete-folder-title" className="text-lg font-semibold text-foreground">Delete Folder?</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              This deletes the folder "{folder.name}" only when it is empty. Files are not deleted.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid size-9 shrink-0 place-items-center rounded-full border border-border bg-white text-muted-foreground transition hover:border-primary/30 hover:text-foreground focus:outline-none focus:ring-4 focus:ring-primary/10"
            aria-label="Close Delete Folder"
            disabled={busy}
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>
        {error ? <p className="mt-3 text-sm font-medium text-destructive">{error}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-10 items-center justify-center rounded-full border border-border bg-white px-4 text-sm font-semibold text-foreground transition hover:border-primary/30 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex min-h-10 items-center justify-center rounded-full bg-destructive px-4 text-sm font-semibold text-white shadow-soft transition hover:bg-destructive/90 focus:outline-none focus:ring-4 focus:ring-destructive/20 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
            disabled={busy}
          >
            Delete
          </button>
        </div>
      </section>
    </div>
  );
}

function EmptyTrashDialog({
  error,
  onClose,
  onConfirm,
  status,
}: {
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
  status: DocumentActionStatus;
}) {
  const busy = status === "emptyingTrash";

  return (
    <div className="fixed inset-0 z-50 grid place-items-end bg-slate-950/35 px-0 py-0 backdrop-blur-sm sm:place-items-center sm:px-4 sm:py-8">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="empty-trash-title"
        className="w-full rounded-t-[1.5rem] bg-white p-5 shadow-[0_30px_100px_-45px_rgba(15,23,42,0.75)] sm:max-w-md sm:rounded-[1.5rem]"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="empty-trash-title" className="text-lg font-semibold text-foreground">Empty Trash?</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              This permanently deletes all files currently in Trash. They cannot be recovered.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid size-9 shrink-0 place-items-center rounded-full border border-border bg-white text-muted-foreground transition hover:border-primary/30 hover:text-foreground focus:outline-none focus:ring-4 focus:ring-primary/10"
            aria-label="Close Empty Trash"
            disabled={busy}
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>
        {error ? <p className="mt-3 text-sm font-medium text-destructive">{error}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-10 items-center justify-center rounded-full border border-border bg-white px-4 text-sm font-semibold text-foreground transition hover:border-primary/30 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex min-h-10 items-center justify-center rounded-full bg-destructive px-4 text-sm font-semibold text-white shadow-soft transition hover:bg-destructive/90 focus:outline-none focus:ring-4 focus:ring-destructive/20 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
            disabled={busy}
          >
            {busy ? "Emptying…" : "Empty Trash"}
          </button>
        </div>
      </section>
    </div>
  );
}

function MoveDocumentDialog({
  document,
  error,
  folders,
  onClose,
  onSubmit,
  onTargetChange,
  status,
  targetFolderId,
}: {
  document: DocumentFile;
  error: string | null;
  folders: DocumentFolder[];
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onTargetChange: (value: string) => void;
  status: DocumentActionStatus;
  targetFolderId: string;
}) {
  const busy = status === "moving";
  const destinationOptions = folderDestinationOptions(folders);

  return (
    <div className="fixed inset-0 z-50 grid place-items-end bg-slate-950/35 px-0 py-0 backdrop-blur-sm sm:place-items-center sm:px-4 sm:py-8">
      <form
        onSubmit={onSubmit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="move-document-title"
        className="w-full rounded-t-[1.5rem] bg-white p-5 shadow-[0_30px_100px_-45px_rgba(15,23,42,0.75)] sm:max-w-md sm:rounded-[1.5rem]"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 id="move-document-title" className="text-lg font-semibold text-foreground">Move File</h2>
            <p className="mt-1 truncate text-sm font-medium text-muted-foreground">{document.original_filename}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid size-9 shrink-0 place-items-center rounded-full border border-border bg-white text-muted-foreground transition hover:border-primary/30 hover:text-foreground focus:outline-none focus:ring-4 focus:ring-primary/10"
            aria-label="Close Move File"
            disabled={busy}
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>
        <label htmlFor="move-document-folder" className="mt-5 block text-sm font-semibold text-foreground">
          Destination
        </label>
        <select
          id="move-document-folder"
          value={targetFolderId}
          onChange={(event) => onTargetChange(event.target.value)}
          className="mt-2 min-h-11 w-full rounded-2xl border border-border bg-white px-4 py-2.5 text-sm font-medium text-foreground outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
          disabled={busy}
        >
          <option value="root">/Your Files</option>
          {destinationOptions.map((option) => (
            <option key={option.folderId} value={option.folderId}>
              {`${"\u00a0".repeat(option.depth * 2)}${option.label}`}
            </option>
          ))}
        </select>
        {error ? <p className="mt-3 text-sm font-medium text-destructive">{error}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-10 items-center justify-center rounded-full border border-border bg-white px-4 text-sm font-semibold text-foreground transition hover:border-primary/30 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="inline-flex min-h-10 items-center justify-center rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-soft transition hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
            disabled={busy}
          >
            Move
          </button>
        </div>
      </form>
    </div>
  );
}

function BulkMoveDocumentsDialog({
  count,
  error,
  folders,
  onClose,
  onSubmit,
  onTargetChange,
  status,
  targetFolderId,
}: {
  count: number;
  error: string | null;
  folders: DocumentFolder[];
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onTargetChange: (value: string) => void;
  status: DocumentActionStatus;
  targetFolderId: string;
}) {
  const busy = status === "bulkMoving";
  const destinationOptions = folderDestinationOptions(folders);

  return (
    <div className="fixed inset-0 z-50 grid place-items-end bg-slate-950/35 px-0 py-0 backdrop-blur-sm sm:place-items-center sm:px-4 sm:py-8">
      <form
        onSubmit={onSubmit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-move-documents-title"
        className="w-full rounded-t-[1.5rem] bg-white p-5 shadow-[0_30px_100px_-45px_rgba(15,23,42,0.75)] sm:max-w-md sm:rounded-[1.5rem]"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 id="bulk-move-documents-title" className="text-lg font-semibold text-foreground">Move Selected Files</h2>
            <p className="mt-1 text-sm font-medium text-muted-foreground">{count} selected {count === 1 ? "item" : "items"}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid size-9 shrink-0 place-items-center rounded-full border border-border bg-white text-muted-foreground transition hover:border-primary/30 hover:text-foreground focus:outline-none focus:ring-4 focus:ring-primary/10"
            aria-label="Close Move Selected Files"
            disabled={busy}
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>
        <label htmlFor="bulk-move-document-folder" className="mt-5 block text-sm font-semibold text-foreground">
          Destination
        </label>
        <select
          id="bulk-move-document-folder"
          value={targetFolderId}
          onChange={(event) => onTargetChange(event.target.value)}
          className="mt-2 min-h-11 w-full rounded-2xl border border-border bg-white px-4 py-2.5 text-sm font-medium text-foreground outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
          disabled={busy}
        >
          <option value="root">/Your Files</option>
          {destinationOptions.map((option) => (
            <option key={option.folderId} value={option.folderId}>
              {`${"\u00a0".repeat(option.depth * 2)}${option.label}`}
            </option>
          ))}
        </select>
        {error ? <p className="mt-3 text-sm font-medium text-destructive">{error}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-10 items-center justify-center rounded-full border border-border bg-white px-4 text-sm font-semibold text-foreground transition hover:border-primary/30 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="inline-flex min-h-10 items-center justify-center rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-soft transition hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
            disabled={busy}
          >
            {busy ? "Moving" : "Move"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ReadyToShareDialog({
  filename,
  onClose,
  onShare,
}: {
  filename: string;
  onClose: () => void;
  onShare: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-end bg-slate-950/35 px-0 py-0 backdrop-blur-sm sm:place-items-center sm:px-4 sm:py-8">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="ready-to-share-title"
        className="w-full rounded-t-[1.5rem] bg-white p-5 shadow-[0_30px_100px_-45px_rgba(15,23,42,0.75)] sm:max-w-md sm:rounded-[1.5rem]"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 id="ready-to-share-title" className="text-lg font-semibold text-foreground">Ready to Share</h2>
            <p className="mt-1 truncate text-sm font-medium text-muted-foreground">{filename}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid size-9 shrink-0 place-items-center rounded-full border border-border bg-white text-muted-foreground transition hover:border-primary/30 hover:text-foreground focus:outline-none focus:ring-4 focus:ring-primary/10"
            aria-label="Close Ready to Share"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">Your file is ready to share.</p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-10 items-center justify-center rounded-full border border-border bg-white px-4 text-sm font-semibold text-foreground transition hover:border-primary/30 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onShare}
            className="inline-flex min-h-10 items-center justify-center gap-2 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-soft transition hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/10"
          >
            <Share2 className="size-4" aria-hidden="true" />
            Share File
          </button>
        </div>
      </section>
    </div>
  );
}

type ReadyToShare = {
  documentId: string;
  file: File;
  filename: string;
};

function SharePreparationProgress({ progress }: { progress: ShareProgress | null }) {
  if (!progress) {
    return null;
  }

  const determinate = progress.percentage !== null;

  return (
    <div className="border-b border-border/70 py-2" role="status">
      <p className="text-xs font-semibold text-muted-foreground">
        {determinate ? `Preparing file to share… ${progress.percentage}%` : "Preparing file to share…"}
      </p>
      <div
        className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-primary/10"
        role="progressbar"
        aria-label="File preparation progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress.percentage ?? undefined}
      >
        <div
          className={cn(
            "h-full rounded-full bg-primary",
            determinate ? "transition-all motion-reduce:transition-none" : "w-2/5 animate-pulse",
          )}
          style={determinate ? { width: `${progress.percentage}%` } : undefined}
        />
      </div>
    </div>
  );
}

export function MyFilesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const [fileManagerView, setFileManagerView] = useState<FileManagerView>("files");
  const [selectMode, setSelectMode] = useState(false);
  const [selectedItemKeys, setSelectedItemKeys] = useState<Set<string>>(() => new Set());
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadSource, setUploadSource] = useState<UploadSource>("file");
  const [cameraFilename, setCameraFilename] = useState("");
  const [cameraInputKey, setCameraInputKey] = useState(0);
  const [activeMenuDocumentId, setActiveMenuDocumentId] = useState<string | null>(null);
  const [documentActionMenuPlacement, setDocumentActionMenuPlacement] = useState<DocumentActionMenuPlacement | null>(null);
  const [activeFolderMenuId, setActiveFolderMenuId] = useState<string | null>(null);
  const [folderActionMenuPlacement, setFolderActionMenuPlacement] = useState<FolderActionMenuPlacement | null>(null);
  const [renamingDocument, setRenamingDocument] = useState<DocumentFile | null>(null);
  const [deletingDocument, setDeletingDocument] = useState<DocumentFile | null>(null);
  const [renameTitle, setRenameTitle] = useState("");
  const [documentActionStatus, setDocumentActionStatus] = useState<DocumentActionStatus>("idle");
  const [documentActionError, setDocumentActionError] = useState<string | null>(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkMoveDialogOpen, setBulkMoveDialogOpen] = useState(false);
  const [bulkMoveTargetFolderId, setBulkMoveTargetFolderId] = useState("root");
  const [folderNameDialog, setFolderNameDialog] = useState<{ mode: "create" } | { mode: "rename"; folder: DocumentFolder } | null>(null);
  const [deletingFolder, setDeletingFolder] = useState<DocumentFolder | null>(null);
  const [folderName, setFolderName] = useState("");
  const [folderParentFolderId, setFolderParentFolderId] = useState("root");
  const [folderActionStatus, setFolderActionStatus] = useState<FolderActionStatus>("idle");
  const [folderActionError, setFolderActionError] = useState<string | null>(null);
  const [movingDocument, setMovingDocument] = useState<DocumentFile | null>(null);
  const [moveTargetFolderId, setMoveTargetFolderId] = useState("root");
  const [preparingShareDocumentId, setPreparingShareDocumentId] = useState<string | null>(null);
  const [readyToShare, setReadyToShare] = useState<ReadyToShare | null>(null);
  const [emptyTrashDialogOpen, setEmptyTrashDialogOpen] = useState(false);
  const [emptyTrashNotice, setEmptyTrashNotice] = useState<string | null>(null);
  const selectionLocationRef = useRef<string | null>(null);
  const { error, items: uploadItems, progress, reset, status, uploadDocument, uploadDocuments } = useDocumentUpload();
  const {
    commitShare,
    createFolder,
    deleteFolder,
    deleteFolderOnly,
    deleteDocument,
    deleteDocumentOnly,
    documents,
    downloadDocument,
    emptyTrash,
    error: documentsError,
    folderError,
    folderFilter,
    folders,
    folderStatus,
    moveDocument,
    moveDocumentOnly,
    moveFolderOnly,
    prepareShare,
    refreshDocuments,
    refreshFolders,
    refreshTrash,
    renameDocument,
    renameFolder,
    restoreDocument,
    setFolderFilter,
    shareProgress,
    status: documentsStatus,
    trashError,
    trashStatus,
    trashedDocuments,
  } = useDocuments();
  const active = isDocumentUploadActive(status);
  const selectedFile = selectedFiles[0] ?? null;
  const selectedFileErrors = selectedFiles.map((file) => validateDocumentFile(file));
  const selectedFileError = selectedFileErrors.find((nextError): nextError is string => Boolean(nextError)) ?? null;
  const uploadFilename = selectedFile && uploadSource === "camera" ? uploadFilenameForFile(selectedFile, cameraFilename) : "";
  const canUpload =
    selectedFiles.length > 0 &&
    !active &&
    status !== "success" &&
    (selectedFiles.length > 1 ? selectedFileErrors.some((nextError) => !nextError) : !selectedFileError) &&
    !fileError;
  const visibleError = fileError ?? error;
  const selectedFolder = folderFilter !== "all" && folderFilter !== "root" ? folders.find((folder) => folder.folder_id === folderFilter) ?? null : null;
  const selectedFolderAncestry = folderAncestryFor(selectedFolder?.folder_id, folders);
  const currentParentFolderId = fileManagerView === "files" && folderFilter !== "all" && folderFilter !== "root" ? folderFilter : null;
  const visibleFolders = useMemo(
    () => (fileManagerView === "files" ? childFoldersFor(currentParentFolderId, folders) : []),
    [currentParentFolderId, fileManagerView, folders],
  );
  const folderOptions = useMemo(() => folderDestinationOptions(folders), [folders]);
  const selectableVisibleKeys = useMemo(() => [
    ...visibleFolders.map((folder) => selectionKey("folder", folder.folder_id)),
    ...documents.map((document) => selectionKey("file", document.document_id)),
  ], [documents, visibleFolders]);
  const selectedVisibleDocuments = documents.filter((document) => selectedItemKeys.has(selectionKey("file", document.document_id)));
  const selectedVisibleFolders = visibleFolders.filter((folder) => selectedItemKeys.has(selectionKey("folder", folder.folder_id)));
  const selectedItemCount = selectedVisibleDocuments.length + selectedVisibleFolders.length;
  const allVisibleSelected = selectableVisibleKeys.length > 0 && selectableVisibleKeys.every((key) => selectedItemKeys.has(key));
  const bulkBusy = documentActionStatus === "bulkDeleting" || documentActionStatus === "bulkMoving";

  const clearSelection = useCallback(() => {
    setSelectMode(false);
    setSelectedItemKeys(new Set());
    setBulkDeleteDialogOpen(false);
    setBulkMoveDialogOpen(false);
  }, []);

  const openRootFolder = useCallback(() => {
    clearSelection();
    setFileManagerView("files");
    setFolderFilter("root");
    setSearchParams({}, { replace: false });
    setEmptyTrashNotice(null);
  }, [clearSelection, setFolderFilter, setSearchParams]);

  const openFolder = useCallback(
    (folderId: string) => {
      clearSelection();
      setFileManagerView("files");
      setFolderFilter(folderId);
      setSearchParams({ folder: folderId }, { replace: false });
      setEmptyTrashNotice(null);
    },
    [clearSelection, setFolderFilter, setSearchParams],
  );

  const openTrash = useCallback(() => {
    clearSelection();
    setFileManagerView("trash");
    setSearchParams({ view: "trash" }, { replace: false });
  }, [clearSelection, setSearchParams]);

  useEffect(() => {
    if (!activeMenuDocumentId && !activeFolderMenuId) {
      return undefined;
    }

    const closeMenus = (event: PointerEvent) => {
      if (event.target instanceof Element && event.target.closest("[data-document-menu-root], [data-folder-menu-root]")) {
        return;
      }
      setActiveMenuDocumentId(null);
      setDocumentActionMenuPlacement(null);
      setActiveFolderMenuId(null);
      setFolderActionMenuPlacement(null);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setActiveMenuDocumentId(null);
        setDocumentActionMenuPlacement(null);
        setActiveFolderMenuId(null);
        setFolderActionMenuPlacement(null);
      }
    };
    const closeForViewportChange = () => {
      setActiveMenuDocumentId(null);
      setDocumentActionMenuPlacement(null);
      setActiveFolderMenuId(null);
      setFolderActionMenuPlacement(null);
    };

    document.addEventListener("pointerdown", closeMenus);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", closeForViewportChange);
    window.addEventListener("scroll", closeForViewportChange, true);
    return () => {
      document.removeEventListener("pointerdown", closeMenus);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", closeForViewportChange);
      window.removeEventListener("scroll", closeForViewportChange, true);
    };
  }, [activeFolderMenuId, activeMenuDocumentId]);

  useEffect(() => {
    setSelectedItemKeys((currentKeys) => {
      const visibleKeys = new Set(selectableVisibleKeys);
      const nextKeys = new Set([...currentKeys].filter((key) => visibleKeys.has(key)));
      return nextKeys.size === currentKeys.size ? currentKeys : nextKeys;
    });
  }, [selectableVisibleKeys]);

  const enterSelectMode = () => {
    setDocumentActionError(null);
    setActiveMenuDocumentId(null);
    setDocumentActionMenuPlacement(null);
    setActiveFolderMenuId(null);
    setFolderActionMenuPlacement(null);
    setSelectMode(true);
  };

  const toggleItemSelection = (type: SelectableHealthVaultItemType, itemId: string) => {
    setDocumentActionError(null);
    setSelectedItemKeys((currentKeys) => {
      const key = selectionKey(type, itemId);
      const nextKeys = new Set(currentKeys);
      if (nextKeys.has(key)) {
        nextKeys.delete(key);
      } else {
        nextKeys.add(key);
      }
      return nextKeys;
    });
  };

  const toggleSelectAllVisible = () => {
    setDocumentActionError(null);
    setSelectedItemKeys((currentKeys) => {
      if (selectableVisibleKeys.length > 0 && selectableVisibleKeys.every((key) => currentKeys.has(key))) {
        return new Set();
      }
      return new Set(selectableVisibleKeys);
    });
  };

  const openBulkMoveDialog = () => {
    if (!selectedItemCount) {
      return;
    }
    setDocumentActionError(null);
    setBulkMoveTargetFolderId(folderFilter !== "all" && folderFilter !== "root" ? "root" : folderOptions[0]?.folderId ?? "root");
    setBulkMoveDialogOpen(true);
  };

  const openBulkDeleteDialog = () => {
    if (!selectedItemCount) {
      return;
    }
    setDocumentActionError(null);
    setBulkDeleteDialogOpen(true);
  };

  const handleFileChange = (source: UploadSource, event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.currentTarget.files ?? []);
    const nextFiles = source === "camera" ? files.slice(0, 1) : files;
    const validationErrors = nextFiles.map((file) => validateDocumentFile(file)).filter((nextError): nextError is string => Boolean(nextError));
    setUploadSource(source);
    setSelectedFiles(nextFiles);
    setFileError(validationErrors.length ? validationErrors[0] : null);
    if (source === "file") {
      setCameraFilename("");
    }
    if (status === "success" || status === "error") {
      reset();
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedFiles.length) {
      setFileError("Choose a PDF, JPEG or PNG document to upload.");
      return;
    }

    const validUploads = selectedFiles
      .map((file) => ({ file, error: validateDocumentFile(file) }))
      .filter((item): item is { file: File; error: null } => !item.error);

    if (!validUploads.length) {
      setFileError(selectedFileError ?? "Choose a PDF, JPEG or PNG document to upload.");
      return;
    }

    setFileError(null);
    const completedUploads =
      uploadSource === "camera"
        ? await uploadDocument(validUploads[0].file, uploadFilenameForFile(validUploads[0].file, cameraFilename)).then((completed) => (completed ? [completed] : []))
        : validUploads.length === 1
          ? await uploadDocument(validUploads[0].file, validUploads[0].file.name).then((completed) => (completed ? [completed] : []))
        : await uploadDocuments(validUploads.map(({ file }) => ({ file })));
    if (completedUploads.length) {
      await refreshDocuments();
    }
    if (completedUploads.length === selectedFiles.length) {
      setUploadOpen(false);
      setSelectedFiles([]);
      setCameraFilename("");
      setFileError(null);
      setInputKey((currentKey) => currentKey + 1);
      setCameraInputKey((currentKey) => currentKey + 1);
      reset();
    }
  };

  const closeUploadPanel = () => {
    if (active) {
      return;
    }
    reset();
    setSelectedFiles([]);
    setCameraFilename("");
    setFileError(null);
    setInputKey((currentKey) => currentKey + 1);
    setCameraInputKey((currentKey) => currentKey + 1);
    setUploadOpen(false);
  };

  const openUploadPanel = useCallback((source: UploadSource = "file") => {
    setFileManagerView("files");
    setUploadSource(source);
    setUploadOpen(true);
    setFileError(null);
    if (status === "success" || status === "error") {
      reset();
    }
  }, [reset, status]);

  useEffect(() => {
    const uploadMode = searchParams.get("upload");
    if (uploadMode !== "file" && uploadMode !== "camera") {
      return;
    }

    openUploadPanel(uploadMode);
    setSearchParams({}, { replace: true });
  }, [openUploadPanel, searchParams, setSearchParams]);

  useEffect(() => {
    const view = searchParams.get("view");
    const folderId = searchParams.get("folder");
    const selectionLocation = `${view === "trash" ? "trash" : "files"}:${folderId?.trim() || "root"}`;

    if (selectionLocationRef.current === null) {
      selectionLocationRef.current = selectionLocation;
    } else if (selectionLocationRef.current !== selectionLocation) {
      selectionLocationRef.current = selectionLocation;
      clearSelection();
    }

    if (view === "trash") {
      setFileManagerView("trash");
      return;
    }

    setFileManagerView("files");
    setFolderFilter(folderId?.trim() || "root");
  }, [clearSelection, searchParams, setFolderFilter]);

  const toggleDocumentActionMenu = (documentId: string, button: HTMLButtonElement) => {
    setDocumentActionError(null);
    setActiveFolderMenuId(null);
    setFolderActionMenuPlacement(null);
    if (activeMenuDocumentId === documentId) {
      setActiveMenuDocumentId(null);
      setDocumentActionMenuPlacement(null);
      return;
    }

    const { left, top } = computeMenuPlacement(button, 176, 256);
    setActiveMenuDocumentId(documentId);
    setDocumentActionMenuPlacement({ documentId, left, top });
  };

  const toggleFolderActionMenu = (folderId: string, button: HTMLButtonElement) => {
    setFolderActionError(null);
    setActiveMenuDocumentId(null);
    setDocumentActionMenuPlacement(null);
    if (activeFolderMenuId === folderId) {
      setActiveFolderMenuId(null);
      setFolderActionMenuPlacement(null);
      return;
    }

    const { left, top } = computeMenuPlacement(button, 160, 96);
    setActiveFolderMenuId(folderId);
    setFolderActionMenuPlacement({ folderId, left, top });
  };

  const openRenameDialog = (document: DocumentFile) => {
    setActiveMenuDocumentId(null);
    setDocumentActionMenuPlacement(null);
    setDocumentActionError(null);
    setRenamingDocument(document);
    setRenameTitle(document.original_filename);
  };

  const openDeleteDialog = (document: DocumentFile) => {
    setActiveMenuDocumentId(null);
    setDocumentActionMenuPlacement(null);
    setDocumentActionError(null);
    setDeletingDocument(document);
  };

  const openCreateFolderDialog = () => {
    setFolderActionError(null);
    setFolderName("");
    setFolderParentFolderId(currentParentFolderId ?? "root");
    setFolderNameDialog({ mode: "create" });
  };

  const openRenameFolderDialog = (folder: DocumentFolder) => {
    setFolderActionError(null);
    setFolderName(folder.name);
    setFolderNameDialog({ mode: "rename", folder });
  };

  const openDeleteFolderDialog = (folder: DocumentFolder) => {
    setFolderActionError(null);
    setDeletingFolder(folder);
  };

  const openMoveDialog = (document: DocumentFile) => {
    setActiveMenuDocumentId(null);
    setDocumentActionMenuPlacement(null);
    setDocumentActionError(null);
    setMovingDocument(document);
    setMoveTargetFolderId(document.folder_id ?? "root");
  };

  const handleRestore = async (documentId: string) => {
    setDocumentActionError(null);
    const restored = await restoreDocument(documentId);
    if (restored !== true) {
      setDocumentActionError(typeof restored === "string" ? restored : "MedoPilot could not restore this file.");
    }
  };

  const openEmptyTrashDialog = () => {
    setDocumentActionError(null);
    setEmptyTrashNotice(null);
    setEmptyTrashDialogOpen(true);
  };

  const handleEmptyTrashConfirm = async () => {
    setDocumentActionStatus("emptyingTrash");
    setDocumentActionError(null);
    const result = await emptyTrash();
    setDocumentActionStatus("idle");
    if (result === false) {
      return;
    }
    if (typeof result === "string") {
      setDocumentActionError(result);
      return;
    }
    setEmptyTrashDialogOpen(false);
    setEmptyTrashNotice("Trash is being permanently deleted.");
  };

  const handleRenameSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!renamingDocument) {
      return;
    }

    const nextTitle = renameTitle.trim();
    if (!nextTitle) {
      setDocumentActionError("Enter a filename.");
      return;
    }

    setDocumentActionStatus("renaming");
    setDocumentActionError(null);
    const renamed = await renameDocument(renamingDocument.document_id, nextTitle);
    setDocumentActionStatus("idle");
    if (renamed === true) {
      setRenamingDocument(null);
      setRenameTitle("");
      return;
    }
    setDocumentActionError(typeof renamed === "string" ? renamed : "MedoPilot could not rename this file.");
  };

  const handleFolderNameSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!folderNameDialog) {
      return;
    }

    const nextName = folderName.trim();
    if (!nextName) {
      setFolderActionError("Enter a folder name.");
      return;
    }

    setFolderActionStatus("saving");
    setFolderActionError(null);
    const result =
      folderNameDialog.mode === "create"
        ? await createFolder(nextName, folderParentFolderId)
        : await renameFolder(folderNameDialog.folder.folder_id, nextName);
    setFolderActionStatus("idle");
    if (result === true || (typeof result === "object" && result !== null)) {
      setFolderNameDialog(null);
      setFolderName("");
      if (typeof result === "object" && result !== null) {
        openFolder(result.folder_id);
      }
      return;
    }
    setFolderActionError(typeof result === "string" ? result : "MedoPilot could not save this folder.");
  };

  const handleDeleteFolderConfirm = async () => {
    if (!deletingFolder) {
      return;
    }

    setFolderActionStatus("deleting");
    setFolderActionError(null);
    const deleted = await deleteFolder(deletingFolder.folder_id);
    setFolderActionStatus("idle");
    if (deleted === true) {
      setDeletingFolder(null);
      openRootFolder();
      return;
    }
    setFolderActionError(typeof deleted === "string" ? deleted : "MedoPilot could not delete this folder.");
  };

  const handleMoveSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!movingDocument) {
      return;
    }

    setDocumentActionStatus("moving");
    setDocumentActionError(null);
    const moved = await moveDocument(movingDocument.document_id, moveTargetFolderId === "root" ? null : moveTargetFolderId);
    setDocumentActionStatus("idle");
    if (moved === true) {
      setMovingDocument(null);
      setMoveTargetFolderId("root");
      return;
    }
    setDocumentActionError(typeof moved === "string" ? moved : "MedoPilot could not move this file.");
  };

  const handleBulkMoveSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const documentsToMove = selectedVisibleDocuments;
    const foldersToMove = selectedVisibleFolders;
    const itemsToMoveCount = documentsToMove.length + foldersToMove.length;
    if (!itemsToMoveCount) {
      return;
    }

    setDocumentActionStatus("bulkMoving");
    setDocumentActionError(null);
    const documentResults = await runBoundedDocumentActions(documentsToMove, BULK_DOCUMENT_ACTION_CONCURRENCY, (document) =>
      moveDocumentOnly(document.document_id, bulkMoveTargetFolderId === "root" ? null : bulkMoveTargetFolderId),
    );
    const folderResults = await runBoundedDocumentActions(foldersToMove, BULK_DOCUMENT_ACTION_CONCURRENCY, (folder) =>
      moveFolderOnly(folder.folder_id, bulkMoveTargetFolderId === "root" ? null : bulkMoveTargetFolderId),
    );
    await refreshDocuments();
    await refreshFolders();
    setDocumentActionStatus("idle");

    const failedDocumentKeys = documentsToMove
      .filter((_, index) => documentResults[index] !== true)
      .map((document) => selectionKey("file", document.document_id));
    const failedFolderKeys = foldersToMove
      .filter((_, index) => folderResults[index] !== true)
      .map((folder) => selectionKey("folder", folder.folder_id));
    const failedCount = failedDocumentKeys.length + failedFolderKeys.length;
    if (failedCount > 0) {
      setDocumentActionError(
        `${itemsToMoveCount - failedCount} moved, ${failedCount} ${failedCount === 1 ? "item" : "items"} could not be moved.`,
      );
      setSelectedItemKeys(new Set([...failedFolderKeys, ...failedDocumentKeys]));
      setBulkMoveDialogOpen(false);
      return;
    }

    clearSelection();
  };

  const handleDeleteConfirm = async () => {
    if (!deletingDocument) {
      return;
    }

    setDocumentActionStatus("deleting");
    setDocumentActionError(null);
    const deleted = await deleteDocument(deletingDocument.document_id);
    setDocumentActionStatus("idle");
    if (deleted === true) {
      setDeletingDocument(null);
      return;
    }
    setDocumentActionError(typeof deleted === "string" ? deleted : "MedoPilot could not remove this file.");
  };

  const handleBulkDeleteConfirm = async () => {
    const documentsToDelete = selectedVisibleDocuments;
    const foldersToDelete = selectedVisibleFolders;
    const itemsToDeleteCount = documentsToDelete.length + foldersToDelete.length;
    if (!itemsToDeleteCount) {
      return;
    }

    setDocumentActionStatus("bulkDeleting");
    setDocumentActionError(null);
    const documentResults = await runBoundedDocumentActions(documentsToDelete, BULK_DOCUMENT_ACTION_CONCURRENCY, (document) =>
      deleteDocumentOnly(document.document_id),
    );
    const folderResults = await runBoundedDocumentActions(foldersToDelete, BULK_DOCUMENT_ACTION_CONCURRENCY, (folder) =>
      deleteFolderOnly(folder.folder_id),
    );
    await refreshDocuments();
    await refreshFolders();
    await refreshTrash();
    setDocumentActionStatus("idle");

    const failedDocumentKeys = documentsToDelete
      .filter((_, index) => documentResults[index] !== true)
      .map((document) => selectionKey("file", document.document_id));
    const failedFolderKeys = foldersToDelete
      .filter((_, index) => folderResults[index] !== true)
      .map((folder) => selectionKey("folder", folder.folder_id));
    const failedCount = failedDocumentKeys.length + failedFolderKeys.length;
    if (failedCount > 0) {
      setDocumentActionError(
        `${itemsToDeleteCount - failedCount} deleted, ${failedCount} ${failedCount === 1 ? "item" : "items"} could not be deleted.`,
      );
      setSelectedItemKeys(new Set([...failedFolderKeys, ...failedDocumentKeys]));
      setBulkDeleteDialogOpen(false);
      return;
    }

    clearSelection();
  };

  const handleShareClick = async (document: DocumentFile) => {
    if (preparingShareDocumentId) {
      return;
    }

    setDocumentActionError(null);
    setPreparingShareDocumentId(document.document_id);
    const result = await prepareShare(document.document_id, document.original_filename, document.content_type);
    setPreparingShareDocumentId(null);
    if (result.outcome === "ready") {
      setReadyToShare({ documentId: document.document_id, file: result.file, filename: document.original_filename });
      return;
    }
    if (result.message) {
      setDocumentActionError(result.message);
    }
  };

  const handleConfirmShare = async () => {
    if (!readyToShare) {
      return;
    }

    const result = await commitShare(readyToShare.file);
    if (result.outcome === "cancelled") {
      return;
    }
    setReadyToShare(null);
    if (result.outcome === "blocked") {
      setDocumentActionError(result.message);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <PageHero
        icon={Folder}
        title="Your Files"
        description="Keep your health documents organized and easy to find."
        action={
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={openCreateFolderDialog} className={heroSecondaryActionClassName}>
              <Plus className="size-4" aria-hidden="true" />
              New Folder
            </button>
            <button type="button" onClick={() => openUploadPanel()} className={heroActionClassName}>
              <Upload className="size-4" aria-hidden="true" />
              Upload File
            </button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-1 rounded-full border border-border bg-white p-1 shadow-soft">
          <button
            type="button"
            onClick={openRootFolder}
            aria-current={fileManagerView === "files" ? "true" : undefined}
            className={cn(
              "inline-flex min-h-9 items-center gap-1.5 rounded-full px-3.5 text-sm font-semibold transition focus:outline-none focus:ring-4 focus:ring-primary/10",
              fileManagerView === "files" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-primary/5 hover:text-foreground",
            )}
          >
            <Folder className="size-4" aria-hidden="true" />
            Files
          </button>
          <button
            type="button"
            onClick={openTrash}
            aria-current={fileManagerView === "trash" ? "true" : undefined}
            className={cn(
              "inline-flex min-h-9 items-center gap-1.5 rounded-full px-3.5 text-sm font-semibold transition focus:outline-none focus:ring-4 focus:ring-primary/10",
              fileManagerView === "trash" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-primary/5 hover:text-foreground",
            )}
          >
            <Trash2 className="size-4" aria-hidden="true" />
            Trash
          </button>
        </div>
      </div>

      {fileManagerView === "files" && selectedFolder ? (
        <nav aria-label="Folder path" className="flex min-w-0 items-center justify-between gap-3">
          <ol className="flex min-w-0 items-center gap-1 text-sm font-semibold text-muted-foreground">
            <li>
              <button
                type="button"
                onClick={openRootFolder}
                className="rounded-full px-2 py-1 transition hover:bg-primary/5 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
              >
                Your Files
              </button>
            </li>
            {selectedFolderAncestry.map((folder, index) => {
              const isCurrent = index === selectedFolderAncestry.length - 1;
              return (
                <Fragment key={folder.folder_id}>
                  <li aria-hidden="true">
                    <ChevronRight className="size-3.5" />
                  </li>
                  <li className="min-w-0">
                    {isCurrent ? (
                      <h2 className="inline-block max-w-[14rem] truncate align-middle text-sm font-semibold text-foreground" aria-current="page">
                        {folder.name}
                      </h2>
                    ) : (
                      <button
                        type="button"
                        onClick={() => openFolder(folder.folder_id)}
                        className="inline-block max-w-[10rem] truncate rounded-full px-2 py-1 align-middle transition hover:bg-primary/5 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
                      >
                        {folder.name}
                      </button>
                    )}
                  </li>
                </Fragment>
              );
            })}
          </ol>
          <div className="flex shrink-0 items-center gap-0.5">
            <button
              type="button"
              onClick={() => openRenameFolderDialog(selectedFolder)}
              className="grid size-9 place-items-center rounded-full text-muted-foreground transition hover:bg-primary/10 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
              aria-label={`Rename ${selectedFolder.name}`}
              title="Rename"
            >
              <Pencil className="size-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => openDeleteFolderDialog(selectedFolder)}
              className="grid size-9 place-items-center rounded-full text-muted-foreground transition hover:bg-destructive/10 hover:text-destructive focus:outline-none focus:ring-4 focus:ring-destructive/20"
              aria-label={`Delete ${selectedFolder.name}`}
              title="Delete"
            >
              <Trash2 className="size-4" aria-hidden="true" />
            </button>
          </div>
        </nav>
      ) : null}

      {fileManagerView === "files" ? (
      <section className="rounded-[1.5rem] bg-white px-4 pt-2 shadow-soft sm:px-5">
        {folderStatus === "loading" ? (
          <div className="flex justify-end border-b border-border/70 py-2">
            <span className="text-xs font-semibold text-muted-foreground">Loading folders</span>
          </div>
        ) : null}
        {folderError ? <p className="border-b border-border/70 py-2 text-sm font-medium text-destructive">{folderError}</p> : null}
        {selectMode ? (
          <div className="flex flex-col gap-3 border-b border-border/70 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={toggleSelectAllVisible}
                className="inline-flex min-h-10 items-center justify-center rounded-full border border-border bg-white px-4 text-sm font-semibold text-foreground transition hover:border-primary/30 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
                disabled={!selectableVisibleKeys.length || bulkBusy}
              >
                {allVisibleSelected ? "Clear Visible" : "Select All Visible"}
              </button>
              <span className="text-sm font-semibold text-muted-foreground">
                {selectedItemCount} selected
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={openBulkMoveDialog}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-full border border-border bg-white px-4 text-sm font-semibold text-primary transition hover:border-primary/30 hover:bg-primary/5 focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:cursor-not-allowed disabled:text-muted-foreground"
                disabled={!selectedItemCount || bulkBusy}
              >
                <Folder className="size-4" aria-hidden="true" />
                Move
              </button>
              <button
                type="button"
                onClick={openBulkDeleteDialog}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-full border border-destructive/30 bg-white px-4 text-sm font-semibold text-destructive transition hover:bg-destructive/10 focus:outline-none focus:ring-4 focus:ring-destructive/20 disabled:cursor-not-allowed disabled:border-border disabled:text-muted-foreground"
                disabled={!selectedItemCount || bulkBusy}
              >
                <Trash2 className="size-4" aria-hidden="true" />
                Delete
              </button>
              <button
                type="button"
                onClick={clearSelection}
                className="inline-flex min-h-10 items-center justify-center rounded-full border border-border bg-white px-4 text-sm font-semibold text-foreground transition hover:border-primary/30 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
                disabled={bulkBusy}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : selectableVisibleKeys.length ? (
          <div className="flex justify-end border-b border-border/70 py-2">
            <button
              type="button"
              onClick={enterSelectMode}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-full border border-border bg-white px-4 text-sm font-semibold text-primary transition hover:border-primary/30 hover:bg-primary/5 focus:outline-none focus:ring-4 focus:ring-primary/10"
            >
              Select
            </button>
          </div>
        ) : null}
        {visibleFolders.length ? (
          <div>
            {visibleFolders.map((folder) => {
              const selected = selectedItemKeys.has(selectionKey("folder", folder.folder_id));
              return (
              <div key={folder.folder_id} className="group relative flex min-h-16 items-center gap-1 border-b border-border/70 py-2 last:border-b-0">
                {selectMode ? (
                  <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-xl px-1 py-1.5 transition hover:bg-primary/5">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleItemSelection("folder", folder.folder_id)}
                      className="size-5 shrink-0 rounded border-border text-primary focus:ring-4 focus:ring-primary/10"
                      aria-label={`Select folder ${folder.name}`}
                      disabled={bulkBusy}
                    />
                    <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary">
                      <Folder className="size-5" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[0.95rem] font-semibold leading-5 text-foreground">{folder.name}</span>
                      <span className="mt-1 block text-xs font-semibold text-muted-foreground">Folder</span>
                    </span>
                  </label>
                ) : (
                  <button
                    type="button"
                    onClick={() => openFolder(folder.folder_id)}
                    className="flex min-w-0 flex-1 items-center gap-3 rounded-xl px-1 py-1.5 text-left transition hover:bg-primary/5 focus:outline-none focus:ring-4 focus:ring-inset focus:ring-primary/10"
                  >
                    <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary">
                      <Folder className="size-5" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[0.95rem] font-semibold leading-5 text-foreground">{folder.name}</span>
                      <span className="mt-1 block text-xs font-semibold text-muted-foreground">Folder</span>
                    </span>
                  </button>
                )}
                {!selectMode ? (
                <div className="hidden shrink-0 items-center gap-0.5 opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100 lg:flex">
                  <button
                    type="button"
                    onClick={() => openRenameFolderDialog(folder)}
                    className="grid size-9 place-items-center rounded-full text-muted-foreground transition hover:bg-primary/10 hover:text-primary focus:opacity-100 focus:outline-none focus:ring-4 focus:ring-primary/10"
                    aria-label={`Rename ${folder.name}`}
                    title="Rename"
                  >
                    <Pencil className="size-4" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => openDeleteFolderDialog(folder)}
                    className="grid size-9 place-items-center rounded-full text-muted-foreground transition hover:bg-destructive/10 hover:text-destructive focus:opacity-100 focus:outline-none focus:ring-4 focus:ring-destructive/20"
                    aria-label={`Delete ${folder.name}`}
                    title="Delete"
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                  </button>
                </div>
                ) : null}
                {!selectMode ? (
                <div className="relative shrink-0 lg:hidden" data-folder-menu-root>
                  <button
                    type="button"
                    onClick={(event) => toggleFolderActionMenu(folder.folder_id, event.currentTarget)}
                    className="grid size-10 place-items-center rounded-full border border-border bg-white text-muted-foreground transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
                    aria-label={`Folder actions for ${folder.name}`}
                    aria-haspopup="menu"
                    aria-expanded={activeFolderMenuId === folder.folder_id}
                  >
                    <MoreHorizontal className="size-4" aria-hidden="true" />
                  </button>
                </div>
                ) : null}
                {!selectMode ? <ChevronRight className="hidden size-4 shrink-0 text-muted-foreground lg:block" aria-hidden="true" /> : null}
              </div>
              );
            })}
          </div>
        ) : null}
        {documentsStatus === "loading" && documents.length ? (
          <div className="flex justify-end border-b border-border/70 py-2">
            <span className="text-xs font-semibold text-muted-foreground">Loading</span>
          </div>
        ) : null}

        {preparingShareDocumentId ? <SharePreparationProgress progress={shareProgress} /> : null}

        {documentActionError && !renamingDocument && !deletingDocument ? (
          <p className="border-b border-border/70 py-2 text-sm font-medium text-destructive">{documentActionError}</p>
        ) : null}

        {documentsError ? (
          <p className="py-4 text-sm font-medium text-destructive">{documentsError}</p>
        ) : documentsStatus === "loading" && !documents.length ? (
          <div className="py-4">
            <BrandedLoader compact label="Loading files" />
          </div>
        ) : documents.length ? (
          <div>
            {documents.map((document) => {
              const uploadedDate = document.uploaded_at ? formatDate(document.uploaded_at.slice(0, 10)) : null;
              const selected = selectedItemKeys.has(selectionKey("file", document.document_id));
              const fileDetails = (
                <>
                  <DocumentFileIcon contentType={document.content_type} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[0.95rem] font-semibold leading-5 text-foreground">
                      {document.original_filename}
                    </p>
                    <p className="mt-1 text-xs font-semibold text-muted-foreground">
                      {documentContentTypeLabel(document.content_type)} · {formatFileSize(document.size_bytes)}
                    </p>
                    <p className="mt-0.5 text-xs font-medium text-muted-foreground">
                      {uploadedDate ? `Uploaded ${uploadedDate}` : "Uploaded date unavailable"}
                    </p>
                  </div>
                </>
              );
              return (
                <div key={document.document_id} className="flex min-h-16 items-center gap-3 border-b border-border/70 py-3.5 last:border-b-0">
                  {selectMode ? (
                    <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-xl py-1">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleItemSelection("file", document.document_id)}
                        className="size-5 shrink-0 rounded border-border text-primary focus:ring-4 focus:ring-primary/10"
                        aria-label={`Select ${document.original_filename}`}
                        disabled={bulkBusy}
                      />
                      {fileDetails}
                    </label>
                  ) : (
                    fileDetails
                  )}
                  {!selectMode ? (
                  <div className="relative shrink-0" data-document-menu-root>
                    <button
                      type="button"
                      onClick={(event) => {
                        toggleDocumentActionMenu(document.document_id, event.currentTarget);
                      }}
                      className="grid size-10 place-items-center rounded-full border border-border bg-white text-muted-foreground transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
                      aria-label={`File actions for ${document.original_filename}`}
                      aria-haspopup="menu"
                      aria-expanded={activeMenuDocumentId === document.document_id}
                    >
                      <MoreHorizontal className="size-4" aria-hidden="true" />
                    </button>
                  </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : visibleFolders.length ? null : (
          <p className="py-4 text-sm leading-6 text-muted-foreground">
            {selectedFolder ? "No files in this folder." : "No files uploaded yet."}
          </p>
        )}
      </section>
      ) : (
      <section className="rounded-[1.5rem] bg-white px-4 shadow-soft sm:px-5">
        <div className="flex flex-col gap-3 border-b border-border/70 py-3.5 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-foreground">Trash</h2>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">Files in Trash are permanently deleted after 30 days.</p>
            {trashStatus === "loading" ? <span className="mt-2 inline-flex text-xs font-semibold text-muted-foreground">Loading</span> : null}
          </div>
          {trashedDocuments.length ? (
            <button
              type="button"
              onClick={openEmptyTrashDialog}
              className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-full border border-destructive/30 bg-white px-4 text-sm font-semibold text-destructive transition hover:bg-destructive/10 focus:outline-none focus:ring-4 focus:ring-destructive/20 sm:self-start"
            >
              <Trash2 className="size-4" aria-hidden="true" />
              Empty Trash
            </button>
          ) : null}
        </div>

        {emptyTrashNotice ? <p className="pt-4 text-sm font-semibold text-primary">{emptyTrashNotice}</p> : null}
        {documentActionError && !emptyTrashDialogOpen ? <p className="pt-4 text-sm font-medium text-destructive">{documentActionError}</p> : null}
        {trashError ? (
          <p className="py-4 text-sm font-medium text-destructive">{trashError}</p>
        ) : trashStatus === "loading" && !trashedDocuments.length ? (
          <div className="py-4">
            <BrandedLoader compact label="Loading Trash" />
          </div>
        ) : trashedDocuments.length ? (
          <div>
            {trashedDocuments.map((document) => {
              const deletedDate = document.trashed_at ? formatDate(document.trashed_at.slice(0, 10)) : null;
              const sourcePath = logicalFolderPathLabel(logicalFolderPathSegments(document.folder_id, folders));
              return (
                <div key={document.document_id} className="flex min-h-16 items-center gap-3 border-b border-border/70 py-3.5 last:border-b-0">
                  <DocumentFileIcon contentType={document.content_type} muted />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[0.95rem] font-semibold leading-5 text-foreground">
                      {document.original_filename}
                    </p>
                    <p className="mt-1 text-xs font-semibold text-muted-foreground">
                      {documentContentTypeLabel(document.content_type)} · {formatFileSize(document.size_bytes)}
                    </p>
                    <p className="mt-0.5 text-xs font-medium text-muted-foreground">
                      {deletedDate ? `Deleted ${deletedDate}` : "Deleted date unavailable"}
                    </p>
                    <p
                      className="mt-1 inline-flex max-w-full items-center gap-1.5 rounded-full bg-muted/50 px-2 py-1 text-xs font-semibold text-muted-foreground"
                      aria-label={`Original location ${sourcePath}`}
                    >
                      <Folder className="size-3.5 shrink-0" aria-hidden="true" />
                      <span className="truncate">{sourcePath}</span>
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { void handleRestore(document.document_id); }}
                    className="inline-flex min-h-10 shrink-0 items-center justify-center rounded-full border border-border bg-white px-3 text-sm font-semibold text-primary transition hover:border-primary/30 hover:bg-primary/5 focus:outline-none focus:ring-4 focus:ring-primary/10"
                  >
                    Restore
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="py-4 text-sm leading-6 text-muted-foreground">Trash is empty.</p>
        )}
      </section>
      )}

      {uploadOpen ? (
        <div className="fixed inset-0 z-50 grid place-items-end bg-slate-950/35 px-0 py-0 backdrop-blur-sm sm:place-items-center sm:px-4 sm:py-8">
          <form
            onSubmit={(event) => { void handleSubmit(event); }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="upload-document-title"
            className="max-h-[92vh] w-full overflow-y-auto rounded-t-[1.5rem] bg-white p-5 shadow-[0_30px_100px_-45px_rgba(15,23,42,0.75)] sm:max-w-md sm:rounded-[1.5rem]"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 id="upload-document-title" className="text-lg font-semibold text-foreground">Upload File</h2>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">PDF, JPEG or PNG up to 25 MiB.</p>
              </div>
              <button
                type="button"
                onClick={closeUploadPanel}
                className="grid size-9 shrink-0 place-items-center rounded-full border border-border bg-white text-muted-foreground transition hover:border-primary/30 hover:text-foreground focus:outline-none focus:ring-4 focus:ring-primary/10"
                aria-label="Close Upload File"
                disabled={active}
              >
                <X className="size-4" aria-hidden="true" />
              </button>
            </div>

            <div className="mt-5">
              <p className="text-sm font-semibold text-foreground">Add File</p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <label
                  htmlFor="document-file"
                  className="flex min-h-16 cursor-pointer items-center gap-3 rounded-2xl border border-border bg-white px-4 py-3 transition hover:border-primary/35 hover:bg-primary/5 focus-within:ring-4 focus-within:ring-primary/10"
                >
                  <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary">
                    <FileText className="size-5" aria-hidden="true" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-foreground">Choose File</span>
                    <span className="mt-1 block text-xs font-medium text-muted-foreground">PDF, JPEG or PNG</span>
                  </span>
                  <input
                    key={inputKey}
                    id="document-file"
                    type="file"
                    accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png"
                    multiple
                    onChange={(event) => handleFileChange("file", event)}
                    className="sr-only"
                    disabled={active}
                  />
                </label>
                <label
                  htmlFor="document-camera-file"
                  className="flex min-h-16 cursor-pointer items-center gap-3 rounded-2xl border border-border bg-white px-4 py-3 transition hover:border-primary/35 hover:bg-primary/5 focus-within:ring-4 focus-within:ring-primary/10"
                >
                  <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary">
                    <Camera className="size-5" aria-hidden="true" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-foreground">Take Photo</span>
                    <span className="mt-1 block text-xs font-medium text-muted-foreground">JPEG or PNG</span>
                  </span>
                  <input
                    key={cameraInputKey}
                    id="document-camera-file"
                    type="file"
                    accept="image/jpeg,image/png"
                    capture="environment"
                    onChange={(event) => handleFileChange("camera", event)}
                    className="sr-only"
                    disabled={active}
                  />
                </label>
              </div>
              {selectedFiles.length ? (
                <div className="mt-3 min-w-0 rounded-2xl bg-primary/5 px-4 py-3">
                  <div className="grid gap-2">
                    {selectedFiles.map((file) => {
                      const itemError = validateDocumentFile(file);
                      return (
                        <div key={`${file.name}-${file.size}-${file.lastModified}`} className="min-w-0">
                          <p className="truncate text-sm font-semibold text-foreground">{file.name}</p>
                          <p className={cn("mt-1 text-xs font-medium", itemError ? "text-destructive" : "text-muted-foreground")}>
                            {itemError ?? formatFileSize(file.size)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                  {uploadSource === "camera" ? (
                    <div className="mt-3">
                      <label htmlFor="camera-filename" className="text-xs font-semibold text-foreground">
                        Filename optional
                      </label>
                      <input
                        id="camera-filename"
                        value={cameraFilename}
                        onChange={(event) => setCameraFilename(event.target.value)}
                        placeholder={`Uses ${selectedFile.name} if blank`}
                        className="mt-1 min-h-10 w-full rounded-xl border border-border bg-white px-3 py-2 text-sm font-medium text-foreground outline-none transition placeholder:text-muted-foreground/80 focus:border-primary focus:ring-4 focus:ring-primary/10"
                        disabled={active}
                      />
                      {cameraFilename.trim() && uploadFilename !== cameraFilename.trim() ? (
                        <p className="mt-1 text-xs font-medium text-muted-foreground">Will save as {uploadFilename}</p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="mt-4 border-t border-border/70 pt-4" aria-live="polite">
              <p className="text-sm font-semibold text-foreground">{documentUploadStatusLabels[status]}</p>
              {visibleError ? <p className="mt-1 text-sm font-medium text-destructive">{visibleError}</p> : null}
              {uploadItems.length ? (
                <div className="mt-3 grid gap-2">
                  {uploadItems.map((item) => (
                    <div key={item.id} className="flex min-w-0 items-start justify-between gap-3 rounded-2xl bg-muted/40 px-3 py-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-foreground">{item.filename}</p>
                        <p className="mt-0.5 text-xs font-medium text-muted-foreground">{formatFileSize(item.size)}</p>
                        {item.error ? <p className="mt-1 text-xs font-semibold text-destructive">{item.error}</p> : null}
                      </div>
                      <span
                        className={cn(
                          "shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold",
                          item.status === "complete"
                            ? "bg-emerald-50 text-emerald-700"
                            : item.status === "failed"
                              ? "bg-destructive/10 text-destructive"
                              : "bg-primary/10 text-primary",
                        )}
                      >
                        {documentUploadItemStatusLabels[item.status]}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
              {status === "uploading" && progress ? (
                <div className="mt-2">
                  {progress.percentage !== null ? (
                    <>
                      <div
                        className="h-2 overflow-hidden rounded-full bg-primary/10"
                        role="progressbar"
                        aria-label="Document upload progress"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={progress.percentage}
                      >
                        <div className="h-full rounded-full bg-primary transition-all motion-reduce:transition-none" style={{ width: `${progress.percentage}%` }} />
                      </div>
                      <p className="mt-1 text-xs font-semibold text-muted-foreground">
                        {progress.percentage}% · {formatFileSize(progress.loaded)} of {formatFileSize(progress.total ?? progress.loaded)}
                      </p>
                    </>
                  ) : (
                    <p className="text-xs font-semibold text-muted-foreground">
                      Uploading document · {formatFileSize(progress.loaded)}
                    </p>
                  )}
                </div>
              ) : null}
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeUploadPanel}
                className="inline-flex min-h-10 items-center justify-center rounded-full border border-border bg-white px-4 text-sm font-semibold text-foreground transition hover:border-primary/30 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
                disabled={active}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canUpload}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-soft transition hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none"
              >
                <Upload className="size-4" aria-hidden="true" />
                Upload
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {activeMenuDocumentId && documentActionMenuPlacement ? (
        <div
          role="menu"
          data-document-menu-root
          className="fixed z-50 w-44 overflow-hidden rounded-2xl border border-border bg-white py-1 shadow-[0_20px_55px_-32px_rgba(15,23,42,0.45)]"
          style={{ left: documentActionMenuPlacement.left, top: documentActionMenuPlacement.top }}
        >
          {documents
            .filter((document) => document.document_id === activeMenuDocumentId)
            .map((document) => (
              <Fragment key={document.document_id}>
                <Link
                  to={`/app/documents/${encodeURIComponent(document.document_id)}/view`}
                  role="menuitem"
                  onClick={() => {
                    setActiveMenuDocumentId(null);
                    setDocumentActionMenuPlacement(null);
                  }}
                  className="flex min-h-10 w-full items-center gap-2 px-3 text-left text-sm font-semibold text-foreground transition hover:bg-primary/5 focus:bg-primary/5 focus:outline-none"
                >
                  <Eye className="size-4 text-primary" aria-hidden="true" />
                  View
                </Link>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setActiveMenuDocumentId(null);
                    setDocumentActionMenuPlacement(null);
                    void downloadDocument(document.document_id, document.original_filename);
                  }}
                  className="flex min-h-10 w-full items-center gap-2 px-3 text-left text-sm font-semibold text-foreground transition hover:bg-primary/5 focus:bg-primary/5 focus:outline-none"
                >
                  <Download className="size-4 text-primary" aria-hidden="true" />
                  Download
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setActiveMenuDocumentId(null);
                    setDocumentActionMenuPlacement(null);
                    void handleShareClick(document);
                  }}
                  className="flex min-h-10 w-full items-center gap-2 px-3 text-left text-sm font-semibold text-foreground transition hover:bg-primary/5 focus:bg-primary/5 focus:outline-none"
                >
                  <Share2 className="size-4 text-primary" aria-hidden="true" />
                  Share
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => openRenameDialog(document)}
                  className="flex min-h-10 w-full items-center gap-2 px-3 text-left text-sm font-semibold text-foreground transition hover:bg-primary/5 focus:bg-primary/5 focus:outline-none"
                >
                  <Pencil className="size-4 text-primary" aria-hidden="true" />
                  Rename
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => openMoveDialog(document)}
                  className="flex min-h-10 w-full items-center gap-2 px-3 text-left text-sm font-semibold text-foreground transition hover:bg-primary/5 focus:bg-primary/5 focus:outline-none"
                >
                  <Folder className="size-4 text-primary" aria-hidden="true" />
                  Move
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => openDeleteDialog(document)}
                  className="flex min-h-10 w-full items-center gap-2 px-3 text-left text-sm font-semibold text-destructive transition hover:bg-destructive/10 focus:bg-destructive/10 focus:outline-none"
                >
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              </Fragment>
            ))}
        </div>
      ) : null}

      {activeFolderMenuId && folderActionMenuPlacement ? (
        <div
          role="menu"
          data-folder-menu-root
          className="fixed z-50 w-40 overflow-hidden rounded-2xl border border-border bg-white py-1 shadow-[0_20px_55px_-32px_rgba(15,23,42,0.45)]"
          style={{ left: folderActionMenuPlacement.left, top: folderActionMenuPlacement.top }}
        >
          {folders
            .filter((folder) => folder.folder_id === activeFolderMenuId)
            .map((folder) => (
              <Fragment key={folder.folder_id}>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setActiveFolderMenuId(null);
                    setFolderActionMenuPlacement(null);
                    openRenameFolderDialog(folder);
                  }}
                  className="flex min-h-10 w-full items-center gap-2 px-3 text-left text-sm font-semibold text-foreground transition hover:bg-primary/5 focus:bg-primary/5 focus:outline-none"
                >
                  <Pencil className="size-4 text-primary" aria-hidden="true" />
                  Rename
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setActiveFolderMenuId(null);
                    setFolderActionMenuPlacement(null);
                    openDeleteFolderDialog(folder);
                  }}
                  className="flex min-h-10 w-full items-center gap-2 px-3 text-left text-sm font-semibold text-destructive transition hover:bg-destructive/10 focus:bg-destructive/10 focus:outline-none"
                >
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              </Fragment>
            ))}
        </div>
      ) : null}

      {renamingDocument ? (
        <RenameDocumentDialog
          document={renamingDocument}
          error={documentActionError}
          onClose={() => {
            setRenamingDocument(null);
            setDocumentActionError(null);
          }}
          onSubmit={(event) => { void handleRenameSubmit(event); }}
          onTitleChange={setRenameTitle}
          status={documentActionStatus}
          title={renameTitle}
        />
      ) : null}

      {deletingDocument ? (
        <DeleteDocumentDialog
          document={deletingDocument}
          error={documentActionError}
          onClose={() => {
            setDeletingDocument(null);
            setDocumentActionError(null);
          }}
          onConfirm={() => { void handleDeleteConfirm(); }}
          status={documentActionStatus}
        />
      ) : null}

      {bulkDeleteDialogOpen ? (
        <BulkDeleteDocumentsDialog
          count={selectedItemCount}
          error={documentActionError}
          onClose={() => {
            setBulkDeleteDialogOpen(false);
            setDocumentActionError(null);
          }}
          onConfirm={() => { void handleBulkDeleteConfirm(); }}
          status={documentActionStatus}
        />
      ) : null}

      {folderNameDialog ? (
        <FolderNameDialog
          error={folderActionError}
          folder={folderNameDialog.mode === "rename" ? folderNameDialog.folder : undefined}
          folders={folders}
          name={folderName}
          onClose={() => {
            setFolderNameDialog(null);
            setFolderActionError(null);
          }}
          onNameChange={setFolderName}
          onParentFolderChange={setFolderParentFolderId}
          onSubmit={(event) => { void handleFolderNameSubmit(event); }}
          parentFolderId={folderParentFolderId}
          status={folderActionStatus}
        />
      ) : null}

      {deletingFolder ? (
        <DeleteFolderDialog
          error={folderActionError}
          folder={deletingFolder}
          onClose={() => {
            setDeletingFolder(null);
            setFolderActionError(null);
          }}
          onConfirm={() => { void handleDeleteFolderConfirm(); }}
          status={folderActionStatus}
        />
      ) : null}

      {movingDocument ? (
        <MoveDocumentDialog
          document={movingDocument}
          error={documentActionError}
          folders={folders}
          onClose={() => {
            setMovingDocument(null);
            setDocumentActionError(null);
          }}
          onSubmit={(event) => { void handleMoveSubmit(event); }}
          onTargetChange={setMoveTargetFolderId}
          status={documentActionStatus}
          targetFolderId={moveTargetFolderId}
        />
      ) : null}

      {bulkMoveDialogOpen ? (
        <BulkMoveDocumentsDialog
          count={selectedItemCount}
          error={documentActionError}
          folders={folders}
          onClose={() => {
            setBulkMoveDialogOpen(false);
            setDocumentActionError(null);
          }}
          onSubmit={(event) => { void handleBulkMoveSubmit(event); }}
          onTargetChange={setBulkMoveTargetFolderId}
          status={documentActionStatus}
          targetFolderId={bulkMoveTargetFolderId}
        />
      ) : null}

      {emptyTrashDialogOpen ? (
        <EmptyTrashDialog
          error={documentActionError}
          onClose={() => {
            setEmptyTrashDialogOpen(false);
            setDocumentActionError(null);
          }}
          onConfirm={() => { void handleEmptyTrashConfirm(); }}
          status={documentActionStatus}
        />
      ) : null}

      {readyToShare ? (
        <ReadyToShareDialog
          filename={readyToShare.filename}
          onClose={() => setReadyToShare(null)}
          onShare={() => { void handleConfirmShare(); }}
        />
      ) : null}
    </div>
  );
}

export function DocumentViewerPage() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { commitShare, deleteDocument, downloadDocument, error, findDocumentById, getDocumentUrl, prepareShare, renameDocument, shareProgress } =
    useDocuments();
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerStatus, setViewerStatus] = useState<"error" | "loading" | "ready">("loading");
  const [viewerError, setViewerError] = useState<string | null>(null);
  const [renamingDocument, setRenamingDocument] = useState<DocumentFile | null>(null);
  const [deletingDocument, setDeletingDocument] = useState<DocumentFile | null>(null);
  const [renameTitle, setRenameTitle] = useState("");
  const [documentActionStatus, setDocumentActionStatus] = useState<DocumentActionStatus>("idle");
  const [documentActionError, setDocumentActionError] = useState<string | null>(null);
  const [preparingShare, setPreparingShare] = useState(false);
  const [readyToShare, setReadyToShare] = useState<ReadyToShare | null>(null);
  const [shareMessage, setShareMessage] = useState<string | null>(null);
  const [document, setDocument] = useState<DocumentFile | null>(null);
  const [documentLookupStatus, setDocumentLookupStatus] = useState<"loading" | "ready">("loading");
  const filename = document?.original_filename ?? "File";
  const isImage = document?.content_type.startsWith("image/") ?? false;
  const isPdf = document?.content_type === "application/pdf" || !document;

  const openRenameDialog = (target: DocumentFile) => {
    setDocumentActionError(null);
    setRenamingDocument(target);
    setRenameTitle(target.original_filename);
  };

  const openDeleteDialog = (target: DocumentFile) => {
    setDocumentActionError(null);
    setDeletingDocument(target);
  };

  const handleRenameSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!renamingDocument) {
      return;
    }

    const nextTitle = renameTitle.trim();
    if (!nextTitle) {
      setDocumentActionError("Enter a filename.");
      return;
    }

    setDocumentActionStatus("renaming");
    setDocumentActionError(null);
    const renamed = await renameDocument(renamingDocument.document_id, nextTitle);
    setDocumentActionStatus("idle");
    if (renamed === true) {
      setDocument(await findDocumentById(renamingDocument.document_id));
      setRenamingDocument(null);
      setRenameTitle("");
      return;
    }
    setDocumentActionError(typeof renamed === "string" ? renamed : "MedoPilot could not rename this file.");
  };

  const handleDeleteConfirm = async () => {
    if (!deletingDocument) {
      return;
    }

    setDocumentActionStatus("deleting");
    setDocumentActionError(null);
    const deleted = await deleteDocument(deletingDocument.document_id);
    setDocumentActionStatus("idle");
    if (deleted === true) {
      setDeletingDocument(null);
      void navigate("/app/documents");
      return;
    }
    setDocumentActionError(typeof deleted === "string" ? deleted : "MedoPilot could not remove this file.");
  };

  const handleShareClick = async () => {
    if (!documentId || preparingShare) {
      return;
    }

    setShareMessage(null);
    setPreparingShare(true);
    const result = await prepareShare(documentId, filename, document?.content_type ?? "");
    setPreparingShare(false);
    if (result.outcome === "ready") {
      setReadyToShare({ documentId, file: result.file, filename });
      return;
    }
    if (result.message) {
      setShareMessage(result.message);
    }
  };

  const handleConfirmShare = async () => {
    if (!readyToShare) {
      return;
    }

    const result = await commitShare(readyToShare.file);
    if (result.outcome === "cancelled") {
      return;
    }
    setReadyToShare(null);
    if (result.outcome === "blocked") {
      setShareMessage(result.message);
    }
  };

  useEffect(() => {
    let canceled = false;
    setDocument(null);
    setDocumentLookupStatus("loading");

    if (!documentId) {
      setDocumentLookupStatus("ready");
      return undefined;
    }

    void findDocumentById(documentId).then((found) => {
      if (canceled) {
        return;
      }
      setDocument(found);
      setDocumentLookupStatus("ready");
    });

    return () => {
      canceled = true;
    };
  }, [documentId, findDocumentById]);

  useEffect(() => {
    let canceled = false;

    async function loadViewerUrl() {
      if (!documentId) {
        setViewerStatus("error");
        setViewerError("MedoPilot could not find this file.");
        return;
      }
      if (!document && documentLookupStatus === "loading") {
        return;
      }
      if (!document && documentLookupStatus === "ready") {
        setViewerStatus("error");
        setViewerError("MedoPilot could not find this file.");
        return;
      }

      setViewerStatus("loading");
      setViewerError(null);
      setViewerUrl(null);
      const result = await getDocumentUrl(documentId, "inline", isImage ? "preview" : "original");
      if (canceled) {
        return;
      }

      if (!result) {
        setViewerStatus("error");
        setViewerError("MedoPilot could not open this file.");
        return;
      }

      setViewerUrl(result.download_url);
      setViewerStatus("ready");
    }

    void loadViewerUrl();

    return () => {
      canceled = true;
    };
  }, [document, documentId, documentLookupStatus, getDocumentUrl, isImage]);

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <BackAction to="/app/documents" label="My Files" compact />
          <h1 className="mt-3 truncate text-2xl font-semibold leading-tight text-foreground sm:text-[1.7rem]">{filename}</h1>
          {document ? (
            <p className="mt-1 text-sm font-medium text-muted-foreground">
              {documentContentTypeLabel(document.content_type)} · {formatFileSize(document.size_bytes)}
            </p>
          ) : documentLookupStatus === "loading" ? (
            <p className="mt-1 text-sm font-medium text-muted-foreground">Loading file details</p>
          ) : null}
        </div>
        {documentId ? (
          <div className="flex shrink-0 items-center gap-2" role="toolbar" aria-label={`File actions for ${filename}`}>
            <button
              type="button"
              onClick={() => { void downloadDocument(documentId, filename); }}
              className="grid size-10 place-items-center rounded-full border border-border bg-white text-muted-foreground transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
              aria-label={`Download ${filename}`}
              title="Download"
            >
              <Download className="size-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => { void handleShareClick(); }}
              className="grid size-10 place-items-center rounded-full border border-border bg-white text-muted-foreground transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:cursor-not-allowed disabled:opacity-60"
              aria-label={`Share ${filename}`}
              title="Share"
              disabled={preparingShare}
            >
              <Share2 className="size-4" aria-hidden="true" />
            </button>
            {document ? (
              <button
                type="button"
                onClick={() => openRenameDialog(document)}
                className="grid size-10 place-items-center rounded-full border border-border bg-white text-muted-foreground transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
                aria-label={`Rename ${filename}`}
                title="Rename"
              >
                <Pencil className="size-4" aria-hidden="true" />
              </button>
            ) : null}
            {document ? (
              <button
                type="button"
                onClick={() => openDeleteDialog(document)}
                className="grid size-10 place-items-center rounded-full border border-border bg-white text-destructive transition hover:border-destructive/30 hover:bg-destructive/10 focus:outline-none focus:ring-4 focus:ring-destructive/20"
                aria-label={`Delete ${filename}`}
                title="Delete"
              >
                <Trash2 className="size-4" aria-hidden="true" />
              </button>
            ) : null}
          </div>
        ) : null}
      </header>

      {preparingShare ? <SharePreparationProgress progress={shareProgress} /> : null}

      {viewerStatus === "ready" && shareMessage ? <p className="text-sm font-medium text-destructive">{shareMessage}</p> : null}

      {viewerStatus === "ready" && !shareMessage && error ? <p className="text-sm font-medium text-destructive">{error}</p> : null}

      {viewerStatus === "loading" ? (
        <section className="rounded-[1.5rem] bg-white px-4 py-8 shadow-soft">
          <BrandedLoader compact label="Opening file" />
        </section>
      ) : viewerStatus === "error" || !viewerUrl ? (
        <section className="rounded-[1.5rem] bg-white px-4 py-5 shadow-soft">
          <p className="text-sm font-semibold text-destructive">{viewerError ?? error ?? "MedoPilot could not open this file."}</p>
          <button
            type="button"
            onClick={() => { void navigate("/app/documents"); }}
            className="mt-4 inline-flex min-h-10 items-center justify-center rounded-full border border-border bg-white px-4 text-sm font-semibold text-foreground transition hover:border-primary/30 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
          >
            Back to My Files
          </button>
        </section>
      ) : (
        <section className="overflow-hidden rounded-[1.5rem] bg-white shadow-soft">
          {isImage ? (
            <div className="flex max-h-[72vh] min-h-[18rem] items-center justify-center overflow-auto bg-slate-950/5 p-3 sm:p-5">
              <img src={viewerUrl} alt={filename} className="max-h-full max-w-full object-contain" loading="eager" decoding="async" />
            </div>
          ) : isPdf ? (
            <PdfDocumentViewer filename={filename} url={viewerUrl} />
          ) : (
            <div className="px-4 py-5">
              <p className="text-sm font-medium text-muted-foreground">Preview is not available for this file type. You can download the file instead.</p>
            </div>
          )}
        </section>
      )}

      {renamingDocument ? (
        <RenameDocumentDialog
          document={renamingDocument}
          error={documentActionError}
          onClose={() => {
            setRenamingDocument(null);
            setDocumentActionError(null);
          }}
          onSubmit={(event) => { void handleRenameSubmit(event); }}
          onTitleChange={setRenameTitle}
          status={documentActionStatus}
          title={renameTitle}
        />
      ) : null}

      {deletingDocument ? (
        <DeleteDocumentDialog
          document={deletingDocument}
          error={documentActionError}
          onClose={() => {
            setDeletingDocument(null);
            setDocumentActionError(null);
          }}
          onConfirm={() => { void handleDeleteConfirm(); }}
          status={documentActionStatus}
        />
      ) : null}

      {readyToShare ? (
        <ReadyToShareDialog
          filename={readyToShare.filename}
          onClose={() => setReadyToShare(null)}
          onShare={() => { void handleConfirmShare(); }}
        />
      ) : null}
    </div>
  );
}

function ProfileSuccessMessage() {
  const location = useLocation();
  const state = location.state as { saved?: boolean } | null;

  if (!state?.saved) {
    return null;
  }

  return <p className="rounded-full bg-primary/10 px-4 py-2 text-sm font-semibold text-primary">Saved</p>;
}

function SectionHeader({ body, title }: { body?: string; title: string }) {
  return (
    <div className="min-w-0">
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      {body ? <p className="mt-2 text-sm leading-6 text-muted-foreground">{body}</p> : null}
    </div>
  );
}

function EmptySection({ action, children }: { action?: ReactNode; children: ReactNode }) {
  return (
    <div className="mt-3 rounded-2xl border border-dashed border-primary/20 bg-surface px-4 py-3 text-sm leading-6 text-muted-foreground">
      <p>{children}</p>
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}

function EmergencyContactSummary({ contact }: { contact: EmergencyContact }) {
  return (
    <div className="border-b border-border/70 py-4 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">Priority {contact.priority}</span>
        <h4 className="font-semibold text-foreground">{contact.name}</h4>
        <span className="text-sm text-muted-foreground">{contact.relationship}</span>
      </div>
      <div className="mt-3 grid gap-x-8 sm:grid-cols-2">
        <ProfileDetail label="Primary mobile" value={contact.primary_mobile_number} />
        <ProfileDetail label="Alternate mobile" value={contact.alternate_mobile_number} />
        <ProfileDetail label="WhatsApp number" value={contact.whatsapp_number} />
        <ProfileDetail label="Address" value={formatAddress(contact.address)} />
      </div>
    </div>
  );
}

function PrimaryDoctorSummary({ doctor }: { doctor: PrimaryDoctor }) {
  return (
    <div className="mt-3 grid gap-x-8 sm:grid-cols-2 xl:grid-cols-3">
      <ProfileDetail label="Name" value={doctor.name} />
      <ProfileDetail label="Speciality" value={doctor.speciality} />
      <ProfileDetail label="Hospital / clinic" value={doctor.hospital_clinic_name} />
      <ProfileDetail label="Mobile number" value={doctor.mobile_number} />
      <ProfileDetail label="Emergency contact" value={doctor.emergency_contact_number} />
      <ProfileDetail label="Email" value={doctor.email} />
      <ProfileDetail label="Preferred for emergency" value={doctor.preferred_for_emergency ? "Yes" : "No"} />
      <ProfileDetail label="Address" value={formatAddress(doctor.address)} />
    </div>
  );
}

function HospitalSummary({ hospital, label }: { hospital: PreferredHospital; label: string }) {
  return (
    <div className="border-b border-border/70 py-4 last:border-b-0">
      <h4 className="font-semibold text-foreground">{label}</h4>
      <div className="mt-3 grid gap-x-8 sm:grid-cols-2 xl:grid-cols-3">
        <ProfileDetail label="Name" value={hospital.name} />
        <ProfileDetail label="Emergency number" value={hospital.emergency_number} />
        <ProfileDetail label="Ambulance contact" value={hospital.ambulance_contact_number} />
        <ProfileDetail label="Address" value={formatAddress(hospital.address)} />
      </div>
    </div>
  );
}

function HealthInsuranceSummary({ insurance }: { insurance: HealthInsurance }) {
  return (
    <div className="mt-3 grid gap-x-8 sm:grid-cols-2 xl:grid-cols-3">
      <ProfileDetail label="Insurance Company" value={insurance.insurance_company} />
      <ProfileDetail label="Policy Name" value={insurance.policy_name} />
      <ProfileDetail label="Policy Number" value={insurance.policy_number} />
      <ProfileDetail label="Contact Email" value={insurance.insurance_company_contact_email} />
      <ProfileDetail label="Contact Phone" value={insurance.insurance_company_contact_phone} />
    </div>
  );
}

export function ProfilePage() {
  const auth = useAuth();
  const account = useMemo(() => getAccountSummary(auth.user), [auth.user]);
  const { error, profile, status } = useAccountProfile();
  const profileCountry = findPhoneCountry(profile?.contact_phone_country_code) ?? null;
  const contactPhone = profile?.contact_phone_e164
    ? `${profileCountry ? `${profileCountry.flag} ` : ""}${profile.contact_phone_e164}`
    : null;
  const profileDisplayName = [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") || account.displayName;
  const emergencyContactsCount = profile?.emergency_contacts.length ?? 0;
  const hasInsurance = Boolean(profile?.health_insurance && hasHealthInsurance(profile.health_insurance));

  return (
    <div className="space-y-4 lg:space-y-5">
      <PageHero
        title={profileDisplayName}
        avatar={
          <span className="grid size-12 shrink-0 place-items-center rounded-full bg-white/14 text-base font-semibold text-white shadow-soft">
            {account.initials}
          </span>
        }
        action={
          <div className="flex flex-wrap items-center gap-2">
            <ProfileSuccessMessage />
            <Link to="/app/profile/edit" className={heroActionClassName}>
              Edit Profile
            </Link>
          </div>
        }
      />

      <section className="min-w-0 rounded-2xl border border-primary/10 bg-primary/5 px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">MedoPilot ID</p>
        <p className="mt-1 font-semibold text-foreground">{profile?.medopilot_user_id ?? "Preparing your ID"}</p>
      </section>

      {status === "loading" ? (
        <BrandedLoader compact label="Loading your profile" />
      ) : (
        <div className="grid gap-4">
          <section className="min-w-0 rounded-[1.5rem] bg-white px-4 py-4 shadow-soft">
            <SectionHeader title="Personal Information" />
            <div className="mt-2 grid gap-x-8 sm:grid-cols-2 xl:grid-cols-3">
              <ProfileDetail label="Name" value={[profile?.first_name, profile?.last_name].filter(Boolean).join(" ") || null} />
              <ProfileDetail label="Date of Birth" value={formatDate(profile?.date_of_birth ?? null)} />
              <ProfileDetail label="Blood Group" value={profile?.blood_group ?? null} />
              {/* Only ever reads the persisted value -- never the browser-detected timezone, which
                  is strictly an Edit Profile suggestion until explicitly saved. */}
              <ProfileDetail label="Default Timezone" value={profile?.default_timezone ? formatTimezoneLabel(profile.default_timezone) : null} />
            </div>
          </section>

          <section className="min-w-0 rounded-[1.5rem] bg-white px-4 py-4 shadow-soft">
            <SectionHeader title="Care Details" />
            <div className="mt-2 grid gap-4 xl:grid-cols-2">
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-foreground">Primary / Family Doctor</h4>
                {profile?.primary_doctor ? <PrimaryDoctorSummary doctor={profile.primary_doctor} /> : <EmptySection>No primary or family doctor has been added yet.</EmptySection>}
              </div>
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-foreground">Preferred Hospitals</h4>
                {profile?.preferred_hospitals?.primary || profile?.preferred_hospitals?.alternate ? (
                  <div className="mt-1">
                    {profile.preferred_hospitals.primary ? <HospitalSummary label="Primary Hospital" hospital={profile.preferred_hospitals.primary} /> : null}
                    {profile.preferred_hospitals.alternate ? <HospitalSummary label="Alternate Hospital" hospital={profile.preferred_hospitals.alternate} /> : null}
                  </div>
                ) : (
                  <EmptySection>No preferred hospitals have been added yet.</EmptySection>
                )}
              </div>
            </div>
          </section>

          <section className="min-w-0 rounded-[1.5rem] bg-white px-4 py-4 shadow-soft" id="emergency-contacts">
            <div className="flex items-center justify-between gap-3">
              <SectionHeader title="Emergency Contacts" />
              <span className="shrink-0 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-primary">
                {pluralize(emergencyContactsCount, "Contact")}
              </span>
            </div>
            {profile?.emergency_contacts.length ? (
              <div className="mt-1">
                {profile.emergency_contacts.map((contact) => (
                  <EmergencyContactSummary key={`${contact.priority}-${contact.name}`} contact={contact} />
                ))}
              </div>
            ) : (
              <EmptySection>No emergency contacts have been added yet.</EmptySection>
            )}
          </section>

          <section className="min-w-0 rounded-[1.5rem] bg-white px-4 py-4 shadow-soft">
            <SectionHeader title="Contact / Address" />
            <div className="mt-2 grid gap-x-8 sm:grid-cols-2 xl:grid-cols-3">
              <ProfileDetail label="Email" value={account.email} />
              <ProfileDetail label="Contact Phone" value={contactPhone} />
              <ProfileDetail label="Country" value={profileCountry ? `${profileCountry.flag} ${profileCountry.name}` : null} />
            </div>
            <ProfileDetail label="Residential Address" value={formatAddress(profile?.residential_address)} />
          </section>

          <section className="min-w-0 rounded-[1.5rem] bg-white px-4 py-4 shadow-soft" id="health-insurance">
            <div className="flex items-center justify-between gap-3">
              <SectionHeader title="Health Insurance" />
              <Link
                to="/app/profile/insurance"
                className="inline-flex min-h-9 shrink-0 items-center gap-1.5 rounded-full border border-border bg-white px-3 py-2 text-sm font-semibold text-primary transition hover:border-primary/30 focus:outline-none focus:ring-4 focus:ring-primary/10"
              >
                Manage
                <ArrowRight className="size-3.5" aria-hidden="true" />
              </Link>
            </div>
            <p className={cn("mt-2 inline-flex rounded-full px-3 py-1 text-xs font-semibold", hasInsurance ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-primary")}>
              {hasInsurance ? "Insurance Added" : "Insurance Not Added"}
            </p>
            {profile?.health_insurance && hasHealthInsurance(profile.health_insurance) ? (
              <HealthInsuranceSummary insurance={profile.health_insurance} />
            ) : (
              <EmptySection>No health insurance information has been added yet.</EmptySection>
            )}
          </section>

          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/app/profile/export"
              className="inline-flex min-h-10 items-center gap-2 rounded-full border border-border bg-white px-4 py-2.5 text-sm font-semibold text-primary shadow-soft transition hover:border-primary/30 focus:outline-none focus:ring-4 focus:ring-primary/10"
            >
              <Download className="size-4" aria-hidden="true" />
              Download Health Profile
            </Link>
          </div>

          <MoreDetails title="More Personal Details">
            <ProfileDetail label="Birth Time" value={formatTime(profile?.birth_time ?? null)} />
            <ProfileDetail label="Birth City" value={profile?.birth_city ?? null} />
            <ProfileDetail label="Birth Country" value={countryNameFromCode(profile?.birth_country)} />
            <ProfileDetail label="Birth Facility" value={profile?.birth_facility_name ?? null} />
            <ProfileDetail
              label="Sex Assigned At Birth"
              value={labelFromOptions(profile?.sex_assigned_at_birth, sexAssignedAtBirthOptions)}
            />
            <ProfileDetail label="Gender Identity" value={profile?.gender_identity ?? null} />
            <ProfileDetail label="Preferred Language" value={profile?.preferred_language ?? null} />
          </MoreDetails>

          {error ? (
            <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">
              {error}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

export function HealthInsurancePage() {
  const navigate = useNavigate();
  const { error, profile, saveProfile, status } = useAccountProfile();
  const [draft, setDraft] = useState<HealthInsuranceDraft>(() => healthInsuranceDraftFromProfile(null));
  const isBusy = status === "loading" || status === "saving";

  useEffect(() => {
    setDraft(healthInsuranceDraftFromProfile(profile?.health_insurance));
  }, [profile]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "saving") {
      return;
    }

    const nextProfile = await saveProfile({
      first_name: profile?.first_name ?? null,
      last_name: profile?.last_name ?? null,
      contact_phone_e164: profile?.contact_phone_e164 ?? null,
      contact_phone_country_code: profile?.contact_phone_country_code ?? null,
      health_insurance: healthInsurancePayloadFromDraft(draft),
    });

    if (nextProfile) {
      void navigate("/app/profile", { replace: true, state: { saved: true } });
    }
  };

  return (
    <div className="space-y-6">
      <BackAction to="/app/profile" label="Back to Profile" />
      <PageHero
        icon={ShieldCheck}
        title="Health Insurance"
        description="Keep policy and insurer contact details close at hand."
      />

      <section className="max-w-3xl">
        <article className="min-w-0 rounded-[1.75rem] border border-primary/10 bg-white p-5 shadow-premium sm:p-6">
          {status === "loading" ? (
            <BrandedLoader compact label="Loading insurance details" />
          ) : (
            <form className="grid gap-5" onSubmit={(event) => void handleSubmit(event)}>
              <div className="flex items-center gap-3">
                <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-blue-50 text-primary">
                  <ShieldCheck className="size-5" aria-hidden="true" />
                </span>
                <h2 className="text-base font-semibold">Policy Details</h2>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                  label="Insurance Company"
                  value={draft.insurance_company}
                  onChange={(value) => setDraft((current) => ({ ...current, insurance_company: value }))}
                  maxLength={120}
                  disabled={isBusy}
                  placeholder="Company name"
                />
                <TextField
                  label="Policy Name"
                  value={draft.policy_name}
                  onChange={(value) => setDraft((current) => ({ ...current, policy_name: value }))}
                  maxLength={80}
                  disabled={isBusy}
                  placeholder="Plan name"
                />
                <TextField
                  label="Policy Number"
                  value={draft.policy_number}
                  onChange={(value) => setDraft((current) => ({ ...current, policy_number: value }))}
                  maxLength={80}
                  disabled={isBusy}
                  placeholder="Policy or member number"
                />
                <TextField
                  label="Contact Phone"
                  value={draft.insurance_company_contact_phone}
                  onChange={(value) => setDraft((current) => ({ ...current, insurance_company_contact_phone: value }))}
                  maxLength={40}
                  inputMode="tel"
                  type="tel"
                  disabled={isBusy}
                  placeholder="Insurer phone number"
                />
                <TextField
                  label="Contact Email"
                  value={draft.insurance_company_contact_email}
                  onChange={(value) => setDraft((current) => ({ ...current, insurance_company_contact_email: value }))}
                  maxLength={254}
                  inputMode="email"
                  type="email"
                  disabled={isBusy}
                  placeholder="Insurer email address"
                />
              </div>

              {error ? (
                <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">
                  {error}
                </p>
              ) : null}

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <button
                  type="submit"
                  disabled={isBusy}
                  className="inline-flex min-h-12 items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "saving" ? "Saving" : "Save Health Insurance"}
                </button>
                <button
                  type="button"
                  onClick={() => void navigate("/app/profile")}
                  disabled={status === "saving"}
                  className="inline-flex min-h-12 items-center justify-center rounded-full border border-border bg-white px-6 py-3 text-sm font-semibold text-muted-foreground transition hover:border-primary/30 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </article>
      </section>
    </div>
  );
}

export function HealthProfileExportPage() {
  const { error, exportHealthProfile, status } = useAccountProfile();
  const [selectedSections, setSelectedSections] = useState<HealthProfileExportSection[]>(
    () => healthProfileExportOptions.map((option) => option.id),
  );
  const [localError, setLocalError] = useState<string | null>(null);
  const isBusy = status === "saving";

  const toggleSection = (section: HealthProfileExportSection) => {
    setSelectedSections((current) =>
      current.includes(section) ? current.filter((item) => item !== section) : [...current, section],
    );
  };

  const handleDownload = async () => {
    if (!selectedSections.length || isBusy) {
      setLocalError("Select at least one section before generating the report.");
      return;
    }

    setLocalError(null);
    const result = await exportHealthProfile(selectedSections);
    if (result) {
      downloadBlob(result.blob, result.filename);
    }
  };

  const allSectionsSelected = selectedSections.length === healthProfileExportOptions.length;

  return (
    <div className="min-w-0 max-w-full space-y-6 overflow-hidden">
      <BackAction to="/app/profile" label="Back to Profile" />
      <PageHero
        icon={Download}
        title="Download Health Profile"
        description="Choose what to include in your professional Health Profile report."
      />

      <section className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1fr)_21rem]">
        <article className="min-w-0 rounded-[1.75rem] border border-primary/10 bg-white p-5 shadow-premium sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-indigo-50 text-indigo-700">
                <Download className="size-5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
              <h2 className="text-base font-semibold">Report Sections</h2>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">Only selected sections will be included.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSelectedSections(healthProfileExportOptions.map((option) => option.id))}
              disabled={allSectionsSelected || isBusy}
              className="inline-flex min-h-10 w-fit items-center rounded-full border border-primary/10 bg-white px-4 py-2 text-sm font-semibold text-primary shadow-soft transition hover:border-primary/30 focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Select All
            </button>
          </div>

          <div className="mt-5 grid gap-2 sm:grid-cols-2">
            {healthProfileExportOptions.map((option) => (
              <label
                key={option.id}
                className={cn(
                  "flex min-h-14 min-w-0 cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 text-left shadow-soft transition hover:-translate-y-0.5 focus-within:ring-4 focus-within:ring-primary/10 motion-reduce:transition-none",
                  selectedSections.includes(option.id)
                    ? "border-primary/25 bg-blue-50/70"
                    : "border-primary/10 bg-white hover:border-primary/20",
                )}
              >
                <input
                  type="checkbox"
                  checked={selectedSections.includes(option.id)}
                  onChange={() => toggleSection(option.id)}
                  className="size-5 shrink-0 accent-primary"
                  disabled={isBusy}
                />
                <span className="min-w-0 text-sm font-semibold text-foreground">{option.label}</span>
              </label>
            ))}
          </div>
        </article>

        <aside className="min-w-0 self-start rounded-[1.75rem] border border-primary/10 bg-white p-5 shadow-premium sm:p-6 xl:sticky xl:top-24">
          <div className="flex items-start gap-3">
            <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-emerald-50 text-emerald-700">
              <Download className="size-5" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <h2 className="text-base font-semibold">Ready to Download</h2>
              <p className="mt-1 text-sm leading-6 text-muted-foreground">
                {selectedSections.length
                  ? `${selectedSections.length} of ${healthProfileExportOptions.length} sections selected.`
                  : `0 of ${healthProfileExportOptions.length} sections selected.`}
              </p>
            </div>
          </div>

          {localError || error ? (
            <p className="mt-5 rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">
              {localError ?? error}
            </p>
          ) : null}

          <button
            type="button"
            onClick={() => void handleDownload()}
            disabled={isBusy}
            className="mt-6 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-[0_18px_34px_-20px_rgba(7,95,198,0.9)] transition hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/15 disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none"
          >
            <Download className="size-4" aria-hidden="true" />
            {isBusy ? "Preparing" : "Download PDF"}
          </button>
        </aside>
      </section>
    </div>
  );
}

type AddressDraft = {
  line1: string;
  line2: string;
  city: string;
  region: string;
  postal_code: string;
  country_code: string;
};

type EmergencyContactDraft = {
  name: string;
  relationship: string;
  primary_mobile_number: string;
  alternate_mobile_number: string;
  whatsapp_number: string;
  address: string;
  priority: string;
};

type PrimaryDoctorDraft = {
  name: string;
  speciality: string;
  hospital_clinic_name: string;
  mobile_number: string;
  emergency_contact_number: string;
  address: string;
  email: string;
  preferred_for_emergency: boolean;
};

type HospitalDraft = {
  name: string;
  emergency_number: string;
  address: string;
  ambulance_contact_number: string;
};

type PreferredHospitalsDraft = {
  primary: HospitalDraft;
  alternate: HospitalDraft;
};

type HealthInsuranceDraft = {
  insurance_company: string;
  policy_name: string;
  policy_number: string;
  insurance_company_contact_email: string;
  insurance_company_contact_phone: string;
};

function TextField({
  autoComplete,
  disabled,
  inputMode,
  label,
  maxLength,
  onChange,
  placeholder,
  type = "text",
  value,
}: {
  autoComplete?: string;
  disabled?: boolean;
  inputMode?: "email" | "tel" | "text";
  label: string;
  maxLength?: number;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  value: string;
}) {
  return (
    <label className="block min-w-0 text-sm font-semibold text-foreground">
      {label}
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
        maxLength={maxLength}
        autoComplete={autoComplete}
        inputMode={inputMode}
        placeholder={placeholder}
        disabled={disabled}
      />
    </label>
  );
}

function TextAreaField({
  disabled,
  label,
  maxLength,
  onChange,
  placeholder,
  value,
}: {
  disabled?: boolean;
  label: string;
  maxLength?: number;
  onChange: (value: string) => void;
  placeholder?: string;
  value: string;
}) {
  return (
    <label className="block min-w-0 text-sm font-semibold text-foreground">
      {label}
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-2 min-h-24 w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
        maxLength={maxLength}
        placeholder={placeholder}
        disabled={disabled}
      />
    </label>
  );
}

function ProfileEditSection({ children, title }: { children: ReactNode; title: string }) {
  return (
    <section className="rounded-2xl border border-primary/10 bg-surface/70 p-4">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}

// A short, hand-picked spread of major IANA zones -- only used when this runtime doesn't expose
// Intl.supportedValuesOf (older browsers). Not an attempt at exhaustive coverage: the combobox
// below always folds the current/persisted/browser-detected value into whatever list it has, so a
// real selection is never unrepresentable even on that reduced list.
const FALLBACK_TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Moscow",
  "Africa/Cairo",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Dhaka",
  "Asia/Bangkok",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Asia/Singapore",
  "Australia/Sydney",
  "Australia/Perth",
  "Pacific/Auckland",
  "Pacific/Honolulu",
];

function availableTimezones(): string[] {
  // Intl.supportedValuesOf is not a validation authority here (the account-service's zoneinfo
  // check remains authoritative) -- it's only used, when present, to offer a fuller search list
  // than the small hand-picked fallback.
  if (typeof Intl.supportedValuesOf === "function") {
    try {
      const zones = Intl.supportedValuesOf("timeZone");
      if (zones.length) {
        return zones;
      }
    } catch {
      // Fall through to the fallback list below.
    }
  }
  return FALLBACK_TIMEZONES;
}

// A small, curated set of legacy IANA zone names that Intl.supportedValuesOf (or an older saved
// profile) may still surface, mapped to their current canonical identifier -- these are all
// documented IANA tzdata renames (e.g. Europe/Kiev -> Europe/Kyiv in 2022), not a home-grown
// timezone database. This only controls what users SEE: the account-service's zoneinfo check
// remains the authority on validity, and a persisted legacy alias is never rewritten on its own --
// see canonicalTimezoneId's call sites for exactly when canonicalization is actually applied.
const LEGACY_TIMEZONE_ALIASES: Record<string, string> = {
  "Asia/Calcutta": "Asia/Kolkata",
  "Asia/Katmandu": "Asia/Kathmandu",
  "Asia/Saigon": "Asia/Ho_Chi_Minh",
  "Asia/Rangoon": "Asia/Yangon",
  "Asia/Dacca": "Asia/Dhaka",
  "Asia/Thimbu": "Asia/Thimphu",
  "Asia/Chungking": "Asia/Chongqing",
  "Asia/Macao": "Asia/Macau",
  "Asia/Ujung_Pandang": "Asia/Makassar",
  "Africa/Asmera": "Africa/Asmara",
  "Atlantic/Faeroe": "Atlantic/Faroe",
  "Europe/Kiev": "Europe/Kyiv",
  "America/Godthab": "America/Nuuk",
  "Pacific/Ponape": "Pacific/Pohnpei",
  "Pacific/Truk": "Pacific/Chuuk",
};

function canonicalTimezoneId(zone: string): string {
  return LEGACY_TIMEZONE_ALIASES[zone] ?? zone;
}

// Computed fresh from the IANA zone for the given moment (defaults to now) via Intl -- naturally
// DST-aware and correct for fractional-hour zones, since it reads whatever offset that zone
// actually observes right now rather than a fixed/stored value. Returns null (never persisted,
// never displayed) if this runtime can't resolve the zone at all.
function timezoneUtcOffsetLabel(zone: string, at: Date = new Date()): string | null {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "shortOffset" }).formatToParts(at);
    const raw = parts.find((part) => part.type === "timeZoneName")?.value;
    const match = raw ? /^GMT([+-])(\d{1,2})(?::(\d{2}))?$/.exec(raw) : null;
    if (!match) {
      return null;
    }
    const [, sign, hours, minutes = "00"] = match;
    return `UTC${sign}${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
  } catch {
    return null;
  }
}

// The single formatter reused everywhere a timezone is shown to a user (the combobox's own
// button, its option list, and the read-only Profile view) so the presentation -- canonical name,
// no spaces around the slash, current UTC offset -- stays identical in every one of those places.
function formatTimezoneLabel(zone: string) {
  const name = canonicalTimezoneId(zone).replace(/_/g, " ");
  const offset = timezoneUtcOffsetLabel(zone);
  return offset ? `${name} (${offset})` : name;
}

function TimezoneCombobox({
  disabled,
  onChange,
  value,
  label = "Timezone",
}: {
  disabled?: boolean;
  onChange: (zone: string) => void;
  value: string;
  label?: string;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const comboRef = useRef<HTMLDivElement | null>(null);

  const zones = useMemo(() => {
    // Canonicalize (and dedupe) up front so legacy aliases never appear as separate-looking
    // entries alongside their modern name, and so every entry here is already the identifier
    // that gets saved on selection -- no separate canonicalization step needed at click time.
    const base = availableTimezones().map(canonicalTimezoneId);
    const canonicalValue = canonicalTimezoneId(value);
    // Guarantee the current value is always selectable/visible in the list, even if it isn't
    // present in whatever zone list this runtime happens to expose.
    const withValue = base.includes(canonicalValue) ? base : [canonicalValue, ...base];
    return Array.from(new Set(withValue));
  }, [value]);

  const filteredZones = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return zones.slice(0, 12);
    }

    return zones.filter((zone) => zone.toLowerCase().includes(normalizedQuery)).slice(0, 24);
  }, [zones, query]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (comboRef.current?.contains(event.target as Node)) {
        return;
      }

      setOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={comboRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((nextOpen) => !nextOpen)}
        className="mt-2 flex min-h-12 w-full items-center gap-3 rounded-2xl border border-border bg-white px-4 py-3 text-left text-sm font-semibold transition hover:border-primary/30 focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:cursor-not-allowed disabled:opacity-60"
        aria-label={`${label}, ${formatTimezoneLabel(value)}`}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="min-w-0 flex-1 truncate">{formatTimezoneLabel(value)}</span>
        <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      </button>
      {open ? (
        <div className="absolute left-0 right-0 top-[calc(100%+0.5rem)] z-20 rounded-2xl border border-border bg-white p-2 shadow-lift">
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="h-11 w-full rounded-xl border border-border bg-surface px-3 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
            placeholder="Search timezones"
            autoComplete="off"
            aria-label={`${label} search`}
          />
          <div className="mt-2 max-h-64 overflow-y-auto" role="listbox" aria-label="Timezone options">
            {filteredZones.length ? (
              filteredZones.map((zone) => (
                <button
                  key={zone}
                  type="button"
                  onClick={() => {
                    onChange(zone);
                    setQuery("");
                    setOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm transition hover:bg-secondary",
                    zone === canonicalTimezoneId(value) ? "bg-secondary font-semibold text-foreground" : "text-muted-foreground",
                  )}
                  role="option"
                  aria-selected={zone === canonicalTimezoneId(value)}
                >
                  <span className="min-w-0 flex-1 truncate">{formatTimezoneLabel(zone)}</span>
                </button>
              ))
            ) : (
              <p className="px-3 py-4 text-sm text-muted-foreground">No timezones found.</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function ProfileEditPage() {
  const navigate = useNavigate();
  const { error, profile, saveProfile, status } = useAccountProfile();
  const defaultCountry = useMemo(() => findPhoneCountry("US") ?? phoneCountries[0], []);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<PhoneCountry>(defaultCountry);
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [birthTime, setBirthTime] = useState("");
  const [birthCity, setBirthCity] = useState("");
  const [birthCountry, setBirthCountry] = useState("");
  const [birthFacilityName, setBirthFacilityName] = useState("");
  const [sexAssignedAtBirth, setSexAssignedAtBirth] = useState("");
  const [genderIdentity, setGenderIdentity] = useState("");
  const [bloodGroup, setBloodGroup] = useState("");
  const [residentialAddress, setResidentialAddress] = useState<AddressDraft>(() => addressDraftFromProfile(null));
  const [preferredLanguage, setPreferredLanguage] = useState("");
  const [emergencyContacts, setEmergencyContacts] = useState<EmergencyContactDraft[]>(() => emergencyContactDraftsFromProfile([]));
  const [primaryDoctor, setPrimaryDoctor] = useState<PrimaryDoctorDraft>(() => doctorDraftFromProfile(null));
  const [preferredHospitals, setPreferredHospitals] = useState<PreferredHospitalsDraft>(() => hospitalsDraftFromProfile(null));
  const [defaultTimezone, setDefaultTimezone] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const isBusy = status === "loading" || status === "saving";
  const selectedBirthCountry = findPhoneCountry(birthCountry);

  const resetDraft = useCallback(() => {
    if (!profile) {
      return;
    }

    const profileCountry = findPhoneCountry(profile.contact_phone_country_code) ?? defaultCountry;
    setFirstName(profile.first_name ?? "");
    setLastName(profile.last_name ?? "");
    setSelectedCountry(profileCountry);
    setPhoneNumber(getEditablePhoneNumber(profile.contact_phone_e164, profile.contact_phone_country_code));
    setDateOfBirth(profile.date_of_birth ?? "");
    setBirthTime(profile.birth_time ?? "");
    setBirthCity(profile.birth_city ?? "");
    setBirthCountry(profile.birth_country ?? "");
    setBirthFacilityName(profile.birth_facility_name ?? "");
    setSexAssignedAtBirth(profile.sex_assigned_at_birth ?? "");
    setGenderIdentity(profile.gender_identity ?? "");
    setBloodGroup(profile.blood_group ?? "");
    setResidentialAddress(addressDraftFromProfile(profile.residential_address));
    setPreferredLanguage(profile.preferred_language ?? "");
    setEmergencyContacts(emergencyContactDraftsFromProfile(profile.emergency_contacts));
    setPrimaryDoctor(doctorDraftFromProfile(profile.primary_doctor));
    setPreferredHospitals(hospitalsDraftFromProfile(profile.preferred_hospitals));
    // Persisted value wins when present -- canonicalized into the draft so a legacy alias saved
    // previously (e.g. "Asia/Calcutta") reads/saves as its current name going forward. This is
    // never an automatic PATCH on its own: it only reaches the backend if/when the user performs
    // an explicit Save Changes on this form, same as every other field's draft value. Otherwise,
    // suggest the browser's detected timezone as draft UI state only -- it's just what the field
    // shows until Save Changes is clicked, never written back on its own.
    setDefaultTimezone(canonicalTimezoneId(profile.default_timezone ?? localTimezone()));
    setValidationError(null);
  }, [defaultCountry, profile]);

  useEffect(() => {
    resetDraft();
  }, [resetDraft]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "saving") {
      return;
    }

    setValidationError(null);

    const trimmedPhone = phoneNumber.trim();
    let contactPhoneE164: string | null = null;
    let contactPhoneCountryCode: string | null = null;

    if (trimmedPhone) {
      const parsedPhone = parsePhoneNumberFromString(trimmedPhone, selectedCountry.code);
      if (!parsedPhone?.isValid()) {
        setValidationError(`Enter a valid phone number for ${selectedCountry.name}.`);
        return;
      }

      contactPhoneE164 = parsedPhone.number;
      contactPhoneCountryCode = selectedCountry.code;
    }

    const { contacts: normalizedEmergencyContacts, error: emergencyContactsError } = emergencyContactsPayloadFromDrafts(emergencyContacts);
    if (emergencyContactsError) {
      setValidationError(emergencyContactsError);
      return;
    }

    const { hospitals: nextPreferredHospitals, error: preferredHospitalsError } = preferredHospitalsPayloadFromDraft(preferredHospitals);
    if (preferredHospitalsError) {
      setValidationError(preferredHospitalsError);
      return;
    }

    const nextProfile = await saveProfile({
      first_name: firstName.trim() || null,
      last_name: lastName.trim() || null,
      contact_phone_e164: contactPhoneE164,
      contact_phone_country_code: contactPhoneCountryCode,
      date_of_birth: dateOfBirth || null,
      birth_time: birthTime || null,
      birth_city: birthCity.trim() || null,
      birth_country: birthCountry || null,
      birth_facility_name: birthFacilityName.trim() || null,
      sex_assigned_at_birth: sexAssignedAtBirth || null,
      gender_identity: genderIdentity.trim() || null,
      blood_group: bloodGroup || null,
      residential_address: addressPayloadFromDraft(residentialAddress),
      preferred_language: preferredLanguage.trim() || null,
      emergency_contacts: normalizedEmergencyContacts,
      primary_doctor: doctorPayloadFromDraft(primaryDoctor),
      preferred_hospitals: nextPreferredHospitals,
      default_timezone: defaultTimezone || null,
    });

    if (nextProfile) {
      void navigate("/app/profile", { replace: true, state: { saved: true } });
    }
  };

  return (
    <div className="space-y-6">
      <BackAction to="/app/profile" label="Back to Profile" />
      <WorkspacePageHeader
        eyebrow="Profile"
        title="Edit Profile"
        body="Update the personal and healthcare details you want available in your Care Hub."
      />
      <article className="min-w-0 rounded-2xl border border-primary/10 bg-white p-5 shadow-premium sm:p-6">
        {status === "loading" || status === "idle" ? (
          <BrandedLoader compact label="Loading your profile" />
        ) : (
            <form className="grid gap-5" onSubmit={(event) => void handleSubmit(event)}>
              <div className="rounded-2xl border border-primary/10 bg-primary/5 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">MedoPilot ID</p>
                <p className="mt-1 font-semibold text-foreground">{profile?.medopilot_user_id ?? "Preparing your ID"}</p>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">Your MedoPilot ID is generated by MedoPilot and cannot be edited.</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Default timezone</p>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">
                  Used as the starting timezone when you create new reminders and schedules. Changing this never affects
                  timezones already saved on existing schedules.
                </p>
                <TimezoneCombobox value={defaultTimezone} onChange={setDefaultTimezone} disabled={isBusy} label="Default timezone" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block min-w-0 text-sm font-semibold text-foreground">
                  First name
                  <input
                    value={firstName}
                    onChange={(event) => setFirstName(event.target.value)}
                    className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                    maxLength={80}
                    autoComplete="given-name"
                    disabled={isBusy}
                  />
                </label>
                <label className="block min-w-0 text-sm font-semibold text-foreground">
                  Last name
                  <input
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                    className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                    maxLength={80}
                    autoComplete="family-name"
                    disabled={isBusy}
                  />
                </label>
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Contact phone</p>
                <div className="mt-2 grid gap-2 rounded-2xl border border-border bg-white p-2 transition focus-within:border-primary focus-within:ring-4 focus-within:ring-primary/10 sm:grid-cols-[minmax(10rem,0.46fr)_minmax(0,1fr)]">
                  <CountryCombobox
                    value={selectedCountry}
                    onChange={setSelectedCountry}
                    disabled={isBusy}
                    label="Calling code"
                    placeholder="Search country or code"
                    buttonClassName="mt-0 min-h-10 rounded-xl border-0 bg-surface px-3 py-2 focus:ring-0"
                  />
                  <label className="sr-only" htmlFor="contact-phone-number">
                    Contact phone number
                  </label>
                  <input
                    id="contact-phone-number"
                    value={phoneNumber}
                    onChange={(event) => setPhoneNumber(event.target.value)}
                    className="min-h-10 min-w-0 rounded-xl px-3 text-sm outline-none"
                    inputMode="tel"
                    autoComplete="tel-national"
                    placeholder="Phone number"
                    disabled={isBusy}
                  />
                </div>
              </div>
              <ProfileEditSection title="Contact / Address">
                <CountryCombobox
                  value={findPhoneCountry(residentialAddress.country_code) ?? defaultCountry}
                  onChange={(country) => setResidentialAddress((current) => ({ ...current, country_code: country.code }))}
                  disabled={isBusy}
                  label="Residential country"
                  placeholder="Search country"
                  showDialCode={false}
                />
                <TextField
                  label="Address Line 1"
                  value={residentialAddress.line1}
                  onChange={(value) => setResidentialAddress((current) => ({ ...current, line1: value }))}
                  maxLength={240}
                  autoComplete="address-line1"
                  disabled={isBusy}
                />
                <TextField
                  label="Address Line 2"
                  value={residentialAddress.line2}
                  onChange={(value) => setResidentialAddress((current) => ({ ...current, line2: value }))}
                  maxLength={240}
                  autoComplete="address-line2"
                  disabled={isBusy}
                />
                <TextField
                  label="City"
                  value={residentialAddress.city}
                  onChange={(value) => setResidentialAddress((current) => ({ ...current, city: value }))}
                  maxLength={120}
                  autoComplete="address-level2"
                  disabled={isBusy}
                />
                <TextField
                  label="State / Region"
                  value={residentialAddress.region}
                  onChange={(value) => setResidentialAddress((current) => ({ ...current, region: value }))}
                  maxLength={120}
                  autoComplete="address-level1"
                  disabled={isBusy}
                />
                <TextField
                  label="Postal Code"
                  value={residentialAddress.postal_code}
                  onChange={(value) => setResidentialAddress((current) => ({ ...current, postal_code: value }))}
                  maxLength={32}
                  autoComplete="postal-code"
                  disabled={isBusy}
                />
              </ProfileEditSection>
              <div className="rounded-2xl border border-primary/10 bg-surface/70 p-4">
                <h3 className="text-sm font-semibold text-foreground">Personal Information</h3>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <label className="block min-w-0 text-sm font-semibold text-foreground">
                    Date Of Birth
                    <input
                      type="date"
                      value={dateOfBirth}
                      onChange={(event) => setDateOfBirth(event.target.value)}
                      className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                      max={new Date().toISOString().slice(0, 10)}
                      disabled={isBusy}
                    />
                  </label>
                  <label className="block min-w-0 text-sm font-semibold text-foreground">
                    Blood Group
                    <select
                      value={bloodGroup}
                      onChange={(event) => setBloodGroup(event.target.value)}
                      className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                      disabled={isBusy}
                    >
                      <option value="">Not added yet</option>
                      {bloodGroups.map((group) => (
                        <option key={group} value={group}>
                          {group}
                        </option>
                      ))}
                    </select>
                  </label>
                  <MoreDetails className="sm:col-span-2" title="More Personal Details">
                    <label className="block min-w-0 text-sm font-semibold text-foreground">
                      Birth Time <span className="font-normal text-muted-foreground">(optional)</span>
                      <input
                        type="time"
                        value={birthTime}
                        onChange={(event) => setBirthTime(event.target.value)}
                        className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                        disabled={isBusy}
                      />
                    </label>
                    <label className="block min-w-0 text-sm font-semibold text-foreground">
                      Birth City
                      <input
                        value={birthCity}
                        onChange={(event) => setBirthCity(event.target.value)}
                        className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                        maxLength={120}
                        autoComplete="address-level2"
                        disabled={isBusy}
                      />
                    </label>
                    <CountryCombobox
                      value={selectedBirthCountry ?? defaultCountry}
                      onChange={(country) => setBirthCountry(country.code)}
                      disabled={isBusy}
                      label="Birth Country"
                      placeholder="Search birth country"
                      showDialCode={false}
                    />
                    <label className="block min-w-0 text-sm font-semibold text-foreground">
                      Birth Facility
                      <input
                        value={birthFacilityName}
                        onChange={(event) => setBirthFacilityName(event.target.value)}
                        className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                        maxLength={120}
                        disabled={isBusy}
                      />
                    </label>
                    <label className="block min-w-0 text-sm font-semibold text-foreground">
                      Sex Assigned At Birth
                      <select
                        value={sexAssignedAtBirth}
                        onChange={(event) => setSexAssignedAtBirth(event.target.value)}
                        className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                        disabled={isBusy}
                      >
                        {sexAssignedAtBirthOptions.map((option) => (
                          <option key={option.value || "blank"} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block min-w-0 text-sm font-semibold text-foreground">
                      Gender Identity <span className="font-normal text-muted-foreground">(optional)</span>
                      <input
                        value={genderIdentity}
                        onChange={(event) => setGenderIdentity(event.target.value)}
                        className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                        maxLength={80}
                        disabled={isBusy}
                      />
                    </label>
                    <TextField
                      label="Preferred Language"
                      value={preferredLanguage}
                      onChange={setPreferredLanguage}
                      maxLength={80}
                      disabled={isBusy}
                      placeholder="English"
                    />
                  </MoreDetails>
                </div>
              </div>
              <ProfileEditSection title="Emergency Contacts">
                {emergencyContacts.map((contact, index) => (
                  <div key={index} className="min-w-0 rounded-2xl border border-border bg-white p-4 sm:col-span-2">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <h4 className="font-semibold text-foreground">Contact {index + 1}</h4>
                    </div>
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      <TextField
                        label="Name"
                        value={contact.name}
                        onChange={(value) =>
                          setEmergencyContacts((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, name: value } : item)))
                        }
                        maxLength={80}
                        disabled={isBusy}
                      />
                      <TextField
                        label="Relationship"
                        value={contact.relationship}
                        onChange={(value) =>
                          setEmergencyContacts((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, relationship: value } : item)))
                        }
                        maxLength={80}
                        disabled={isBusy}
                      />
                      <TextField
                        label="Primary mobile number"
                        value={contact.primary_mobile_number}
                        onChange={(value) =>
                          setEmergencyContacts((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, primary_mobile_number: value } : item)))
                        }
                        maxLength={40}
                        inputMode="tel"
                        disabled={isBusy}
                      />
                      <MoreDetails className="sm:col-span-2">
                        <label className="block text-sm font-semibold text-foreground">
                          Priority
                          <select
                            value={contact.priority}
                            onChange={(event) =>
                              setEmergencyContacts((current) =>
                                current.map((item, itemIndex) => (itemIndex === index ? { ...item, priority: event.target.value } : item)),
                              )
                            }
                            className="mt-2 h-10 rounded-xl border border-border bg-white px-3 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                            disabled={isBusy}
                          >
                            {[1, 2, 3].map((priority) => (
                              <option key={priority} value={priority}>
                                {priority}
                              </option>
                            ))}
                          </select>
                        </label>
                        <TextField
                          label="Alternate Mobile"
                          value={contact.alternate_mobile_number}
                          onChange={(value) =>
                            setEmergencyContacts((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, alternate_mobile_number: value } : item)))
                          }
                          maxLength={40}
                          inputMode="tel"
                          disabled={isBusy}
                        />
                        <TextField
                          label="WhatsApp"
                          value={contact.whatsapp_number}
                          onChange={(value) =>
                            setEmergencyContacts((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, whatsapp_number: value } : item)))
                          }
                          maxLength={40}
                          inputMode="tel"
                          disabled={isBusy}
                        />
                        <TextAreaField
                          label="Address"
                          value={contact.address}
                          onChange={(value) =>
                            setEmergencyContacts((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, address: value } : item)))
                          }
                          maxLength={240}
                          disabled={isBusy}
                        />
                      </MoreDetails>
                    </div>
                  </div>
                ))}
              </ProfileEditSection>
              <ProfileEditSection title="Primary / Family Doctor">
                <TextField
                  label="Name"
                  value={primaryDoctor.name}
                  onChange={(value) => setPrimaryDoctor((current) => ({ ...current, name: value }))}
                  maxLength={80}
                  disabled={isBusy}
                />
                <TextField
                  label="Mobile number"
                  value={primaryDoctor.mobile_number}
                  onChange={(value) => setPrimaryDoctor((current) => ({ ...current, mobile_number: value }))}
                  maxLength={40}
                  inputMode="tel"
                  disabled={isBusy}
                />
                <TextField
                  label="Emergency contact number"
                  value={primaryDoctor.emergency_contact_number}
                  onChange={(value) => setPrimaryDoctor((current) => ({ ...current, emergency_contact_number: value }))}
                  maxLength={40}
                  inputMode="tel"
                  disabled={isBusy}
                />
                <MoreDetails className="sm:col-span-2">
                  <TextField
                    label="Speciality"
                    value={primaryDoctor.speciality}
                    onChange={(value) => setPrimaryDoctor((current) => ({ ...current, speciality: value }))}
                    maxLength={120}
                    disabled={isBusy}
                  />
                  <TextField
                    label="Hospital / clinic name"
                    value={primaryDoctor.hospital_clinic_name}
                    onChange={(value) => setPrimaryDoctor((current) => ({ ...current, hospital_clinic_name: value }))}
                    maxLength={120}
                    disabled={isBusy}
                  />
                  <TextField
                    label="Email"
                    value={primaryDoctor.email}
                    onChange={(value) => setPrimaryDoctor((current) => ({ ...current, email: value }))}
                    maxLength={254}
                    inputMode="email"
                    disabled={isBusy}
                  />
                  <TextAreaField
                    label="Address"
                    value={primaryDoctor.address}
                    onChange={(value) => setPrimaryDoctor((current) => ({ ...current, address: value }))}
                    maxLength={240}
                    disabled={isBusy}
                  />
                  <label className="flex min-w-0 items-start gap-3 rounded-2xl border border-border bg-white px-4 py-3 text-sm font-semibold text-foreground">
                    <input
                      type="checkbox"
                      checked={primaryDoctor.preferred_for_emergency}
                      onChange={(event) => setPrimaryDoctor((current) => ({ ...current, preferred_for_emergency: event.target.checked }))}
                      className="mt-1 size-4 accent-primary"
                      disabled={isBusy}
                    />
                    <span>
                      Preferred For Emergency
                      <span className="mt-1 block font-normal leading-6 text-muted-foreground">Show this doctor prominently in Emergency Mode.</span>
                    </span>
                  </label>
                </MoreDetails>
              </ProfileEditSection>
              <ProfileEditSection title="Preferred Hospitals">
                {(["primary", "alternate"] as const).map((kind) => (
                  <div key={kind} className="min-w-0 rounded-2xl border border-border bg-white p-4 sm:col-span-2">
                    <h4 className="font-semibold text-foreground">{kind === "primary" ? "Primary Hospital" : "Alternate Hospital"}</h4>
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      <TextField label="Name" value={preferredHospitals[kind].name} onChange={(value) => setPreferredHospitals((current) => ({ ...current, [kind]: { ...current[kind], name: value } }))} maxLength={120} disabled={isBusy} />
                      <TextField label="Emergency number" value={preferredHospitals[kind].emergency_number} onChange={(value) => setPreferredHospitals((current) => ({ ...current, [kind]: { ...current[kind], emergency_number: value } }))} maxLength={40} inputMode="tel" disabled={isBusy} />
                      <MoreDetails className="sm:col-span-2">
                        <TextField label="Ambulance contact number" value={preferredHospitals[kind].ambulance_contact_number} onChange={(value) => setPreferredHospitals((current) => ({ ...current, [kind]: { ...current[kind], ambulance_contact_number: value } }))} maxLength={40} inputMode="tel" disabled={isBusy} />
                        <TextAreaField label="Address" value={preferredHospitals[kind].address} onChange={(value) => setPreferredHospitals((current) => ({ ...current, [kind]: { ...current[kind], address: value } }))} maxLength={240} disabled={isBusy} />
                      </MoreDetails>
                    </div>
                  </div>
                ))}
              </ProfileEditSection>
              <p className="text-sm leading-6 text-muted-foreground">
                Contact phone is used for your MedoPilot profile only. It is separate from sign-in security settings.
              </p>
              {validationError || error ? (
                <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">
                  {validationError ?? error}
                </p>
              ) : null}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <button
                  type="submit"
                  disabled={isBusy}
                  className="inline-flex min-h-12 items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "saving" ? "Saving" : "Save Changes"}
                </button>
                <button
                  type="button"
                  onClick={() => void navigate("/app/profile")}
                  disabled={status === "saving"}
                  className="inline-flex min-h-12 items-center justify-center rounded-full border border-border bg-white px-6 py-3 text-sm font-semibold text-muted-foreground transition hover:border-primary/30 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Cancel
                </button>
                {status === "error" ? <p className="text-sm text-muted-foreground">Please try again in a moment.</p> : null}
              </div>
            </form>
        )}
      </article>
    </div>
  );
}

type AllergyDraft = {
  substance_name: string;
  category: AllergyCategory;
  reaction: string;
  severity: AllergySeverity;
  status: AllergyStatus;
  notes: string;
};

type ConditionDraft = {
  condition_name: string;
  status: HealthConditionStatus;
  diagnosed_date: string;
  notes: string;
};

const allergyCategoryOptions: Array<{ value: AllergyCategory; label: string }> = [
  { value: "medication", label: "Medication / Drug" },
  { value: "food", label: "Food" },
  { value: "environmental", label: "Environmental" },
  { value: "other", label: "Other" },
];

const allergySeverityOptions: Array<{ value: AllergySeverity; label: string }> = [
  { value: "unknown", label: "Unknown" },
  { value: "mild", label: "Mild" },
  { value: "moderate", label: "Moderate" },
  { value: "severe", label: "Severe" },
  { value: "life_threatening", label: "Life-threatening" },
];

const clinicalStatusOptions: Array<{ value: AllergyStatus; label: string }> = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "resolved", label: "Resolved" },
];

function emptyAllergyDraft(): AllergyDraft {
  return {
    substance_name: "",
    category: "medication",
    reaction: "",
    severity: "unknown",
    status: "active",
    notes: "",
  };
}

function allergyDraftFromRecord(record: Allergy): AllergyDraft {
  return {
    substance_name: record.substance_name,
    category: record.category,
    reaction: record.reaction ?? "",
    severity: record.severity,
    status: record.status,
    notes: record.notes ?? "",
  };
}

function allergyPayloadFromDraft(draft: AllergyDraft): { payload: AllergyInput; error: string | null } {
  if (!draft.substance_name.trim()) {
    return { payload: { ...emptyAllergyDraft(), reaction: null, notes: null }, error: "Add the allergy or substance name before saving." };
  }

  return {
    payload: {
      substance_name: draft.substance_name.trim(),
      category: draft.category,
      reaction: draft.reaction.trim() || null,
      severity: draft.severity,
      status: draft.status,
      notes: draft.notes.trim() || null,
    },
    error: null,
  };
}

function emptyConditionDraft(): ConditionDraft {
  return {
    condition_name: "",
    status: "active",
    diagnosed_date: "",
    notes: "",
  };
}

function conditionDraftFromRecord(record: HealthCondition): ConditionDraft {
  return {
    condition_name: record.condition_name,
    status: record.status,
    diagnosed_date: record.diagnosed_date ?? "",
    notes: record.notes ?? "",
  };
}

function conditionPayloadFromDraft(draft: ConditionDraft): { payload: HealthConditionInput; error: string | null } {
  if (!draft.condition_name.trim()) {
    return { payload: { condition_name: "", status: "active", diagnosed_date: null, notes: null }, error: "Add the condition name before saving." };
  }

  if (draft.diagnosed_date && draft.diagnosed_date > new Date().toISOString().slice(0, 10)) {
    return { payload: { condition_name: "", status: "active", diagnosed_date: null, notes: null }, error: "The diagnosed date cannot be in the future." };
  }

  return {
    payload: {
      condition_name: draft.condition_name.trim(),
      status: draft.status,
      diagnosed_date: draft.diagnosed_date || null,
      notes: draft.notes.trim() || null,
    },
    error: null,
  };
}

function ClinicalSelect<TValue extends string>({
  disabled,
  label,
  onChange,
  options,
  value,
}: {
  disabled?: boolean;
  label: string;
  onChange: (value: TValue) => void;
  options: Array<{ value: TValue; label: string }>;
  value: TValue;
}) {
  return (
    <label className="block min-w-0 text-sm font-semibold text-foreground">
      {label}
      <select
        value={value}
        onChange={(event) => onChange(event.target.value as TValue)}
        className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
        disabled={disabled}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function AllergyForm({
  draft,
  onCancel,
  onChange,
  onSubmit,
  saving,
  validationError,
}: {
  draft: AllergyDraft;
  onCancel?: () => void;
  onChange: (draft: AllergyDraft) => void;
  onSubmit: () => void;
  saving: boolean;
  validationError: string | null;
}) {
  const update = <TKey extends keyof AllergyDraft>(key: TKey, value: AllergyDraft[TKey]) => onChange({ ...draft, [key]: value });

  return (
    <form className="rounded-2xl border border-primary/10 bg-white p-5 shadow-premium" onSubmit={(event) => { event.preventDefault(); onSubmit(); }}>
      <h2 className="text-base font-semibold">Allergy Details</h2>
      <div className="mt-4 grid gap-4">
        <TextField label="Allergy or substance" value={draft.substance_name} onChange={(value) => update("substance_name", value)} maxLength={160} disabled={saving} placeholder="Penicillin, peanuts, pollen" />
        <TextField label="Reaction" value={draft.reaction} onChange={(value) => update("reaction", value)} maxLength={160} disabled={saving} placeholder="Hives, swelling, breathing trouble" />
        <ClinicalSelect label="Severity" value={draft.severity} onChange={(value) => update("severity", value)} options={allergySeverityOptions} disabled={saving} />
        <ClinicalSelect label="Status" value={draft.status} onChange={(value) => update("status", value)} options={clinicalStatusOptions} disabled={saving} />
        <MoreDetails>
          <ClinicalSelect label="Type" value={draft.category} onChange={(value) => update("category", value)} options={allergyCategoryOptions} disabled={saving} />
          <TextAreaField label="Notes" value={draft.notes} onChange={(value) => update("notes", value)} maxLength={2000} disabled={saving} />
        </MoreDetails>
      </div>
      {validationError ? <p className="mt-4 rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">{validationError}</p> : null}
      <div className="mt-5 flex flex-col gap-3 sm:flex-row">
        <button type="submit" disabled={saving} className="inline-flex min-h-10 items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-60">
          {saving ? "Saving" : "Save Allergy"}
        </button>
        {onCancel ? (
          <button type="button" onClick={onCancel} disabled={saving} className="inline-flex min-h-10 items-center justify-center rounded-full border border-border bg-white px-5 py-2.5 text-sm font-semibold text-muted-foreground transition hover:border-primary/30 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60">
            Cancel
          </button>
        ) : null}
      </div>
    </form>
  );
}

function ConditionForm({
  draft,
  onCancel,
  onChange,
  onSubmit,
  saving,
  validationError,
}: {
  draft: ConditionDraft;
  onCancel?: () => void;
  onChange: (draft: ConditionDraft) => void;
  onSubmit: () => void;
  saving: boolean;
  validationError: string | null;
}) {
  const update = <TKey extends keyof ConditionDraft>(key: TKey, value: ConditionDraft[TKey]) => onChange({ ...draft, [key]: value });

  return (
    <form className="rounded-2xl border border-primary/10 bg-white p-5 shadow-premium" onSubmit={(event) => { event.preventDefault(); onSubmit(); }}>
      <h2 className="text-base font-semibold">Condition Details</h2>
      <div className="mt-4 grid gap-4">
        <TextField label="Condition name" value={draft.condition_name} onChange={(value) => update("condition_name", value)} maxLength={160} disabled={saving} placeholder="Asthma, diabetes, hypertension" />
        <ClinicalSelect label="Status" value={draft.status} onChange={(value) => update("status", value)} options={clinicalStatusOptions} disabled={saving} />
        <label className="block min-w-0 text-sm font-semibold text-foreground">
          Diagnosed date <span className="font-normal text-muted-foreground">(optional)</span>
          <input
            type="date"
            value={draft.diagnosed_date}
            onChange={(event) => update("diagnosed_date", event.target.value)}
            className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
            max={new Date().toISOString().slice(0, 10)}
            disabled={saving}
          />
        </label>
        <MoreDetails>
          <TextAreaField label="Notes" value={draft.notes} onChange={(value) => update("notes", value)} maxLength={2000} disabled={saving} />
        </MoreDetails>
      </div>
      {validationError ? <p className="mt-4 rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">{validationError}</p> : null}
      <div className="mt-5 flex flex-col gap-3 sm:flex-row">
        <button type="submit" disabled={saving} className="inline-flex min-h-10 items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-60">
          {saving ? "Saving" : "Save Condition"}
        </button>
        {onCancel ? (
          <button type="button" onClick={onCancel} disabled={saving} className="inline-flex min-h-10 items-center justify-center rounded-full border border-border bg-white px-5 py-2.5 text-sm font-semibold text-muted-foreground transition hover:border-primary/30 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60">
            Cancel
          </button>
        ) : null}
      </div>
    </form>
  );
}

export function AllergiesPage() {
  const location = useLocation();
  const { allergies, error, saved, status, updateAllergy } = useClinicalRecords();
  const state = location.state as { saved?: boolean } | null;
  const activeAllergies = useMemo(() => getImportantAllergies(allergies), [allergies]);
  const sortedAllergies = useMemo(
    () => [...allergies].sort((left, right) => (left.status === "active" ? -1 : 1) - (right.status === "active" ? -1 : 1) || left.substance_name.localeCompare(right.substance_name)),
    [allergies],
  );

  return (
    <div className="space-y-4 lg:space-y-5">
      <PageHero
        icon={AlertTriangle}
        title="Allergies"
        description="Allergies and sensitivities to keep on file."
        action={
          <Link to="/app/allergies/new" className={heroActionClassName}>
            <Plus className="size-4" aria-hidden="true" />
            Add
          </Link>
        }
      />

      <div className="flex min-w-0 items-center gap-2 overflow-x-auto pb-1 text-sm font-semibold text-muted-foreground [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <span className={cn("shrink-0 rounded-full px-3 py-1.5", activeAllergies.length ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700")}>
          {pluralize(activeAllergies.length, "Active Allergy", "Active Allergies")}
        </span>
      </div>

      {saved || state?.saved ? <p className="rounded-full bg-primary/10 px-4 py-2 text-sm font-semibold text-primary">Allergy saved</p> : null}
      {error ? <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">{error}</p> : null}

      <section className="min-w-0">
        <h2 className="text-base font-semibold">Your Allergies</h2>
        {status === "loading" ? <BrandedLoader compact label="Preparing allergies" /> : null}
        {status !== "loading" && sortedAllergies.length ? (
          <div className="mt-2 rounded-[1.5rem] bg-white px-4 shadow-soft">
            {sortedAllergies.map((record) => (
              <div key={record.allergy_id} className="border-b border-border/70 py-3.5 last:border-b-0">
                <Link
                  to={`/app/allergies/${encodeURIComponent(record.allergy_id)}/edit`}
                  className="group flex min-w-0 items-center gap-3 transition hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10 motion-reduce:transition-none"
                >
                  <span className={cn("grid size-10 shrink-0 place-items-center rounded-2xl", record.severity === "life_threatening" || record.severity === "severe" ? "bg-rose-50 text-rose-700" : "bg-blue-50 text-primary")}>
                    <AlertTriangle className="size-4" aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[0.95rem] font-semibold leading-5 text-foreground">{record.substance_name}</span>
                    <span className="mt-1 block truncate text-sm text-muted-foreground">
                      {[record.reaction, allergyCategoryLabel(record.category), clinicalStatusLabel(record.status)].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  <span
                    className={cn(
                      "shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold",
                      record.severity === "life_threatening" || record.severity === "severe" ? "bg-rose-50 text-rose-700" : "bg-secondary text-muted-foreground",
                    )}
                  >
                    {allergySeverityLabel(record.severity)}
                  </span>
                  <ArrowRight className="size-4 shrink-0 text-primary transition group-hover:translate-x-0.5 motion-reduce:transition-none" aria-hidden="true" />
                </Link>
                {record.status === "active" ? (
                  <button type="button" onClick={() => void updateAllergy(record.allergy_id, { status: "resolved" })} className="ml-[3.25rem] mt-2 inline-flex min-h-9 rounded-full border border-border bg-white px-3 py-2 text-xs font-semibold text-muted-foreground transition hover:border-primary/30 hover:text-foreground focus:outline-none focus:ring-4 focus:ring-primary/10">
                    Mark Resolved
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
        {status !== "loading" && !sortedAllergies.length ? (
          <div className="mt-2 rounded-[1.25rem] bg-white px-4 py-4 shadow-soft">
            <p className="text-sm leading-6 text-muted-foreground">No allergies added yet.</p>
            <Link to="/app/allergies/new" className="mt-3 inline-flex min-h-10 items-center justify-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
              <Plus className="size-4" aria-hidden="true" />
              Add Allergy
            </Link>
          </div>
        ) : null}
      </section>
    </div>
  );
}

export function AllergyEditorPage() {
  const navigate = useNavigate();
  const { allergyId } = useParams();
  const { allergies, createAllergy, status, updateAllergy } = useClinicalRecords();
  const selectedRecord = allergyId ? allergies.find((record) => record.allergy_id === allergyId) ?? null : null;
  const [draft, setDraft] = useState<AllergyDraft>(() => emptyAllergyDraft());
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedRecord) {
      setDraft(allergyDraftFromRecord(selectedRecord));
    }
  }, [selectedRecord]);

  const save = async () => {
    const { payload, error: nextValidationError } = allergyPayloadFromDraft(draft);
    if (nextValidationError) {
      setValidationError(nextValidationError);
      return;
    }

    const record = selectedRecord ? await updateAllergy(selectedRecord.allergy_id, payload) : await createAllergy(payload);
    if (record) {
      void navigate("/app/allergies", { replace: true, state: { saved: true } });
    }
  };

  return (
    <div className="space-y-6">
      <BackAction to="/app/allergies" label="Back to Allergies" />
      <WorkspacePageHeader eyebrow="Allergies" title={selectedRecord ? "Edit Allergy" : "Add Allergy"} body="Start with the details someone would need quickly." />
      {status === "loading" && allergyId ? <BrandedLoader compact label="Preparing allergy" /> : null}
      {allergyId && status !== "loading" && !selectedRecord ? (
        <EmptySection>We could not find that allergy record.</EmptySection>
      ) : (
        <AllergyForm draft={draft} onCancel={() => void navigate("/app/allergies")} onChange={setDraft} onSubmit={() => void save()} saving={status === "saving"} validationError={validationError} />
      )}
    </div>
  );
}

export function KnownHealthConditionsPage() {
  const location = useLocation();
  const { conditions, error, saved, status, updateCondition } = useClinicalRecords();
  const state = location.state as { saved?: boolean } | null;
  const activeConditions = useMemo(() => getActiveConditions(conditions), [conditions]);
  const sortedConditions = useMemo(
    () => [...conditions].sort((left, right) => (left.status === "active" ? -1 : 1) - (right.status === "active" ? -1 : 1) || left.condition_name.localeCompare(right.condition_name)),
    [conditions],
  );

  return (
    <div className="space-y-4 lg:space-y-5">
      <PageHero
        icon={HeartPulse}
        title="Known Health Conditions"
        description="Ongoing and past health conditions."
        action={
          <Link to="/app/known-health-conditions/new" className={heroActionClassName}>
            <Plus className="size-4" aria-hidden="true" />
            Add
          </Link>
        }
      />

      <div className="flex min-w-0 items-center gap-2 overflow-x-auto pb-1 text-sm font-semibold text-muted-foreground [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <span className="shrink-0 rounded-full bg-indigo-50 px-3 py-1.5 text-indigo-700">
          {pluralize(activeConditions.length, "Active Condition")}
        </span>
      </div>

      {saved || state?.saved ? <p className="rounded-full bg-primary/10 px-4 py-2 text-sm font-semibold text-primary">Condition saved</p> : null}
      {error ? <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">{error}</p> : null}

      <section className="min-w-0">
        <h2 className="text-base font-semibold">Your Conditions</h2>
        {status === "loading" ? <BrandedLoader compact label="Preparing known health conditions" /> : null}
        {status !== "loading" && sortedConditions.length ? (
          <div className="mt-2 rounded-[1.5rem] bg-white px-4 shadow-soft">
            {sortedConditions.map((record) => (
              <div key={record.condition_id} className="border-b border-border/70 py-3.5 last:border-b-0">
                <Link
                  to={`/app/known-health-conditions/${encodeURIComponent(record.condition_id)}/edit`}
                  className="group flex min-w-0 items-center gap-3 transition hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10 motion-reduce:transition-none"
                >
                  <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-indigo-50 text-indigo-700">
                    <HeartPulse className="size-4" aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[0.95rem] font-semibold leading-5 text-foreground">{record.condition_name}</span>
                    <span className="mt-1 block truncate text-sm text-muted-foreground">
                      {record.diagnosed_date ? `Diagnosed ${formatDate(record.diagnosed_date) ?? record.diagnosed_date}` : "Diagnosis date not added"}
                    </span>
                  </span>
                  <span className={cn("shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold", record.status === "active" ? "bg-indigo-50 text-indigo-700" : "bg-secondary text-muted-foreground")}>
                    {clinicalStatusLabel(record.status)}
                  </span>
                  <ArrowRight className="size-4 shrink-0 text-primary transition group-hover:translate-x-0.5 motion-reduce:transition-none" aria-hidden="true" />
                </Link>
                {record.status === "active" ? (
                  <button type="button" onClick={() => void updateCondition(record.condition_id, { status: "inactive" })} className="ml-[3.25rem] mt-2 inline-flex min-h-9 rounded-full border border-border bg-white px-3 py-2 text-xs font-semibold text-muted-foreground transition hover:border-primary/30 hover:text-foreground focus:outline-none focus:ring-4 focus:ring-primary/10">
                    Mark Inactive
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
        {status !== "loading" && !sortedConditions.length ? (
          <div className="mt-2 rounded-[1.25rem] bg-white px-4 py-4 shadow-soft">
            <p className="text-sm leading-6 text-muted-foreground">No known health conditions added yet.</p>
            <Link to="/app/known-health-conditions/new" className="mt-3 inline-flex min-h-10 items-center justify-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
              <Plus className="size-4" aria-hidden="true" />
              Add Condition
            </Link>
          </div>
        ) : null}
      </section>
    </div>
  );
}

export function ConditionEditorPage() {
  const navigate = useNavigate();
  const { conditionId } = useParams();
  const { conditions, createCondition, status, updateCondition } = useClinicalRecords();
  const selectedRecord = conditionId ? conditions.find((record) => record.condition_id === conditionId) ?? null : null;
  const [draft, setDraft] = useState<ConditionDraft>(() => emptyConditionDraft());
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedRecord) {
      setDraft(conditionDraftFromRecord(selectedRecord));
    }
  }, [selectedRecord]);

  const save = async () => {
    const { payload, error: nextValidationError } = conditionPayloadFromDraft(draft);
    if (nextValidationError) {
      setValidationError(nextValidationError);
      return;
    }

    const record = selectedRecord ? await updateCondition(selectedRecord.condition_id, payload) : await createCondition(payload);
    if (record) {
      void navigate("/app/known-health-conditions", { replace: true, state: { saved: true } });
    }
  };

  return (
    <div className="space-y-6">
      <BackAction to="/app/known-health-conditions" label="Back to Conditions" />
      <WorkspacePageHeader eyebrow="Known Health Conditions" title={selectedRecord ? "Edit Condition" : "Add Condition"} body="Keep the core clinical details easy to update." />
      {status === "loading" && conditionId ? <BrandedLoader compact label="Preparing condition" /> : null}
      {conditionId && status !== "loading" && !selectedRecord ? (
        <EmptySection>We could not find that condition record.</EmptySection>
      ) : (
        <ConditionForm draft={draft} onCancel={() => void navigate("/app/known-health-conditions")} onChange={setDraft} onSubmit={() => void save()} saving={status === "saving"} validationError={validationError} />
      )}
    </div>
  );
}

// The browser's detected IANA timezone -- the sensible starting value for any per-record
// reminder timezone picker (medication + vaccination forms). Falls back to UTC on the rare
// runtime that can't resolve one.
function localTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

type VaccinationDraft = {
  place_type: string;
  vaccine_name: string;
  dose_label: string;
  dose_number: string;
  series_name: string;
  series_total: string;
  administered_date: string;
  administered_time: string;
  cost_amount: string;
  cost_currency: string;
  payment_mode: string;
  notes: string;
  next_dose_due_date: string;
  next_dose_due_time: string;
  timezone: string;
  reminder_enabled: boolean;
};

function emptyVaccinationDraft(): VaccinationDraft {
  return {
    place_type: "",
    vaccine_name: "",
    dose_label: "",
    dose_number: "",
    series_name: "",
    series_total: "",
    administered_date: "",
    administered_time: "",
    cost_amount: "",
    cost_currency: "",
    payment_mode: "",
    notes: "",
    next_dose_due_date: "",
    next_dose_due_time: "",
    timezone: localTimezone(),
    reminder_enabled: false,
  };
}

function vaccinationDraftFromRecord(record: Vaccination): VaccinationDraft {
  return {
    place_type: "",
    vaccine_name: record.vaccine_name,
    dose_label: record.dose_label ?? "",
    dose_number: record.dose_number ? String(record.dose_number) : "",
    series_name: record.series_name ?? "",
    series_total: record.series_total ? String(record.series_total) : "",
    administered_date: record.administered_date,
    administered_time: record.administered_time ?? "",
    cost_amount: record.cost_amount ?? "",
    cost_currency: record.cost_currency ?? "",
    payment_mode: record.payment_mode ?? "",
    notes: record.notes ?? "",
    next_dose_due_date: record.next_dose_due_date ?? "",
    next_dose_due_time: record.next_dose_due_time ?? "",
    timezone: record.timezone ?? localTimezone(),
    reminder_enabled: record.reminder_enabled,
  };
}

function optionalText(value: string) {
  return value.trim() || null;
}

function optionalNumber(value: string) {
  const trimmed = value.trim();
  return trimmed ? Number(trimmed) : null;
}

function vaccinationPayloadFromDraft(draft: VaccinationDraft): { payload: VaccinationInput; error: string | null } {
  if (!draft.vaccine_name.trim()) {
    return { payload: emptyVaccinationPayload(), error: "Add the vaccine name before saving." };
  }

  if (!draft.administered_date) {
    return { payload: emptyVaccinationPayload(), error: "Add the vaccination date before saving." };
  }

  const today = new Date().toISOString().slice(0, 10);
  if (draft.administered_date > today) {
    return { payload: emptyVaccinationPayload(), error: "Vaccination date cannot be in the future." };
  }

  if (draft.dose_number && optionalNumber(draft.dose_number)! < 1) {
    return { payload: emptyVaccinationPayload(), error: "Dose number must be a positive whole number." };
  }

  if (draft.series_total && optionalNumber(draft.series_total)! < 1) {
    return { payload: emptyVaccinationPayload(), error: "Total doses in series must be a positive whole number." };
  }

  if (draft.cost_amount && !/^\d{1,10}(\.\d{1,2})?$/.test(draft.cost_amount.trim())) {
    return { payload: emptyVaccinationPayload(), error: "Cost must be a valid amount." };
  }

  if (draft.cost_currency && !/^[A-Za-z]{3}$/.test(draft.cost_currency.trim())) {
    return { payload: emptyVaccinationPayload(), error: "Currency must use a three-letter code, such as USD or INR." };
  }

  return {
    error: null,
    payload: {
      vaccine_name: draft.vaccine_name.trim(),
      dose_label: optionalText(draft.dose_label),
      dose_number: optionalNumber(draft.dose_number),
      series_name: optionalText(draft.series_name),
      series_total: optionalNumber(draft.series_total),
      administered_date: draft.administered_date,
      administered_time: draft.administered_time || null,
      cost_amount: optionalText(draft.cost_amount),
      cost_currency: optionalText(draft.cost_currency)?.toUpperCase() ?? null,
      payment_mode: optionalText(draft.payment_mode),
      notes: optionalText(draft.notes),
      next_dose_due_date: draft.next_dose_due_date || null,
      next_dose_due_time: draft.next_dose_due_time || null,
      timezone: draft.timezone.trim() || null,
      reminder_enabled: draft.reminder_enabled,
    },
  };
}

function emptyVaccinationPayload(): VaccinationInput {
  return {
    vaccine_name: "",
    dose_label: null,
    dose_number: null,
    series_name: null,
    series_total: null,
    administered_date: "",
    administered_time: null,
    cost_amount: null,
    cost_currency: null,
    payment_mode: null,
    notes: null,
    next_dose_due_date: null,
    next_dose_due_time: null,
    timezone: null,
    reminder_enabled: false,
  };
}

function doseSummary(record: Vaccination) {
  const parts = [record.dose_label, record.dose_number ? `Dose ${record.dose_number}` : null, record.series_total ? `of ${record.series_total}` : null].filter(Boolean);
  return parts.join(" ") || "Dose details not added";
}

function VaccinationDueCard({ record, status }: { record: Vaccination; status: "upcoming" | "overdue" }) {
  const toneClass = status === "overdue" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700";

  return (
    <Link
      to={`/app/vaccinations/${encodeURIComponent(record.vaccination_id)}`}
      className="group block border-b border-border/70 py-3.5 transition last:border-b-0 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10 motion-reduce:transition-none"
    >
      <span className="flex items-start justify-between gap-3">
        <span className="flex min-w-0 gap-3">
          <span className={cn("grid size-10 shrink-0 place-items-center rounded-2xl", toneClass)}>
            <Syringe className="size-5" aria-hidden="true" />
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-foreground">{record.vaccine_name}</span>
            <span className="mt-1 block text-sm text-muted-foreground">
              {[doseSummary(record), record.next_dose_due_date ? `Due ${formatDate(record.next_dose_due_date)}` : null].filter(Boolean).join(" · ")}
            </span>
          </span>
        </span>
        <span className={cn("shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold", toneClass)}>
          {status === "overdue" ? "Overdue" : "Upcoming"}
        </span>
        <ArrowRight className="mt-1 size-4 shrink-0 text-primary transition group-hover:translate-x-0.5 motion-reduce:transition-none" aria-hidden="true" />
      </span>
    </Link>
  );
}

function VaccinationHistoryRow({ record }: { record: Vaccination }) {
  return (
    <Link
      to={`/app/vaccinations/${encodeURIComponent(record.vaccination_id)}`}
      className="group flex min-w-0 items-center gap-3 border-b border-border/70 py-3.5 transition last:border-b-0 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10 motion-reduce:transition-none"
    >
      <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-blue-50 text-primary">
        <Syringe className="size-4" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[0.95rem] font-semibold leading-5 text-foreground">{record.vaccine_name}</span>
        <span className="mt-1 block truncate text-sm text-muted-foreground">
          {[formatDate(record.administered_date) ?? record.administered_date, record.dose_label || doseSummary(record)].filter(Boolean).join(" · ")}
        </span>
      </span>
      <ArrowRight className="size-4 shrink-0 text-primary transition group-hover:translate-x-0.5 motion-reduce:transition-none" aria-hidden="true" />
    </Link>
  );
}

function VaccinationDetail({ record, onEdit }: { record: Vaccination | null; onEdit: () => void }) {
  if (!record) {
    return (
      <article className="rounded-2xl border border-primary/10 bg-[linear-gradient(145deg,#061b43,#123d8b)] p-6 text-white shadow-lift">
        <Syringe className="size-8 text-blue-200" aria-hidden="true" />
        <h2 className="mt-5 text-base font-semibold">Select a vaccination</h2>
        <p className="mt-2 text-sm leading-6 text-white/72">
          Choose a record from your history to review dose, cost, next-dose date and reminder details.
        </p>
      </article>
    );
  }

  return (
    <article className="rounded-2xl border border-primary/10 bg-white p-6 shadow-premium">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-primary">Vaccination Detail</p>
          <h2 className="mt-2 text-xl font-semibold">{record.vaccine_name}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{doseSummary(record)}</p>
        </div>
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex min-h-10 items-center justify-center rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
        >
          Edit
        </button>
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <ProfileDetail label="Vaccination date" value={formatDate(record.administered_date)} />
        <ProfileDetail label="Time given" value={record.administered_time} />
        <ProfileDetail label="Vaccine series" value={record.series_name} />
        <ProfileDetail
          label="Cost"
          value={record.cost_amount ? `${record.cost_currency ?? ""} ${record.cost_amount}`.trim() : null}
        />
        <ProfileDetail label="Payment method" value={record.payment_mode} />
        <ProfileDetail label="Next dose date" value={formatDate(record.next_dose_due_date)} />
        <ProfileDetail label="Next dose time" value={record.next_dose_due_time} />
        <ProfileDetail label="Reminder timezone" value={record.timezone} />
        <ProfileDetail
          label="Next-dose reminder"
          value={
            record.reminder_enabled
              ? record.next_dose_due_date
                ? "On"
                : "On (add a next dose date to schedule it)"
              : "Off"
          }
        />
      </div>
      {record.notes ? (
        <div className="mt-4 rounded-2xl border border-border bg-surface px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Notes</p>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-foreground">{record.notes}</p>
        </div>
      ) : null}
    </article>
  );
}

function VaccinationFormSection({ children, title }: { children: ReactNode; title: string }) {
  return (
    <fieldset className="rounded-2xl border border-border bg-surface/70 p-4">
      <legend className="px-1 text-sm font-semibold text-foreground">{title}</legend>
      <div className="mt-3 grid gap-4 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}

function MoreDetails({ children, className, title = "More Details" }: { children: ReactNode; className?: string; title?: string }) {
  return (
    <details className={cn("group rounded-2xl border border-border bg-surface/70 p-4", className)}>
      <summary className="flex min-h-10 cursor-pointer list-none items-center justify-between gap-3 rounded-xl text-sm font-semibold text-foreground outline-none marker:hidden focus-visible:ring-4 focus-visible:ring-primary/10">
        {title}
        <ChevronDown className="size-4 shrink-0 text-primary transition group-open:rotate-180" aria-hidden="true" />
      </summary>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">{children}</div>
    </details>
  );
}

function VaccinationForm({
  draft,
  onCancel,
  onChange,
  onSubmit,
  saving,
  validationError,
}: {
  draft: VaccinationDraft;
  onCancel: () => void;
  onChange: (draft: VaccinationDraft) => void;
  onSubmit: () => void;
  saving: boolean;
  validationError: string | null;
}) {
  const update = <TKey extends keyof VaccinationDraft>(key: TKey, value: VaccinationDraft[TKey]) => {
    onChange({ ...draft, [key]: value });
  };

  return (
    <form
      className="rounded-2xl border border-primary/10 bg-white p-5 shadow-premium sm:p-6"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-base font-semibold">Vaccination record</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">Save details from your records or care instructions.</p>
        </div>
      </div>
      <div className="mt-5 grid gap-4">
        <VaccinationFormSection title="Basic Details">
          <label className="block min-w-0 text-sm font-semibold text-foreground sm:col-span-2">
            Vaccine Name
            <input
              value={draft.vaccine_name}
              onChange={(event) => update("vaccine_name", event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              maxLength={160}
              required
              disabled={saving}
            />
          </label>
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Date Given
            <input
              type="date"
              value={draft.administered_date}
              onChange={(event) => update("administered_date", event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              max={new Date().toISOString().slice(0, 10)}
              required
              disabled={saving}
            />
          </label>
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Dose <span className="font-normal text-muted-foreground">(optional)</span>
            <input
              value={draft.dose_label}
              onChange={(event) => update("dose_label", event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              maxLength={160}
              disabled={saving}
            />
          </label>
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Dose Number <span className="font-normal text-muted-foreground">(optional)</span>
            <input
              type="number"
              min="1"
              value={draft.dose_number}
              onChange={(event) => update("dose_number", event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              disabled={saving}
            />
          </label>
        </VaccinationFormSection>

        <fieldset className="rounded-2xl border border-border bg-surface/70 p-4">
          <legend className="px-1 text-sm font-semibold text-foreground">Next dose reminder</legend>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            When the next dose is due, and whether MedoPilot should remind you.
          </p>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <label className="block min-w-0 text-sm font-semibold text-foreground">
              Next dose date <span className="font-normal text-muted-foreground">(optional)</span>
              <input
                type="date"
                value={draft.next_dose_due_date}
                onChange={(event) => update("next_dose_due_date", event.target.value)}
                className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                disabled={saving}
              />
            </label>
            <label className="block min-w-0 text-sm font-semibold text-foreground">
              Next dose time <span className="font-normal text-muted-foreground">(optional)</span>
              <input
                type="time"
                value={draft.next_dose_due_time}
                onChange={(event) => update("next_dose_due_time", event.target.value)}
                className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                disabled={saving}
              />
            </label>
            <label className="block min-w-0 text-sm font-semibold text-foreground sm:col-span-2">
              Reminder timezone
              <TimezoneCombobox
                value={draft.timezone}
                onChange={(zone) => update("timezone", zone)}
                disabled={saving}
                label="Reminder timezone"
              />
              <span className="mt-2 block text-xs font-normal leading-5 text-muted-foreground">
                Used to schedule the reminder at the right local time.
              </span>
            </label>
            <label className="flex min-w-0 items-start gap-3 rounded-2xl border border-border bg-white px-4 py-3 text-sm font-semibold text-foreground sm:col-span-2">
              <input
                type="checkbox"
                checked={draft.reminder_enabled}
                onChange={(event) => update("reminder_enabled", event.target.checked)}
                className="mt-1 size-4 accent-primary"
                disabled={saving}
              />
              <span>
                Remind me when the next dose is due
                <span className="mt-1 block text-xs font-normal leading-5 text-muted-foreground">
                  Needs a due date above. You can change this any time.
                </span>
              </span>
            </label>
          </div>
        </fieldset>

        <MoreDetails>
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Time Given <span className="font-normal text-muted-foreground">(optional)</span>
            <input
              type="time"
              value={draft.administered_time}
              onChange={(event) => update("administered_time", event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              disabled={saving}
            />
          </label>
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Vaccine Series <span className="font-normal text-muted-foreground">(optional)</span>
            <input
              value={draft.series_name}
              onChange={(event) => update("series_name", event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              maxLength={160}
              disabled={saving}
            />
          </label>
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Total Doses In Series <span className="font-normal text-muted-foreground">(optional)</span>
            <input
              type="number"
              min="1"
              value={draft.series_total}
              onChange={(event) => update("series_total", event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              disabled={saving}
            />
          </label>

          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Place Type <span className="font-normal text-muted-foreground">(optional)</span>
            <select
              value={draft.place_type}
              onChange={(event) => update("place_type", event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              disabled={saving}
            >
              {placeTypeOptions.map((option) => (
                <option key={option.value || "blank"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Cost <span className="font-normal text-muted-foreground">(optional)</span>
            <input
              inputMode="decimal"
              value={draft.cost_amount}
              onChange={(event) => update("cost_amount", event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              disabled={saving}
            />
          </label>
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Currency <span className="font-normal text-muted-foreground">(optional)</span>
            <select
              value={draft.cost_currency}
              onChange={(event) => update("cost_currency", event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              disabled={saving}
            >
              {draft.cost_currency && !currencyOptions.some((option) => option.value === draft.cost_currency) ? (
                <option value={draft.cost_currency}>{draft.cost_currency}</option>
              ) : null}
              {currencyOptions.map((option) => (
                <option key={option.value || "blank"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Payment Method <span className="font-normal text-muted-foreground">(optional)</span>
            <select
              value={draft.payment_mode}
              onChange={(event) => update("payment_mode", event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              disabled={saving}
            >
              {draft.payment_mode && !paymentModeOptions.some((option) => option.value === draft.payment_mode) ? (
                <option value={draft.payment_mode}>{draft.payment_mode}</option>
              ) : null}
              {paymentModeOptions.map((option) => (
                <option key={option.value || "blank"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block min-w-0 text-sm font-semibold text-foreground sm:col-span-2">
            Notes <span className="font-normal text-muted-foreground">(optional)</span>
            <textarea
              value={draft.notes}
              onChange={(event) => update("notes", event.target.value)}
              className="mt-2 min-h-24 w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              maxLength={2000}
              disabled={saving}
            />
          </label>
        </MoreDetails>
      </div>
      {validationError ? (
        <p className="mt-4 rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">
          {validationError}
        </p>
      ) : null}
      <div className="mt-5 flex flex-col gap-3 sm:flex-row">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex min-h-10 items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? "Saving" : "Save"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="inline-flex min-h-10 items-center justify-center rounded-full border border-border bg-white px-5 py-2.5 text-sm font-semibold text-muted-foreground transition hover:border-primary/30 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export function VaccinationsPage() {
  const location = useLocation();
  const { error, status, vaccinations } = useVaccinations();
  const latestVaccinations = useMemo(() => getLatestVaccinations(vaccinations), [vaccinations]);
  const upcomingVaccinations = useMemo(() => getUpcomingVaccinations(vaccinations), [vaccinations]);
  const overdueVaccinations = useMemo(() => getOverdueVaccinations(vaccinations), [vaccinations]);
  const state = location.state as { saved?: boolean } | null;
  const dueVaccinations = [...overdueVaccinations, ...upcomingVaccinations].slice(0, 5);

  return (
    <div className="space-y-4 lg:space-y-5">
      <PageHero
        icon={Syringe}
        title="Vaccinations"
        description="Vaccination records and upcoming doses."
        action={
          <Link to="/app/vaccinations/new" className={heroActionClassName}>
            <Plus className="size-4" aria-hidden="true" />
            Add
          </Link>
        }
      />

      <div className="flex min-w-0 items-center gap-2 overflow-x-auto pb-1 text-sm font-semibold text-muted-foreground [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <span className="shrink-0 rounded-full bg-amber-50 px-3 py-1.5 text-amber-700">{pluralize(upcomingVaccinations.length, "Upcoming", "Upcoming")}</span>
        <span className="shrink-0 text-border" aria-hidden="true">·</span>
        <span className={cn("shrink-0 rounded-full px-3 py-1.5", overdueVaccinations.length ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700")}>{pluralize(overdueVaccinations.length, "Overdue", "Overdue")}</span>
      </div>

      {state?.saved ? <p className="rounded-full bg-primary/10 px-4 py-2 text-sm font-semibold text-primary">Vaccination saved</p> : null}
      {error ? (
        <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">
          {error}
        </p>
      ) : null}

      {status === "loading" ? (
        <BrandedLoader compact label="Preparing vaccination records" />
      ) : (
        <div className="space-y-5">
          <section className="min-w-0">
            <h2 className="text-base font-semibold">Needs Attention</h2>
            {dueVaccinations.length ? (
              <div className="mt-2 rounded-[1.5rem] bg-white px-4 shadow-soft">
                {dueVaccinations.map((record) => (
                  <VaccinationDueCard
                    key={record.vaccination_id}
                    record={record}
                    status={overdueVaccinations.some((item) => item.vaccination_id === record.vaccination_id) ? "overdue" : "upcoming"}
                  />
                ))}
              </div>
            ) : (
              <p className="mt-2 rounded-[1.25rem] bg-white px-4 py-3 text-sm leading-6 text-muted-foreground shadow-soft">No vaccine doses need attention right now.</p>
            )}
          </section>

          <section className="min-w-0">
            <h2 className="text-base font-semibold">Vaccination History</h2>
            {latestVaccinations.length ? (
              <div className="mt-2 rounded-[1.5rem] bg-white px-4 shadow-soft">
                {latestVaccinations.map((record) => <VaccinationHistoryRow key={record.vaccination_id} record={record} />)}
              </div>
            ) : (
              <div className="mt-2 rounded-[1.25rem] bg-white px-4 py-4 shadow-soft">
                <p className="text-sm leading-6 text-muted-foreground">No vaccinations added yet.</p>
                <Link to="/app/vaccinations/new" className="mt-3 inline-flex min-h-10 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground">
                  <Plus className="size-4" aria-hidden="true" />
                  Add Vaccination
                </Link>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

export function VaccinationNewPage() {
  const navigate = useNavigate();
  const { createVaccination, status } = useVaccinations();
  const [draft, setDraft] = useState<VaccinationDraft>(emptyVaccinationDraft);
  const [validationError, setValidationError] = useState<string | null>(null);

  const submitForm = async () => {
    if (status === "saving") {
      return;
    }

    const { payload, error: nextValidationError } = vaccinationPayloadFromDraft(draft);
    if (nextValidationError) {
      setValidationError(nextValidationError);
      return;
    }

    setValidationError(null);
    const record = await createVaccination(payload);
    if (record) {
      void navigate(`/app/vaccinations/${encodeURIComponent(record.vaccination_id)}`, { replace: true, state: { saved: true } });
    }
  };

  return (
    <div className="space-y-6">
      <BackAction to="/app/vaccinations" label="Back to Vaccinations" />
      <WorkspacePageHeader
        eyebrow="Vaccinations"
        title="Add Vaccination"
        body="Save details from your records or care instructions."
      />
      <VaccinationForm
        draft={draft}
        onCancel={() => void navigate("/app/vaccinations")}
        onChange={setDraft}
        onSubmit={() => void submitForm()}
        saving={status === "saving"}
        validationError={validationError}
      />
    </div>
  );
}

export function VaccinationDetailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { vaccinationId } = useParams();
  const { error, status, vaccinations } = useVaccinations();
  const selectedRecord = vaccinations.find((record) => record.vaccination_id === vaccinationId) ?? null;
  const state = location.state as { saved?: boolean } | null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <WorkspacePageHeader
          eyebrow="Vaccinations"
          title="Vaccination Detail"
          body="Review the details saved for this vaccination record."
        />
        <BackAction to="/app/vaccinations" label="Back to Vaccinations" />
      </div>
      {state?.saved ? <p className="rounded-full bg-primary/10 px-4 py-2 text-sm font-semibold text-primary">Vaccination saved</p> : null}
      {error ? (
        <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">
          {error}
        </p>
      ) : null}
      {status === "loading" ? (
        <BrandedLoader compact label="Preparing vaccination record" />
      ) : selectedRecord ? (
        <VaccinationDetail record={selectedRecord} onEdit={() => void navigate(`/app/vaccinations/${encodeURIComponent(selectedRecord.vaccination_id)}/edit`)} />
      ) : (
        <article className="rounded-2xl border border-primary/10 bg-white p-6 shadow-soft">
          <h2 className="text-base font-semibold">Vaccination not found</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">We could not find that vaccination record in your Care Hub.</p>
        </article>
      )}
    </div>
  );
}

export function VaccinationEditPage() {
  const navigate = useNavigate();
  const { vaccinationId } = useParams();
  const { status, updateVaccination, vaccinations } = useVaccinations();
  const selectedRecord = vaccinations.find((record) => record.vaccination_id === vaccinationId) ?? null;
  const [draft, setDraft] = useState<VaccinationDraft>(emptyVaccinationDraft);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedRecord) {
      setDraft(vaccinationDraftFromRecord(selectedRecord));
    }
  }, [selectedRecord]);

  const submitForm = async () => {
    if (!selectedRecord || status === "saving") {
      return;
    }

    const { payload, error: nextValidationError } = vaccinationPayloadFromDraft(draft);
    if (nextValidationError) {
      setValidationError(nextValidationError);
      return;
    }

    setValidationError(null);
    const record = await updateVaccination(selectedRecord.vaccination_id, payload);
    if (record) {
      void navigate(`/app/vaccinations/${encodeURIComponent(record.vaccination_id)}`, { replace: true, state: { saved: true } });
    }
  };

  return (
    <div className="space-y-6">
      <BackAction to="/app/vaccinations" label="Back to Vaccinations" />
      <WorkspacePageHeader
        eyebrow="Vaccinations"
        title="Edit vaccination"
        body="Update this vaccination record using details from your records or care instructions."
      />
      {status === "loading" ? (
        <BrandedLoader compact label="Preparing vaccination record" />
      ) : selectedRecord ? (
        <VaccinationForm
          draft={draft}
          onCancel={() => void navigate(`/app/vaccinations/${encodeURIComponent(selectedRecord.vaccination_id)}`)}
          onChange={setDraft}
          onSubmit={() => void submitForm()}
          saving={status === "saving"}
          validationError={validationError}
        />
      ) : (
        <article className="rounded-2xl border border-primary/10 bg-white p-6 shadow-soft">
          <h2 className="text-base font-semibold">Vaccination not found</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">We could not find that vaccination record in your Care Hub.</p>
        </article>
      )}
    </div>
  );
}

function AppointmentStatusPill({ status }: { status: AppointmentStatus }) {
  const appearance = {
    archived: { label: "Archived", className: "bg-surface text-muted-foreground" },
    cancelled: { label: "Cancelled", className: "bg-rose-50 text-rose-700" },
    scheduled: { label: "Scheduled", className: "bg-blue-50 text-primary" },
  }[status];

  return <span className={cn("shrink-0 rounded-full px-3 py-1 text-xs font-semibold", appearance.className)}>{appearance.label}</span>;
}

function AppointmentRow({ record }: { record: Appointment }) {
  return (
    <Link
      to={`/app/appointments/${encodeURIComponent(record.appointment_id)}`}
      className="group flex min-w-0 items-center gap-3 border-b border-border/70 py-3.5 transition last:border-b-0 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10 motion-reduce:transition-none"
    >
      <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-blue-50 text-primary">
        <Stethoscope className="size-4" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[0.95rem] font-semibold leading-5 text-foreground">{record.title}</span>
        <span className="mt-1 block truncate text-sm text-muted-foreground">
          {[appointmentTypeLabel(record.appointment_type), formatDate(record.scheduled_date), formatTime(record.scheduled_time)].filter(Boolean).join(" · ")}
        </span>
      </span>
      <AppointmentStatusPill status={record.status} />
      <ArrowRight className="size-4 shrink-0 text-primary transition group-hover:translate-x-0.5 motion-reduce:transition-none" aria-hidden="true" />
    </Link>
  );
}

// A disclosure header for a collapsible appointments section -- the heading itself is the
// clickable control (WAI-ARIA disclosure pattern: <button> nested in the heading element), rather
// than a separate toggle beside a static heading, so the count-bearing label ("Past (3)") is what
// the user clicks. Collapsed by default; expanding/collapsing only ever shows or hides this one
// section's own rows, never affecting Upcoming or the other collapsible section.
function CollapsibleAppointmentSection({
  children,
  controlsId,
  count,
  expanded,
  onToggle,
  title,
}: {
  children: ReactNode;
  controlsId: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  title: string;
}) {
  return (
    <section className="min-w-0">
      <h2 className="text-base font-semibold">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={controlsId}
          className="flex min-h-9 w-full items-center justify-between gap-2 rounded-xl text-left outline-none focus-visible:ring-4 focus-visible:ring-primary/10"
        >
          <span>
            {title} <span className="font-normal text-muted-foreground">({count})</span>
          </span>
          <ChevronDown
            className={cn("size-4 shrink-0 text-muted-foreground transition motion-reduce:transition-none", expanded ? "rotate-180" : "")}
            aria-hidden="true"
          />
        </button>
      </h2>
      {expanded ? (
        <div id={controlsId} className="mt-2">
          {children}
        </div>
      ) : null}
    </section>
  );
}

export function AppointmentsPage() {
  const { appointments, error, status } = useAppointments();
  const upcoming = useMemo(() => getUpcomingAppointments(appointments), [appointments]);
  const past = useMemo(() => getPastAppointments(appointments), [appointments]);
  const archived = useMemo(() => getArchivedAppointments(appointments), [appointments]);
  const [pastExpanded, setPastExpanded] = useState(false);
  const [archivedExpanded, setArchivedExpanded] = useState(false);

  return (
    <div className="space-y-4 lg:space-y-5">
      <PageHero
        icon={Stethoscope}
        title="Appointments"
        description="Upcoming and past healthcare appointments."
        action={
          <Link to="/app/appointments/new" className={heroActionClassName}>
            <Plus className="size-4" aria-hidden="true" />
            Add
          </Link>
        }
      />

      {error ? (
        <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">{error}</p>
      ) : null}

      {status === "loading" ? (
        <BrandedLoader compact label="Preparing appointments" />
      ) : (
        <div className="space-y-5">
          <section className="min-w-0">
            <h2 className="text-base font-semibold">Upcoming</h2>
            {upcoming.length ? (
              <div className="mt-2 rounded-[1.5rem] bg-white px-4 shadow-soft">
                {upcoming.map((record) => (
                  <AppointmentRow key={record.appointment_id} record={record} />
                ))}
              </div>
            ) : (
              <div className="mt-2 rounded-[1.25rem] bg-white px-4 py-4 shadow-soft">
                <p className="text-sm leading-6 text-muted-foreground">No upcoming appointments.</p>
                <Link
                  to="/app/appointments/new"
                  className="mt-3 inline-flex min-h-10 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground"
                >
                  <Plus className="size-4" aria-hidden="true" />
                  Add Appointment
                </Link>
              </div>
            )}
          </section>

          <CollapsibleAppointmentSection
            controlsId="appointments-past"
            count={past.length}
            expanded={pastExpanded}
            onToggle={() => setPastExpanded((expanded) => !expanded)}
            title="Past"
          >
            {past.length ? (
              <div className="rounded-[1.5rem] bg-white px-4 shadow-soft">
                {past.map((record) => (
                  <AppointmentRow key={record.appointment_id} record={record} />
                ))}
              </div>
            ) : (
              <p className="rounded-[1.25rem] bg-white px-4 py-3 text-sm leading-6 text-muted-foreground shadow-soft">No past appointments yet.</p>
            )}
          </CollapsibleAppointmentSection>

          {archived.length ? (
            <CollapsibleAppointmentSection
              controlsId="appointments-archived"
              count={archived.length}
              expanded={archivedExpanded}
              onToggle={() => setArchivedExpanded((expanded) => !expanded)}
              title="Archived"
            >
              <div className="rounded-[1.5rem] bg-white px-4 shadow-soft">
                {archived.map((record) => (
                  <AppointmentRow key={record.appointment_id} record={record} />
                ))}
              </div>
            </CollapsibleAppointmentSection>
          ) : null}
        </div>
      )}
    </div>
  );
}

type AppointmentDraft = {
  appointment_type: AppointmentType;
  title: string;
  scheduled_date: string;
  scheduled_time: string;
  timezone: string;
  provider_name: string;
  facility_name: string;
  mode: AppointmentMode;
  location: string;
  notes: string;
  reminder_enabled: boolean;
  extra_reminder: ExtraReminderOption | "";
};

function emptyAppointmentDraft(): AppointmentDraft {
  return {
    appointment_type: "doctor_visit",
    title: "",
    scheduled_date: "",
    scheduled_time: "",
    timezone: localTimezone(),
    provider_name: "",
    facility_name: "",
    mode: "in_person",
    location: "",
    notes: "",
    reminder_enabled: true,
    // New appointments default to an advance reminder 1 day before, on top of the always-available
    // appointment-time reminder above -- the user can change or clear it before saving.
    extra_reminder: "1_day_before",
  };
}

function appointmentDraftFromRecord(record: Appointment): AppointmentDraft {
  return {
    appointment_type: record.appointment_type,
    title: record.title,
    scheduled_date: record.scheduled_date,
    scheduled_time: record.scheduled_time,
    timezone: record.timezone || localTimezone(),
    provider_name: record.provider_name ?? "",
    facility_name: record.facility_name ?? "",
    mode: record.mode,
    location: record.location ?? "",
    notes: record.notes ?? "",
    reminder_enabled: record.reminder_enabled,
    extra_reminder: record.extra_reminder ?? "",
  };
}

function emptyAppointmentPayload(): AppointmentInput {
  return {
    appointment_type: "other",
    title: "",
    scheduled_date: "",
    scheduled_time: "",
    timezone: "UTC",
    provider_name: null,
    facility_name: null,
    mode: "in_person",
    location: null,
    notes: null,
    reminder_enabled: true,
    extra_reminder: null,
  };
}

function appointmentPayloadFromDraft(draft: AppointmentDraft): { payload: AppointmentInput; error: string | null } {
  if (!draft.title.trim()) {
    return { payload: emptyAppointmentPayload(), error: "Add a title before saving." };
  }

  if (!draft.scheduled_date) {
    return { payload: emptyAppointmentPayload(), error: "Add the appointment date before saving." };
  }

  if (!draft.scheduled_time) {
    return { payload: emptyAppointmentPayload(), error: "Add the appointment time before saving." };
  }

  if (!draft.timezone) {
    return { payload: emptyAppointmentPayload(), error: "Add a timezone before saving." };
  }

  return {
    payload: {
      appointment_type: draft.appointment_type,
      title: draft.title.trim(),
      scheduled_date: draft.scheduled_date,
      scheduled_time: draft.scheduled_time,
      timezone: draft.timezone,
      provider_name: optionalText(draft.provider_name),
      facility_name: optionalText(draft.facility_name),
      mode: draft.mode,
      location: optionalText(draft.location),
      notes: optionalText(draft.notes),
      reminder_enabled: draft.reminder_enabled,
      extra_reminder: draft.extra_reminder || null,
    },
    error: null,
  };
}

function AppointmentForm({
  draft,
  onCancel,
  onChange,
  onSubmit,
  saving,
  validationError,
}: {
  draft: AppointmentDraft;
  onCancel: () => void;
  onChange: (draft: AppointmentDraft) => void;
  onSubmit: () => void;
  saving: boolean;
  validationError: string | null;
}) {
  const update = <TKey extends keyof AppointmentDraft>(key: TKey, value: AppointmentDraft[TKey]) => {
    onChange({ ...draft, [key]: value });
  };

  return (
    <form
      className="rounded-2xl border border-primary/10 bg-white p-5 shadow-premium sm:p-6"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div className="grid gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Appointment Type
            <select
              value={draft.appointment_type}
              onChange={(event) => update("appointment_type", event.target.value as AppointmentType)}
              className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              disabled={saving}
            >
              {appointmentTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Title
            <input
              value={draft.title}
              onChange={(event) => update("title", event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              maxLength={160}
              placeholder="e.g. Dr. Smith — annual checkup"
              required
              disabled={saving}
            />
          </label>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Date
            <input
              type="date"
              value={draft.scheduled_date}
              onChange={(event) => update("scheduled_date", event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              required
              disabled={saving}
            />
          </label>
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Time
            <input
              type="time"
              value={draft.scheduled_time}
              onChange={(event) => update("scheduled_time", event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              required
              disabled={saving}
            />
          </label>
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Timezone
            <TimezoneCombobox value={draft.timezone} onChange={(zone) => update("timezone", zone)} disabled={saving} label="Appointment timezone" />
          </label>
        </div>

        <fieldset className="rounded-2xl border border-border bg-surface/70 p-4">
          <legend className="px-1 text-sm font-semibold text-foreground">Reminders</legend>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <label className="flex min-w-0 items-start gap-3 rounded-2xl border border-border bg-white px-4 py-3 text-sm font-semibold text-foreground sm:col-span-2">
              <input
                type="checkbox"
                checked={draft.reminder_enabled}
                onChange={(event) => update("reminder_enabled", event.target.checked)}
                className="mt-1 size-4 accent-primary"
                disabled={saving}
              />
              <span>
                Remind me at the appointment time
                <span className="mt-1 block text-xs font-normal leading-5 text-muted-foreground">Sent on {formatDate(draft.scheduled_date) || "the scheduled date"} at {formatTime(draft.scheduled_time) || "the scheduled time"}.</span>
              </span>
            </label>
            <label className="block min-w-0 text-sm font-semibold text-foreground sm:col-span-2">
              Advance reminder <span className="font-normal text-muted-foreground">(optional)</span>
              <select
                value={draft.extra_reminder}
                onChange={(event) => update("extra_reminder", event.target.value as ExtraReminderOption | "")}
                className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                disabled={saving}
              >
                {extraReminderOptions.map((option) => (
                  <option key={option.value || "none"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </fieldset>

        <MoreDetails>
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Healthcare Professional <span className="font-normal text-muted-foreground">(optional)</span>
            <input
              value={draft.provider_name}
              onChange={(event) => update("provider_name", event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              maxLength={160}
              disabled={saving}
            />
          </label>
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Facility <span className="font-normal text-muted-foreground">(optional)</span>
            <input
              value={draft.facility_name}
              onChange={(event) => update("facility_name", event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              maxLength={160}
              disabled={saving}
            />
          </label>
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Mode
            <select
              value={draft.mode}
              onChange={(event) => update("mode", event.target.value as AppointmentMode)}
              className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              disabled={saving}
            >
              {appointmentModeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Location <span className="font-normal text-muted-foreground">(optional)</span>
            <input
              value={draft.location}
              onChange={(event) => update("location", event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              maxLength={240}
              placeholder="Address or video link"
              disabled={saving}
            />
          </label>
          <label className="block min-w-0 text-sm font-semibold text-foreground sm:col-span-2">
            Notes <span className="font-normal text-muted-foreground">(optional)</span>
            <textarea
              value={draft.notes}
              onChange={(event) => update("notes", event.target.value)}
              className="mt-2 min-h-24 w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              maxLength={2000}
              disabled={saving}
            />
          </label>
        </MoreDetails>
      </div>

      {validationError ? (
        <p className="mt-4 rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">{validationError}</p>
      ) : null}

      <div className="mt-5 flex flex-col gap-3 sm:flex-row">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex min-h-10 items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? "Saving" : "Save"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="inline-flex min-h-10 items-center justify-center rounded-full border border-border bg-white px-5 py-2.5 text-sm font-semibold text-muted-foreground transition hover:border-primary/30 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export function AppointmentNewPage() {
  const navigate = useNavigate();
  const { createAppointment, status } = useAppointments();
  const [draft, setDraft] = useState<AppointmentDraft>(emptyAppointmentDraft);
  const [validationError, setValidationError] = useState<string | null>(null);

  const submitForm = async () => {
    if (status === "saving") {
      return;
    }

    const { payload, error: nextValidationError } = appointmentPayloadFromDraft(draft);
    if (nextValidationError) {
      setValidationError(nextValidationError);
      return;
    }

    setValidationError(null);
    const record = await createAppointment(payload);
    if (record) {
      void navigate(`/app/appointments/${encodeURIComponent(record.appointment_id)}`, { replace: true, state: { saved: true } });
    }
  };

  return (
    <div className="space-y-6">
      <BackAction to="/app/appointments" label="Back to Appointments" />
      <WorkspacePageHeader eyebrow="Appointments" title="Add Appointment" body="Save the date, time and details for a healthcare appointment." />
      <AppointmentForm
        draft={draft}
        onCancel={() => void navigate("/app/appointments")}
        onChange={setDraft}
        onSubmit={() => void submitForm()}
        saving={status === "saving"}
        validationError={validationError}
      />
    </div>
  );
}

function AppointmentDetail({
  record,
  onArchive,
  onCancel,
  onEdit,
}: {
  record: Appointment;
  onArchive: () => void;
  onCancel: () => void;
  onEdit: () => void;
}) {
  const reminderSummary = record.reminder_enabled
    ? ["At appointment time", record.extra_reminder ? extraReminderLabel(record.extra_reminder) : null].filter(Boolean).join(" + ")
    : "Off";

  return (
    <article className="rounded-2xl border border-primary/10 bg-white p-6 shadow-premium">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-primary">{appointmentTypeLabel(record.appointment_type)}</p>
          <h2 className="mt-2 text-xl font-semibold">{record.title}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{[formatDate(record.scheduled_date), formatTime(record.scheduled_time)].filter(Boolean).join(" · ")}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <AppointmentStatusPill status={record.status} />
          <button type="button" onClick={onEdit} className="inline-flex min-h-10 items-center justify-center rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
            Edit
          </button>
        </div>
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <ProfileDetail label="Timezone" value={record.timezone ? formatTimezoneLabel(record.timezone) : null} />
        <ProfileDetail label="Mode" value={appointmentModeLabel(record.mode)} />
        <ProfileDetail label="Healthcare professional" value={record.provider_name} />
        <ProfileDetail label="Facility" value={record.facility_name} />
        <ProfileDetail label="Location" value={record.location} />
        <ProfileDetail label="Reminders" value={reminderSummary} />
      </div>
      {record.notes ? (
        <div className="mt-4 rounded-2xl border border-border bg-surface px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Notes</p>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-foreground">{record.notes}</p>
        </div>
      ) : null}
      {record.status !== "archived" ? (
        <div className="mt-5 flex flex-wrap gap-3">
          {record.status === "scheduled" ? (
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex min-h-10 items-center justify-center rounded-full border border-destructive/30 px-4 text-sm font-semibold text-destructive transition hover:bg-destructive/5"
            >
              Cancel Appointment
            </button>
          ) : null}
          <button
            type="button"
            onClick={onArchive}
            className="inline-flex min-h-10 items-center justify-center rounded-full border border-border px-4 text-sm font-semibold text-muted-foreground transition hover:border-primary/30 hover:text-foreground"
          >
            Archive
          </button>
        </div>
      ) : null}
    </article>
  );
}

export function AppointmentDetailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { appointmentId } = useParams();
  const { appointments, archiveAppointment, cancelAppointment, error, status } = useAppointments();
  const selectedRecord = appointments.find((record) => record.appointment_id === appointmentId) ?? null;
  const state = location.state as { saved?: boolean } | null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <WorkspacePageHeader eyebrow="Appointments" title="Appointment Detail" body="Review the details saved for this appointment." />
        <BackAction to="/app/appointments" label="Back to Appointments" />
      </div>
      {state?.saved ? <p className="rounded-full bg-primary/10 px-4 py-2 text-sm font-semibold text-primary">Appointment saved</p> : null}
      {error ? (
        <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">{error}</p>
      ) : null}
      {status === "loading" ? (
        <BrandedLoader compact label="Preparing appointment" />
      ) : selectedRecord ? (
        <AppointmentDetail
          record={selectedRecord}
          onArchive={() => void archiveAppointment(selectedRecord.appointment_id)}
          onCancel={() => void cancelAppointment(selectedRecord.appointment_id)}
          onEdit={() => void navigate(`/app/appointments/${encodeURIComponent(selectedRecord.appointment_id)}/edit`)}
        />
      ) : (
        <article className="rounded-2xl border border-primary/10 bg-white p-6 shadow-soft">
          <h2 className="text-base font-semibold">Appointment not found</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">We could not find that appointment in your Care Hub.</p>
        </article>
      )}
    </div>
  );
}

export function AppointmentEditPage() {
  const navigate = useNavigate();
  const { appointmentId } = useParams();
  const { appointments, status, updateAppointment } = useAppointments();
  const selectedRecord = appointments.find((record) => record.appointment_id === appointmentId) ?? null;
  const [draft, setDraft] = useState<AppointmentDraft>(emptyAppointmentDraft);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedRecord) {
      setDraft(appointmentDraftFromRecord(selectedRecord));
    }
  }, [selectedRecord]);

  const submitForm = async () => {
    if (!selectedRecord || status === "saving") {
      return;
    }

    const { payload, error: nextValidationError } = appointmentPayloadFromDraft(draft);
    if (nextValidationError) {
      setValidationError(nextValidationError);
      return;
    }

    setValidationError(null);
    const record = await updateAppointment(selectedRecord.appointment_id, payload);
    if (record) {
      void navigate(`/app/appointments/${encodeURIComponent(record.appointment_id)}`, { replace: true, state: { saved: true } });
    }
  };

  return (
    <div className="space-y-6">
      <BackAction to="/app/appointments" label="Back to Appointments" />
      <WorkspacePageHeader eyebrow="Appointments" title="Edit Appointment" body="Update the details saved for this appointment." />
      {status === "loading" ? (
        <BrandedLoader compact label="Preparing appointment" />
      ) : selectedRecord ? (
        <AppointmentForm
          draft={draft}
          onCancel={() => void navigate(`/app/appointments/${encodeURIComponent(selectedRecord.appointment_id)}`)}
          onChange={setDraft}
          onSubmit={() => void submitForm()}
          saving={status === "saving"}
          validationError={validationError}
        />
      ) : (
        <article className="rounded-2xl border border-primary/10 bg-white p-6 shadow-soft">
          <h2 className="text-base font-semibold">Appointment not found</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">We could not find that appointment in your Care Hub.</p>
        </article>
      )}
    </div>
  );
}

function MedicineOrderStatusPill({ status }: { status: MedicineOrderStatus }) {
  // "requested" is the only status this milestone ever produces -- see medopilot-medicine-order-
  // service. The label and every surrounding copy on this page must stay truthful to that: never
  // implying pharmacy acceptance, pricing, payment, processing, or shipment.
  return <span className="shrink-0 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">{status === "requested" ? "Requested" : status}</span>;
}

function MedicineOrderRow({ order }: { order: MedicineOrder }) {
  return (
    <Link
      to={`/app/orders/${encodeURIComponent(order.order_id)}`}
      className="group flex min-w-0 items-center gap-3 border-b border-border/70 py-3.5 transition last:border-b-0 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10 motion-reduce:transition-none"
    >
      <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-amber-50 text-amber-700">
        <ShoppingCart className="size-4" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[0.95rem] font-semibold leading-5 text-foreground">{order.order_reference}</span>
        <span className="mt-1 block truncate text-sm text-muted-foreground">
          {[formatDateTime(order.created_at), order.document_filename].filter(Boolean).join(" · ")}
        </span>
      </span>
      <MedicineOrderStatusPill status={order.status} />
      <ArrowRight className="size-4 shrink-0 text-primary transition group-hover:translate-x-0.5 motion-reduce:transition-none" aria-hidden="true" />
    </Link>
  );
}

export function MedicineOrdersPage() {
  const { error, orders, status } = useMedicineOrders();

  return (
    <div className="space-y-4 lg:space-y-5">
      <PageHero
        icon={ShoppingCart}
        title="Your Medicine Orders"
        description="Prescription medicine order requests."
        action={
          <Link to="/app/orders/new" className={heroActionClassName}>
            <Plus className="size-4" aria-hidden="true" />
            New Order
          </Link>
        }
      />

      {error ? (
        <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">{error}</p>
      ) : null}

      {status === "loading" ? (
        <BrandedLoader compact label="Preparing your medicine orders" />
      ) : orders.length ? (
        <div className="rounded-[1.5rem] bg-white px-4 shadow-soft">
          {orders.map((order) => (
            <MedicineOrderRow key={order.order_id} order={order} />
          ))}
        </div>
      ) : (
        <div className="rounded-[1.25rem] bg-white px-4 py-4 shadow-soft">
          <p className="text-sm leading-6 text-muted-foreground">You haven&rsquo;t placed any medicine orders yet.</p>
          <Link
            to="/app/orders/new"
            className="mt-3 inline-flex min-h-10 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground"
          >
            <Plus className="size-4" aria-hidden="true" />
            Order Medicines
          </Link>
        </div>
      )}
    </div>
  );
}

type MedicineOrderDraft = {
  fullName: string;
  phoneCountry: PhoneCountry;
  phoneNumber: string;
  email: string;
  address: AddressDraft;
  orderNote: string;
};

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function MedicineOrderNewPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const { profile, status: profileStatus } = useAccountProfile();
  const { createOrder, error: orderError, status: orderStatus } = useMedicineOrders();
  const { completed: uploadCompleted, error: uploadError, progress: uploadProgress, status: uploadStatus, uploadDocument } = useDocumentUpload();
  const { ensureFolder } = useMedicineOrdersFolder();

  const defaultCountry = useMemo(() => findPhoneCountry("US") ?? phoneCountries[0], []);
  const [draft, setDraft] = useState<MedicineOrderDraft>(() => ({
    fullName: "",
    phoneCountry: defaultCountry,
    phoneNumber: "",
    email: "",
    address: addressDraftFromProfile(null),
    orderNote: "",
  }));
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const prefilled = useRef(false);

  // Prefilled once, from the account profile -- never re-applied after that, so editing these
  // fields here never gets clobbered and never writes back to the profile (see submitForm, which
  // only ever calls createOrder, never saveProfile).
  useEffect(() => {
    if (prefilled.current || profileStatus !== "ready" || !profile) {
      return;
    }
    prefilled.current = true;

    const profileCountry = findPhoneCountry(profile.contact_phone_country_code) ?? defaultCountry;
    const profileAddress = addressDraftFromProfile(profile.residential_address);
    const email = getClaim(auth.user?.profile, ["email"]);

    setDraft((current) => ({
      ...current,
      fullName: [profile.first_name, profile.last_name].filter(Boolean).join(" ").trim() || current.fullName,
      phoneCountry: profileCountry,
      phoneNumber: getEditablePhoneNumber(profile.contact_phone_e164, profile.contact_phone_country_code) || current.phoneNumber,
      email: email ?? current.email,
      address: hasAddressDraft(profileAddress) ? profileAddress : current.address,
    }));
  }, [auth.user, defaultCountry, profile, profileStatus]);

  const isSubmitting = orderStatus === "saving";
  const isUploading = uploadStatus === "preparing" || uploadStatus === "uploading" || uploadStatus === "finalizing";

  const handleFileChange = async (file: File | null) => {
    if (!file) {
      return;
    }
    setSelectedFile(file);
    setValidationError(null);
    const folderId = await ensureFolder();
    await uploadDocument(file, file.name, folderId);
  };

  const submitForm = async () => {
    if (isSubmitting || isUploading) {
      return;
    }

    if (!uploadCompleted?.document_id) {
      setValidationError("Upload your prescription (PDF, JPEG or PNG) to continue.");
      return;
    }

    const fullName = draft.fullName.trim();
    if (!fullName) {
      setValidationError("Full name is required.");
      return;
    }

    const trimmedPhone = draft.phoneNumber.trim();
    const parsedPhone = trimmedPhone ? parsePhoneNumberFromString(trimmedPhone, draft.phoneCountry.code) : null;
    if (!trimmedPhone || !parsedPhone?.isValid()) {
      setValidationError(`Enter a valid phone number for ${draft.phoneCountry.name}.`);
      return;
    }

    const line1 = draft.address.line1.trim();
    if (!line1) {
      setValidationError("Shipping address line 1 is required.");
      return;
    }

    const email = draft.email.trim();
    if (email && !EMAIL_PATTERN.test(email)) {
      setValidationError("Enter a valid email address.");
      return;
    }

    setValidationError(null);
    const order = await createOrder({
      document_id: uploadCompleted.document_id,
      full_name: fullName,
      phone: parsedPhone.number,
      email: email || null,
      shipping_address: {
        line1,
        line2: draft.address.line2.trim() || null,
        city: draft.address.city.trim() || null,
        region: draft.address.region.trim() || null,
        postal_code: draft.address.postal_code.trim() || null,
        country_code: draft.address.country_code || null,
      },
      order_note: draft.orderNote.trim() || null,
      acknowledged: true,
    });

    if (order) {
      void navigate(`/app/orders/${encodeURIComponent(order.order_id)}`, { replace: true, state: { order, justPlaced: true } });
    }
  };

  return (
    <div className="space-y-6">
      <BackAction to="/app/orders" label="Back to Your Medicine Orders" />
      <WorkspacePageHeader eyebrow="Order Medicines" title="Order Medicines" body="Upload your prescription and confirm delivery details to send a medicine order request." />

      <form
        className="rounded-2xl border border-primary/10 bg-white p-5 shadow-premium sm:p-6"
        onSubmit={(event) => {
          event.preventDefault();
          void submitForm();
        }}
      >
        <div className="grid gap-4">
          <fieldset className="rounded-2xl border border-border bg-surface/70 p-4">
            <legend className="px-1 text-sm font-semibold text-foreground">Prescription</legend>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Upload a PDF, JPEG or PNG of your prescription. It is saved to your Health Vault, the same as any other document.
            </p>
            <div className="mt-3">
              <label className="flex min-h-24 min-w-0 cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border bg-white px-4 py-6 text-center transition hover:border-primary/40">
                <Upload className="size-5 text-primary" aria-hidden="true" />
                <span className="text-sm font-semibold text-foreground">{selectedFile ? selectedFile.name : "Choose a file to upload"}</span>
                <span className="text-xs text-muted-foreground">PDF, JPEG or PNG</span>
                <input
                  type="file"
                  accept="application/pdf,image/jpeg,image/png"
                  className="sr-only"
                  onChange={(event) => void handleFileChange(event.target.files?.[0] ?? null)}
                  disabled={isSubmitting}
                />
              </label>
              {isUploading ? (
                <p className="mt-2 text-sm font-semibold text-primary">{describePrescriptionUploadStage(uploadStatus, uploadProgress)}</p>
              ) : null}
              {uploadCompleted && !isUploading ? <p className="mt-2 text-sm font-semibold text-emerald-700">Prescription ready to send.</p> : null}
              {uploadError ? <p className="mt-2 text-sm font-semibold text-destructive">{uploadError}</p> : null}
            </div>
          </fieldset>

          <fieldset className="rounded-2xl border border-border bg-surface/70 p-4">
            <legend className="px-1 text-sm font-semibold text-foreground">Delivery details</legend>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Prefilled from your profile where available. Changing these here does not update your MedoPilot profile.
            </p>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <TextField label="Full name" value={draft.fullName} onChange={(value) => setDraft((current) => ({ ...current, fullName: value }))} maxLength={160} autoComplete="name" disabled={isSubmitting} />
              <label className="block min-w-0 text-sm font-semibold text-foreground">
                Email <span className="font-normal text-muted-foreground">(optional)</span>
                <input
                  type="email"
                  value={draft.email}
                  onChange={(event) => setDraft((current) => ({ ...current, email: event.target.value }))}
                  className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                  maxLength={254}
                  autoComplete="email"
                  disabled={isSubmitting}
                />
              </label>
            </div>
            <div className="mt-4">
              <p className="text-sm font-semibold text-foreground">Phone</p>
              <div className="mt-2 grid gap-2 rounded-2xl border border-border bg-white p-2 transition focus-within:border-primary focus-within:ring-4 focus-within:ring-primary/10 sm:grid-cols-[minmax(10rem,0.46fr)_minmax(0,1fr)]">
                <CountryCombobox
                  value={draft.phoneCountry}
                  onChange={(country) => setDraft((current) => ({ ...current, phoneCountry: country }))}
                  disabled={isSubmitting}
                  label="Country Code"
                  placeholder="Search country or code"
                  buttonClassName="mt-0 min-h-10 rounded-xl border-0 bg-surface px-3 py-2 focus:ring-0"
                />
                <label className="sr-only" htmlFor="medicine-order-phone">
                  Phone number
                </label>
                <input
                  id="medicine-order-phone"
                  value={draft.phoneNumber}
                  onChange={(event) => setDraft((current) => ({ ...current, phoneNumber: event.target.value }))}
                  className="min-h-10 min-w-0 rounded-xl px-3 text-sm outline-none"
                  inputMode="tel"
                  autoComplete="tel-national"
                  placeholder="Phone number"
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <CountryCombobox
                value={findPhoneCountry(draft.address.country_code) ?? defaultCountry}
                onChange={(country) => setDraft((current) => ({ ...current, address: { ...current.address, country_code: country.code } }))}
                disabled={isSubmitting}
                label="Shipping country"
                placeholder="Search country"
                showDialCode={false}
              />
              <TextField
                label="Address line 1"
                value={draft.address.line1}
                onChange={(value) => setDraft((current) => ({ ...current, address: { ...current.address, line1: value } }))}
                maxLength={240}
                autoComplete="address-line1"
                disabled={isSubmitting}
              />
              <TextField
                label="Address line 2"
                value={draft.address.line2}
                onChange={(value) => setDraft((current) => ({ ...current, address: { ...current.address, line2: value } }))}
                maxLength={240}
                autoComplete="address-line2"
                disabled={isSubmitting}
              />
              <TextField
                label="City"
                value={draft.address.city}
                onChange={(value) => setDraft((current) => ({ ...current, address: { ...current.address, city: value } }))}
                maxLength={120}
                autoComplete="address-level2"
                disabled={isSubmitting}
              />
              <TextField
                label="State / region"
                value={draft.address.region}
                onChange={(value) => setDraft((current) => ({ ...current, address: { ...current.address, region: value } }))}
                maxLength={120}
                autoComplete="address-level1"
                disabled={isSubmitting}
              />
              <TextField
                label="Postal code"
                value={draft.address.postal_code}
                onChange={(value) => setDraft((current) => ({ ...current, address: { ...current.address, postal_code: value } }))}
                maxLength={32}
                autoComplete="postal-code"
                disabled={isSubmitting}
              />
            </div>
          </fieldset>

          <TextAreaField
            label="Order note (optional)"
            value={draft.orderNote}
            onChange={(value) => setDraft((current) => ({ ...current, orderNote: value }))}
            maxLength={500}
            placeholder="Anything the pharmacy should know, e.g. a preferred delivery time."
            disabled={isSubmitting}
          />
        </div>

        {validationError || orderError ? (
          <p className="mt-4 rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">{validationError ?? orderError}</p>
        ) : null}

        <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-sm leading-6 text-amber-900">
            By placing this order, MedoPilot will share the selected prescription and the delivery/contact details above with our pharmacy partner to fulfil your request.
          </p>
        </div>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <button
            type="submit"
            disabled={isSubmitting || isUploading}
            className="inline-flex min-h-10 items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? "Placing order…" : "Place Order"}
          </button>
          <button
            type="button"
            onClick={() => void navigate("/app/orders")}
            disabled={isSubmitting}
            className="inline-flex min-h-10 items-center justify-center rounded-full border border-border bg-white px-5 py-2.5 text-sm font-semibold text-muted-foreground transition hover:border-primary/30 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function MedicineOrderDetail({ order }: { order: MedicineOrder }) {
  return (
    <article className="rounded-2xl border border-primary/10 bg-white p-6 shadow-premium">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-primary">Medicine Order</p>
          <h2 className="mt-2 text-xl font-semibold">{order.order_reference}</h2>
          <p className="mt-2 text-sm text-muted-foreground">Submitted {formatDateTime(order.created_at) ?? "recently"}</p>
        </div>
        <MedicineOrderStatusPill status={order.status} />
      </div>

      <div className="mt-5 rounded-2xl border border-border bg-surface px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Status</p>
        {/* "Ready for processing by" -- never "sent to" or "forwarded to": no SES/API handoff to
            this pharmacy exists yet (see medopilot-medicine-order-service). This must stay
            truthful to that even as the single configured pharmacy identity below is real. */}
        <p className="mt-2 text-sm leading-6 text-foreground">
          Your prescription and delivery details are ready for processing by {order.pharmacy_name}. The pharmacy will contact you
          regarding medicine availability, pricing, payment and delivery. For changes, cancellations or fulfilment-related questions,
          please contact {order.pharmacy_name} directly.
        </p>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <ProfileDetail label="Pharmacy" value={order.pharmacy_name} />
        <ProfileDetail label="Prescription" value={order.document_filename} />
        <ProfileDetail label="Full name" value={order.full_name} />
        <ProfileDetail label="Phone" value={order.phone} />
        <ProfileDetail label="Email" value={order.email} />
      </div>

      {order.document_id ? (
        // Reuses the existing, unmodified Health Vault document viewer route (same authorized
        // presigned-URL flow as My Files) by document_id -- no separate prescription viewer.
        // DocumentViewerPage resolves the document and its viewer URL itself on mount, so this
        // works the same after a refresh or a direct link, not only when navigated to from here.
        <Link
          to={`/app/documents/${encodeURIComponent(order.document_id)}/view`}
          className="mt-4 inline-flex min-h-10 items-center justify-center gap-2 rounded-full border border-primary/25 px-4 text-sm font-semibold text-primary transition hover:bg-primary/5 focus:outline-none focus:ring-4 focus:ring-primary/20"
        >
          <Eye className="size-4" aria-hidden="true" />
          View Prescription
        </Link>
      ) : null}

      <div className="mt-4 rounded-2xl border border-border bg-surface px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Shipping address</p>
        <p className="mt-2 whitespace-pre-line text-sm leading-6 text-foreground">{formatAddress(order.shipping_address) ?? "Not provided"}</p>
      </div>

      {order.order_note ? (
        <div className="mt-4 rounded-2xl border border-border bg-surface px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Order note</p>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-foreground">{order.order_note}</p>
        </div>
      ) : null}
    </article>
  );
}

export function MedicineOrderDetailPage() {
  const location = useLocation();
  const { orderId } = useParams();
  const { error, orders, status } = useMedicineOrders();
  const state = location.state as { order?: MedicineOrder; justPlaced?: boolean } | null;
  const stateOrder = state && state.order && state.order.order_id === orderId ? state.order : null;
  const selectedOrder = orders.find((order) => order.order_id === orderId) ?? stateOrder;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <WorkspacePageHeader eyebrow="Order Medicines" title="Medicine Order Detail" body="Review the details submitted for this order request." />
        <BackAction to="/app/orders" label="Back to Your Medicine Orders" />
      </div>

      {state?.justPlaced ? (
        <p className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
          Order request received. MedoPilot will use this to coordinate with a pharmacy partner.
        </p>
      ) : null}

      {error ? <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">{error}</p> : null}

      {status === "loading" && !selectedOrder ? (
        <BrandedLoader compact label="Preparing your medicine order" />
      ) : selectedOrder ? (
        <MedicineOrderDetail order={selectedOrder} />
      ) : (
        <article className="rounded-2xl border border-primary/10 bg-white p-6 shadow-soft">
          <h2 className="text-base font-semibold">Medicine order not found</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">We could not find that medicine order in your Care Hub.</p>
        </article>
      )}
    </div>
  );
}

const medicationTypeOptions: Array<{ value: MedicationType; label: string }> = [
  { value: "prescribed_course", label: "Prescribed" },
  { value: "long_term_prescribed", label: "Long Term" },
  { value: "self_managed", label: "Self Managed" },
  { value: "supplement", label: "Supplement" },
];

const medicationFormOptions = [
  { value: "", label: "Select form" },
  { value: "Tablet", label: "Tablet" },
  { value: "Capsule", label: "Capsule" },
  { value: "Syrup/Liquid", label: "Syrup / Liquid" },
  { value: "Injection", label: "Injection" },
  { value: "Cream", label: "Cream" },
  { value: "Ointment", label: "Ointment" },
  { value: "Drops", label: "Drops" },
  { value: "Inhaler", label: "Inhaler" },
  { value: "Powder", label: "Powder" },
  { value: "Patch", label: "Patch" },
  { value: "Gel", label: "Gel" },
  { value: "Spray", label: "Spray" },
  { value: "Suppository", label: "Suppository" },
  { value: "Other", label: "Other" },
];

const strengthUnitOptions = [
  { value: "", label: "Select Unit" },
  { value: "mg", label: "mg" },
  { value: "g", label: "g" },
  { value: "mcg", label: "mcg" },
  { value: "mL", label: "mL" },
  { value: "IU", label: "IU" },
  { value: "%", label: "%" },
  { value: "Other", label: "Other" },
];

const daypartOptions: Array<{ value: "" | NonNullable<MedicationScheduleSlot["daypart"]>; label: string }> = [
  { value: "", label: "Select daypart" },
  { value: "morning", label: "Morning" },
  { value: "afternoon", label: "Afternoon" },
  { value: "evening", label: "Evening" },
  { value: "night", label: "Night" },
];

type MedicationSlotDraft = {
  slot_id: string;
  daypart: "" | NonNullable<MedicationScheduleSlot["daypart"]>;
  time: string;
  dose_text: string;
};

type MedicationDraft = {
  name: string;
  medication_type: MedicationType;
  dose_text: string;
  form: string;
  strength_value: string;
  strength_unit: string;
  custom_strength_unit: string;
  prescriber_name: string;
  reason: string;
  instructions: string;
  timezone: string;
  reminder_enabled: boolean;
  start_date: string;
  duration_mode: "ongoing" | "until_date";
  end_date: string;
  schedule: MedicationSlotDraft[];
};

function strengthDraftFromValue(value: string | null): Pick<MedicationDraft, "strength_value" | "strength_unit" | "custom_strength_unit"> {
  if (!value?.trim()) {
    return { strength_value: "", strength_unit: "", custom_strength_unit: "" };
  }

  const knownUnits = strengthUnitOptions.map((option) => option.value).filter((unit) => unit && unit !== "Other");
  const match = value.trim().match(/^(.+?)\s*([^\s]+)$/);
  const unit = match?.[2] ?? "";
  if (match && knownUnits.includes(unit)) {
    return { strength_value: match[1].trim(), strength_unit: unit, custom_strength_unit: "" };
  }

  return { strength_value: value.trim(), strength_unit: "", custom_strength_unit: "" };
}

function formatStrength(draft: MedicationDraft) {
  const value = optionalText(draft.strength_value);
  const unit = draft.strength_unit === "Other" ? optionalText(draft.custom_strength_unit) : optionalText(draft.strength_unit);
  return [value, unit].filter(Boolean).join(" ") || null;
}

function medicationFormDoseLabel(form: string) {
  const normalized = form.trim();
  if (!normalized || normalized === "Other") {
    return "";
  }
  return normalized;
}

function formatDoseText(quantity: string, form: string) {
  const value = optionalText(quantity);
  if (!value) {
    return null;
  }

  const formLabel = medicationFormDoseLabel(form);
  if (!formLabel || /[A-Za-z%]/.test(value)) {
    return value;
  }

  return `${value} ${formLabel}`;
}

function emptyMedicationDraft(): MedicationDraft {
  return {
    name: "",
    medication_type: "self_managed",
    dose_text: "",
    form: "",
    strength_value: "",
    strength_unit: "",
    custom_strength_unit: "",
    prescriber_name: "",
    reason: "",
    instructions: "",
    timezone: localTimezone(),
    reminder_enabled: true,
    start_date: new Date().toISOString().slice(0, 10),
    duration_mode: "ongoing",
    end_date: "",
    schedule: [{ slot_id: "morning", daypart: "morning", time: "", dose_text: "" }],
  };
}

function medicationDraftFromRecord(record: Medication): MedicationDraft {
  const fallback = emptyMedicationDraft();
  return {
    name: record.name,
    medication_type: record.medication_type,
    dose_text: record.dose_text ?? "",
    form: record.form ?? "",
    ...strengthDraftFromValue(record.strength),
    prescriber_name: record.prescriber_name ?? "",
    reason: record.reason ?? "",
    instructions: record.instructions ?? "",
    timezone: record.timezone || fallback.timezone,
    reminder_enabled: record.reminder_enabled,
    start_date: record.start_date ?? "",
    duration_mode: record.end_date ? "until_date" : "ongoing",
    end_date: record.end_date ?? "",
    schedule: record.schedule.slots.length
      ? record.schedule.slots.map((slot) => ({
          slot_id: slot.slot_id,
          daypart: slot.daypart ?? "",
          time: slot.time ?? "",
          dose_text: slot.dose_text ?? "",
        }))
      : fallback.schedule,
  };
}

function medicationPayloadFromDraft(draft: MedicationDraft): { payload: MedicationInput; error: string | null } {
  if (!draft.name.trim()) {
    return { payload: emptyMedicationPayload(), error: "Add the medication name before saving." };
  }

  const endDate = draft.duration_mode === "until_date" ? draft.end_date : "";

  if (draft.duration_mode === "until_date" && !draft.end_date) {
    return { payload: emptyMedicationPayload(), error: "Add an end date or choose Ongoing." };
  }

  if (draft.start_date && endDate && endDate < draft.start_date) {
    return { payload: emptyMedicationPayload(), error: "End date cannot be before start date." };
  }

  const slots = draft.schedule
    .map((slot, index) => ({
      slot_id: [slot.daypart || "dose", slot.time?.replace(":", "") || index + 1].join("-"),
      daypart: slot.daypart || null,
      time: slot.time || null,
      dose_text: formatDoseText(slot.dose_text, draft.form),
    }))
    .filter((slot) => slot.daypart || slot.time || slot.dose_text);

  const seenSlotIds = new Set<string>();
  for (const slot of slots) {
    if (!slot.daypart && !slot.time) {
      return { payload: emptyMedicationPayload(), error: "Each schedule slot needs a daypart or exact time." };
    }
    if (seenSlotIds.has(slot.slot_id)) {
      return { payload: emptyMedicationPayload(), error: "Schedule slot labels must be unique." };
    }
    seenSlotIds.add(slot.slot_id);
  }

  return {
    error: null,
    payload: {
      name: draft.name.trim(),
      medication_type: draft.medication_type,
      dose_text: formatDoseText(draft.dose_text, draft.form),
      form: optionalText(draft.form),
      strength: formatStrength(draft),
      prescriber_name: optionalText(draft.prescriber_name),
      reason: optionalText(draft.reason),
      instructions: optionalText(draft.instructions),
      timezone: draft.timezone.trim() || "UTC",
      reminder_enabled: draft.reminder_enabled,
      start_date: draft.start_date || null,
      end_date: endDate || null,
      schedule: { slots },
    },
  };
}

function emptyMedicationPayload(): MedicationInput {
  return {
    name: "",
    medication_type: "self_managed",
    dose_text: null,
    form: null,
    strength: null,
    prescriber_name: null,
    reason: null,
    instructions: null,
    schedule: { slots: [] },
    timezone: "UTC",
    reminder_enabled: true,
    start_date: null,
    end_date: null,
  };
}

function medicationScheduleSummary(record: Medication) {
  if (!record.schedule.slots.length) {
    return "Schedule not added";
  }

  return record.schedule.slots
    .slice(0, 3)
    .map((slot) => [slot.dose_text ?? record.dose_text, slot.time ? formatTime(slot.time) : daypartLabel(slot.daypart)].filter(Boolean).join(" · "))
    .join("; ");
}

function MedicationMetaChips({ record }: { record: Medication }) {
  return (
    <span className="mt-3 flex flex-wrap gap-2">
      <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">{medicationTypeLabel(record.medication_type)}</span>
      <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-muted-foreground">{medicationStatusLabel(record.status)}</span>
    </span>
  );
}

function MedicationRecordLink({ record }: { record: Medication }) {
  return (
    <Link
      to={`/app/medications/${encodeURIComponent(record.medication_id)}`}
      className="group block min-w-0 rounded-3xl border border-primary/10 bg-white px-4 py-4 shadow-soft transition hover:-translate-y-0.5 hover:border-primary/25 hover:shadow-premium focus:outline-none focus:ring-4 focus:ring-primary/10 motion-reduce:transition-none"
    >
      <span className="flex min-w-0 items-start gap-4">
        <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-blue-50 text-primary">
          <Pill className="size-5" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[0.95rem] font-semibold leading-5 text-foreground">{record.name}</span>
          <MedicationMetaChips record={record} />
          <span className="mt-2 block truncate text-sm text-muted-foreground">{medicationScheduleSummary(record)}</span>
        </span>
        <ArrowRight className="mt-1 size-4 shrink-0 text-primary transition group-hover:translate-x-0.5" aria-hidden="true" />
      </span>
    </Link>
  );
}

function MedicationListRow({ record }: { record: Medication }) {
  return (
    <Link
      to={`/app/medications/${encodeURIComponent(record.medication_id)}`}
      className="group block min-w-0 border-b border-border/70 py-3.5 transition last:border-b-0 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10 motion-reduce:transition-none"
    >
      <span className="flex min-w-0 items-center gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-blue-50 text-primary">
          <Pill className="size-4" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[0.95rem] font-semibold leading-5 text-foreground">{record.name}</span>
          <span className="mt-1 block truncate text-sm text-muted-foreground">
            {[record.dose_text, record.strength, medicationScheduleSummary(record)].filter(Boolean).join(" · ")}
          </span>
        </span>
        <span className="hidden shrink-0 rounded-full bg-secondary px-2.5 py-1 text-xs font-semibold text-muted-foreground sm:inline-flex">
          {medicationStatusLabel(record.status)}
        </span>
        <ArrowRight className="size-4 shrink-0 text-primary transition group-hover:translate-x-0.5 motion-reduce:transition-none" aria-hidden="true" />
      </span>
    </Link>
  );
}

function MedicationForm({
  draft,
  onCancel,
  onChange,
  onSubmit,
  saving,
  validationError,
}: {
  draft: MedicationDraft;
  onCancel: () => void;
  onChange: (draft: MedicationDraft) => void;
  onSubmit: () => void;
  saving: boolean;
  validationError: string | null;
}) {
  const updateSlot = (index: number, changes: Partial<MedicationSlotDraft>) => {
    onChange({
      ...draft,
      schedule: draft.schedule.map((slot, slotIndex) => (slotIndex === index ? { ...slot, ...changes } : slot)),
    });
  };

  const addSlot = () => {
    onChange({
      ...draft,
      schedule: [...draft.schedule, { slot_id: `dose-${draft.schedule.length + 1}`, daypart: "", time: "", dose_text: "" }],
    });
  };

  const removeSlot = (index: number) => {
    onChange({
      ...draft,
      schedule: draft.schedule.filter((_, slotIndex) => slotIndex !== index),
    });
  };

  return (
    <form
      className="rounded-2xl border border-primary/10 bg-white p-5 shadow-premium sm:p-6"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div className="grid gap-5">
        <VaccinationFormSection title="Medication Details">
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Medication / Supplement Name
            <input
              value={draft.name}
              onChange={(event) => onChange({ ...draft, name: event.target.value })}
              className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              maxLength={180}
              disabled={saving}
            />
          </label>
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Type
            <select
              value={draft.medication_type}
              onChange={(event) => onChange({ ...draft, medication_type: event.target.value as MedicationType })}
              className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              disabled={saving}
            >
              {medicationTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block min-w-0 text-sm font-semibold text-foreground sm:col-span-2">
            Dose
            <input
              value={draft.dose_text}
              onChange={(event) => onChange({ ...draft, dose_text: event.target.value })}
              className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              placeholder={draft.form ? `1 ${medicationFormDoseLabel(draft.form) || ""}`.trim() : "1 tablet, 5 mL, 500 mg"}
              disabled={saving}
            />
          </label>
        </VaccinationFormSection>

        <fieldset className="rounded-2xl border border-border bg-surface/70 p-4">
          <legend className="px-1 text-sm font-semibold text-foreground">Treatment Context</legend>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">Choose Ongoing for regular use or End Date for a time-limited treatment.</p>
          <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-[minmax(11rem,0.75fr)_minmax(16rem,1fr)_minmax(11rem,0.75fr)]">
            <label className="block min-w-0 text-sm font-semibold text-foreground">
              Start Date
              <input
                type="date"
                value={draft.start_date}
                onChange={(event) => onChange({ ...draft, start_date: event.target.value })}
                className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                disabled={saving}
              />
            </label>
            <fieldset className="min-w-0">
              <legend className="text-sm font-semibold text-foreground">Duration</legend>
              <div className="mt-2 grid gap-2 rounded-2xl border border-border bg-white p-2 sm:grid-cols-2">
                {[
                  { value: "ongoing", label: "Ongoing" },
                  { value: "until_date", label: "End Date" },
                ].map((option) => (
                  <label
                    key={option.value}
                    className={cn(
                      "flex min-h-10 items-center justify-center rounded-xl px-3 text-sm font-semibold transition",
                      draft.duration_mode === option.value ? "bg-primary text-primary-foreground shadow-soft" : "text-muted-foreground hover:bg-secondary",
                    )}
                  >
                    <input
                      type="radio"
                      name="medication-duration"
                      value={option.value}
                      checked={draft.duration_mode === option.value}
                      onChange={() => onChange({ ...draft, duration_mode: option.value as MedicationDraft["duration_mode"], end_date: option.value === "ongoing" ? "" : draft.end_date })}
                      className="sr-only"
                      disabled={saving}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </fieldset>
            {draft.duration_mode === "until_date" ? (
              <label className="block min-w-0 text-sm font-semibold text-foreground">
                End Date
                <input
                  type="date"
                  value={draft.end_date}
                  onChange={(event) => onChange({ ...draft, end_date: event.target.value })}
                  className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                  required
                  disabled={saving}
                />
              </label>
            ) : null}
          </div>
        </fieldset>

        <fieldset className="rounded-2xl border border-border bg-surface/70 p-4">
          <legend className="px-1 text-sm font-semibold text-foreground">Frequency / Timing</legend>
          <div className="mt-4 grid min-w-0 gap-4 md:grid-cols-3">
            <label className="block min-w-0 text-sm font-semibold text-foreground">
              When
              <select
                value={draft.schedule[0]?.daypart ?? ""}
                onChange={(event) => updateSlot(0, { daypart: event.target.value as MedicationSlotDraft["daypart"] })}
                className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-3 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                disabled={saving}
              >
                {daypartOptions.map((option) => (
                  <option key={option.value || "blank"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block min-w-0 text-sm font-semibold text-foreground">
              Time <span className="font-normal text-muted-foreground">(optional)</span>
              <input
                type="time"
                value={draft.schedule[0]?.time ?? ""}
                onChange={(event) => updateSlot(0, { time: event.target.value })}
                className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-3 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                disabled={saving}
              />
            </label>
            <label className="block min-w-0 text-sm font-semibold text-foreground">
              Dose At This Time
              <input
                value={draft.schedule[0]?.dose_text ?? ""}
                onChange={(event) => updateSlot(0, { dose_text: event.target.value })}
                className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-3 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                placeholder={draft.dose_text || "1"}
                disabled={saving}
              />
            </label>
          </div>
        </fieldset>

        <fieldset className="rounded-2xl border border-border bg-surface/70 p-4">
          <legend className="px-1 text-sm font-semibold text-foreground">Reminders</legend>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Whether MedoPilot should remind you to take this medication at its scheduled times.
          </p>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <label className="flex min-w-0 items-start gap-3 rounded-2xl border border-border bg-white px-4 py-3 text-sm font-semibold text-foreground sm:col-span-2">
              <input
                type="checkbox"
                checked={draft.reminder_enabled}
                onChange={(event) => onChange({ ...draft, reminder_enabled: event.target.checked })}
                className="mt-1 size-4 accent-primary"
                disabled={saving}
              />
              <span>
                Remind me to take this medication
                <span className="mt-1 block text-xs font-normal leading-5 text-muted-foreground">
                  Uses the dose times above. You can change this any time.
                </span>
              </span>
            </label>
            <label className="block min-w-0 text-sm font-semibold text-foreground sm:col-span-2">
              Reminder timezone
              <TimezoneCombobox
                value={draft.timezone}
                onChange={(zone) => onChange({ ...draft, timezone: zone })}
                disabled={saving}
                label="Reminder timezone"
              />
              <span className="mt-2 block text-xs font-normal leading-5 text-muted-foreground">
                Used to schedule reminders at the right local time.
              </span>
            </label>
          </div>
        </fieldset>

        <MoreDetails>
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Form
            <select
              value={draft.form}
              onChange={(event) => onChange({ ...draft, form: event.target.value })}
              className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              disabled={saving}
            >
              {draft.form && !medicationFormOptions.some((option) => option.value === draft.form) ? (
                <option value={draft.form}>{draft.form}</option>
              ) : null}
              {medicationFormOptions.map((option) => (
                <option key={option.value || "blank"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Strength
            <input
              value={draft.strength_value}
              onChange={(event) => onChange({ ...draft, strength_value: event.target.value })}
              className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              placeholder="500"
              disabled={saving}
            />
          </label>
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Unit
            <select
              value={draft.strength_unit}
              onChange={(event) => onChange({ ...draft, strength_unit: event.target.value })}
              className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-3 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              disabled={saving}
            >
              {strengthUnitOptions.map((option) => (
                <option key={option.value || "blank"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {draft.strength_unit === "Other" ? (
            <label className="block min-w-0 text-sm font-semibold text-foreground">
              Custom Unit
              <input
                value={draft.custom_strength_unit}
                onChange={(event) => onChange({ ...draft, custom_strength_unit: event.target.value })}
                className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                placeholder="unit"
                disabled={saving}
              />
            </label>
          ) : null}
          {draft.schedule.slice(1).map((slot, index) => (
            <div key={`${slot.slot_id}-${index + 1}`} className="rounded-2xl border border-border bg-white p-4 sm:col-span-2">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <p className="text-sm font-semibold text-foreground">Additional Dose {index + 2}</p>
                <button
                  type="button"
                  onClick={() => removeSlot(index + 1)}
                  disabled={saving}
                  className="inline-flex min-h-9 w-fit items-center justify-center rounded-full border border-border bg-white px-3 py-1.5 text-xs font-semibold text-muted-foreground transition hover:border-primary/30 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Remove
                </button>
              </div>
              <div className="mt-4 grid min-w-0 gap-4 md:grid-cols-3">
                <label className="block min-w-0 text-sm font-semibold text-foreground">
                  When
                  <select
                    value={slot.daypart}
                    onChange={(event) => updateSlot(index + 1, { daypart: event.target.value as MedicationSlotDraft["daypart"] })}
                    className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-3 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                    disabled={saving}
                  >
                    {daypartOptions.map((option) => (
                      <option key={option.value || "blank"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block min-w-0 text-sm font-semibold text-foreground">
                  Time <span className="font-normal text-muted-foreground">(optional)</span>
                  <input
                    type="time"
                    value={slot.time}
                    onChange={(event) => updateSlot(index + 1, { time: event.target.value })}
                    className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-3 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                    disabled={saving}
                  />
                </label>
                <label className="block min-w-0 text-sm font-semibold text-foreground">
                  Dose
                  <input
                    value={slot.dose_text}
                    onChange={(event) => updateSlot(index + 1, { dose_text: event.target.value })}
                    className="mt-2 h-11 w-full rounded-2xl border border-border bg-white px-3 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                    placeholder={draft.dose_text || "1"}
                    disabled={saving}
                  />
                </label>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addSlot}
            disabled={saving}
            className="inline-flex min-h-10 w-fit items-center gap-2 rounded-full border border-primary/20 bg-white px-4 py-2 text-sm font-semibold text-primary transition hover:border-primary/40 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Plus className="size-4" aria-hidden="true" />
            Add Dose Time
          </button>
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Prescriber
            <input
              value={draft.prescriber_name}
              onChange={(event) => onChange({ ...draft, prescriber_name: event.target.value })}
              className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              disabled={saving}
            />
          </label>
          <label className="block min-w-0 text-sm font-semibold text-foreground">
            Reason
            <input
              value={draft.reason}
              onChange={(event) => onChange({ ...draft, reason: event.target.value })}
              className="mt-2 h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              disabled={saving}
            />
          </label>
          <label className="block min-w-0 text-sm font-semibold text-foreground sm:col-span-2">
            Detailed Instructions
            <textarea
              value={draft.instructions}
              onChange={(event) => onChange({ ...draft, instructions: event.target.value })}
              className="mt-2 min-h-28 w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              maxLength={2000}
              disabled={saving}
            />
          </label>
        </MoreDetails>
      </div>
      {validationError ? (
        <p className="mt-4 rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">
          {validationError}
        </p>
      ) : null}
      <div className="mt-5 flex flex-col gap-3 sm:flex-row">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex min-h-10 items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? "Saving" : "Save"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="inline-flex min-h-10 items-center justify-center rounded-full border border-border bg-white px-5 py-2.5 text-sm font-semibold text-muted-foreground transition hover:border-primary/30 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function MedicationDetail({ record, upcomingDoses, onEdit, onLifecycle, onRecordDose, saving }: {
  record: Medication;
  upcomingDoses: UpcomingDose[];
  onEdit: () => void;
  onLifecycle: (status: "completed" | "stopped") => void;
  onRecordDose: (dose: UpcomingDose, status: DoseEventStatus) => void;
  saving: boolean;
}) {
  return (
    <article className="min-w-0 rounded-2xl border border-primary/10 bg-white p-5 shadow-premium sm:p-6 lg:p-7">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <MedicationMetaChips record={record} />
          <h2 className="mt-2 text-xl font-semibold">{record.name}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {[record.dose_text, record.form, record.strength].filter(Boolean).join(" · ") || "Dose details not added yet"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex min-h-10 items-center justify-center rounded-full border border-border bg-white px-4 py-2 text-sm font-semibold text-primary transition hover:border-primary/30"
          >
            Edit
          </button>
          {record.status === "active" ? (
            <>
              <button
                type="button"
                onClick={() => onLifecycle("completed")}
                disabled={saving}
                className="inline-flex min-h-10 items-center justify-center rounded-full border border-primary/20 bg-primary/10 px-4 py-2 text-sm font-semibold text-primary transition hover:border-primary/40 disabled:opacity-60"
              >
                Complete
              </button>
              <button
                type="button"
                onClick={() => onLifecycle("stopped")}
                disabled={saving}
                className="inline-flex min-h-10 items-center justify-center rounded-full border border-border bg-white px-4 py-2 text-sm font-semibold text-muted-foreground transition hover:border-primary/30 disabled:opacity-60"
              >
                Stop
              </button>
            </>
          ) : null}
        </div>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <ProfileDetail label="Status" value={medicationStatusLabel(record.status)} />
        <ProfileDetail label="Schedule" value={medicationScheduleSummary(record)} />
        <ProfileDetail label="Start Date" value={formatDate(record.start_date) ?? record.start_date} />
        <ProfileDetail label="End Date" value={formatDate(record.end_date) ?? record.end_date} />
        <ProfileDetail label="Prescriber" value={record.prescriber_name} />
        <ProfileDetail label="Reason" value={record.reason} />
        <ProfileDetail label="Instructions" value={record.instructions} />
        <ProfileDetail label="Reminder timezone" value={record.timezone} />
        <ProfileDetail label="Reminders" value={record.reminder_enabled ? "On" : "Off"} />
      </div>

      <div className="mt-7 rounded-2xl border border-primary/10 bg-surface p-4 sm:p-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h3 className="font-semibold">Upcoming Doses</h3>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">Mark a pending dose when the service provides it here.</p>
          </div>
        </div>
        {upcomingDoses.length ? (
          <div className="mt-4 grid gap-3">
            {upcomingDoses.slice(0, 5).map((dose) => (
              <div key={dose.occurrence_key} className="rounded-2xl border border-border bg-white p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="font-semibold text-foreground">{dose.dose_text ?? record.dose_text ?? "Dose"}</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {[formatDate(dose.due_date) ?? dose.due_date, dose.due_time ? formatTime(dose.due_time) : daypartLabel(dose.daypart)].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(["taken", "missed", "skipped"] as DoseEventStatus[]).map((status) => (
                      <button
                        key={status}
                        type="button"
                        onClick={() => onRecordDose(dose, status)}
                        disabled={saving}
                        className="inline-flex min-h-9 items-center rounded-full border border-border bg-white px-3 py-1.5 text-xs font-semibold text-muted-foreground transition hover:border-primary/30 hover:text-primary disabled:opacity-60"
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-sm leading-6 text-muted-foreground">No upcoming doses are currently calculated for this medication.</p>
        )}
      </div>
    </article>
  );
}

export function MedicationsPage() {
  const location = useLocation();
  const { error, medications, saved, status, upcomingDoses } = useMedications();
  const [upcomingExpanded, setUpcomingExpanded] = useState(false);
  const state = location.state as { saved?: boolean } | null;
  const latestMedications = useMemo(() => getLatestMedications(medications), [medications]);
  const activeMedications = latestMedications.filter((record) => record.status === "active");
  const historyMedications = latestMedications.filter((record) => record.status !== "active");
  const activeCount = medications.filter((record) => record.status === "active").length;
  const hasMedications = medications.length > 0;
  const upcomingSummary = upcomingDoses.length
    ? pluralize(upcomingDoses.length, "Upcoming Dose")
    : "No upcoming medication doses";

  return (
    <div className="space-y-4 lg:space-y-5">
      <PageHero
        icon={Pill}
        title="Medications & Supplements"
        description="Medications, supplements and treatment schedules."
        action={
          <Link to="/app/medications/new" className={heroActionClassName}>
            <Plus className="size-4" aria-hidden="true" />
            Add
          </Link>
        }
      />

      <div className="flex min-w-0 items-center gap-2 overflow-x-auto pb-1 text-sm font-semibold text-muted-foreground [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <span className="shrink-0 rounded-full bg-blue-50 px-3 py-1.5 text-primary">{pluralize(activeCount, "Active")}</span>
        <span className="shrink-0 text-border" aria-hidden="true">·</span>
        <span className="shrink-0 rounded-full bg-amber-50 px-3 py-1.5 text-amber-700">{pluralize(upcomingDoses.length, "Upcoming Dose")}</span>
        {historyMedications.length ? (
          <>
            <span className="shrink-0 text-border" aria-hidden="true">·</span>
          <Link
            to="/app/medications/history"
              className="inline-flex min-h-9 shrink-0 items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-primary shadow-soft transition hover:shadow-premium focus:outline-none focus:ring-4 focus:ring-primary/10"
          >
              History
              <ArrowRight className="size-3.5" aria-hidden="true" />
          </Link>
          </>
        ) : null}
      </div>

      {state?.saved || saved ? <p className="rounded-full bg-primary/10 px-4 py-2 text-sm font-semibold text-primary">Medication saved</p> : null}
      {error ? (
        <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">
          {error}
        </p>
      ) : null}

      {status === "loading" ? (
        <BrandedLoader compact label="Preparing medication records" />
      ) : (
        <div className="space-y-5">
          <section className="min-w-0">
            <h2 className="text-base font-semibold">Upcoming</h2>
            <button
              type="button"
              onClick={() => setUpcomingExpanded((expanded) => !expanded)}
              className="group mt-2 flex min-h-16 w-full min-w-0 items-center gap-3 rounded-[1.25rem] bg-white px-3.5 py-3 text-left shadow-soft transition hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10 motion-reduce:transition-none"
              aria-expanded={upcomingExpanded}
              aria-controls="medication-upcoming-doses"
            >
              <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-amber-50 text-amber-700">
                <CalendarClock className="size-4" aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[0.95rem] font-semibold leading-5 text-foreground">{upcomingSummary}</span>
                <span className="mt-1 block text-sm text-muted-foreground">
                  {upcomingDoses.length ? "Expand to review dose timing" : "Future scheduled doses will appear here"}
                </span>
              </span>
              <ChevronDown
                className={cn("size-4 shrink-0 text-primary transition motion-reduce:transition-none", upcomingExpanded ? "rotate-180" : "")}
                aria-hidden="true"
              />
            </button>
            {upcomingExpanded ? (
              upcomingDoses.length ? (
                <div id="medication-upcoming-doses" className="mt-2 rounded-[1.5rem] bg-white px-4 shadow-soft">
                  {upcomingDoses.slice(0, 5).map((dose) => (
                  <Link
                    key={dose.occurrence_key}
                    to={`/app/medications/${encodeURIComponent(dose.medication_id)}`}
                    className="group flex min-w-0 items-center gap-3 border-b border-border/70 py-3.5 transition last:border-b-0 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10 motion-reduce:transition-none"
                  >
                    <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-amber-50 text-amber-700">
                      <CalendarClock className="size-4" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[0.95rem] font-semibold leading-5 text-foreground">{dose.medication_name}</span>
                      <span className="mt-1 block text-sm text-muted-foreground">
                        {[dose.category, dose.dose_text, dueLabel(dose)].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    <ArrowRight className="mt-1 size-4 shrink-0 text-primary transition group-hover:translate-x-0.5 motion-reduce:transition-none" aria-hidden="true" />
                  </Link>
                  ))}
                </div>
              ) : (
                <p id="medication-upcoming-doses" className="mt-2 rounded-[1.25rem] bg-white px-4 py-3 text-sm leading-6 text-muted-foreground shadow-soft">
                  No upcoming medication doses right now.
                </p>
              )
            ) : null}
          </section>

          <section className="min-w-0">
            <h2 className="text-base font-semibold">Your Medications</h2>
            {activeMedications.length ? (
              <div className="mt-2 rounded-[1.5rem] bg-white px-4 shadow-soft">
                {activeMedications.map((record) => <MedicationListRow key={record.medication_id} record={record} />)}
              </div>
            ) : (
              <div className="mt-2 rounded-[1.25rem] bg-white px-4 py-4 shadow-soft">
                <p className="text-sm leading-6 text-muted-foreground">
                  {hasMedications ? "No active medications right now." : "No medications added yet."}
                </p>
                {!hasMedications ? (
                  <Link
                    to="/app/medications/new"
                    className="mt-3 inline-flex min-h-10 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground"
                  >
                    <Plus className="size-4" aria-hidden="true" />
                    Add Medication
                  </Link>
                ) : null}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

export function MedicationsHistoryPage() {
  const { error, medications, status } = useMedications();
  const historyMedications = useMemo(
    () => getLatestMedications(medications).filter((record) => record.status !== "active"),
    [medications],
  );

  return (
    <div className="space-y-6">
      <BackAction to="/app/medications" label="Back to Medications" />
      <WorkspacePageHeader
        eyebrow="Medications"
        title="Medication History"
        body="Completed and stopped medication records remain available for review."
      />
      {error ? (
        <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">
          {error}
        </p>
      ) : null}
      {status === "loading" ? (
        <BrandedLoader compact label="Preparing medication history" />
      ) : (
        <article className="min-w-0 rounded-2xl border border-primary/10 bg-white p-5 shadow-premium sm:p-6">
          {historyMedications.length ? (
            <div className="grid gap-3">
              {historyMedications.map((record) => (
                <MedicationRecordLink key={record.medication_id} record={record} />
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-primary/20 bg-surface p-6">
              <Pill className="size-8 text-primary" aria-hidden="true" />
              <h2 className="mt-5 text-base font-semibold">No medication history yet</h2>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                Completed or stopped medications will appear here while active schedules stay on the main Medication Tracker.
              </p>
            </div>
          )}
        </article>
      )}
    </div>
  );
}

export function MedicationNewPage() {
  const navigate = useNavigate();
  const { createMedication, status } = useMedications();
  const [draft, setDraft] = useState<MedicationDraft>(() => emptyMedicationDraft());
  const [validationError, setValidationError] = useState<string | null>(null);

  const submitForm = async () => {
    if (status === "saving") {
      return;
    }

    const { payload, error: nextValidationError } = medicationPayloadFromDraft(draft);
    if (nextValidationError) {
      setValidationError(nextValidationError);
      return;
    }

    setValidationError(null);
    const record = await createMedication(payload);
    if (record) {
      void navigate(`/app/medications/${encodeURIComponent(record.medication_id)}`, { replace: true, state: { saved: true } });
    }
  };

  return (
    <div className="space-y-6">
      <BackAction to="/app/medications" label="Back to Medications" />
      <WorkspacePageHeader eyebrow="Medications" title="Add Medication" body="Save a medication, supplement or treatment schedule." />
      <MedicationForm
        draft={draft}
        onCancel={() => void navigate("/app/medications")}
        onChange={setDraft}
        onSubmit={() => void submitForm()}
        saving={status === "saving"}
        validationError={validationError}
      />
    </div>
  );
}

export function MedicationDetailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { medicationId } = useParams();
  const { error, loadDoseEvents, medications, recordDoseEvent, setMedicationLifecycle, status, upcomingDoses } = useMedications();
  const selectedRecord = medications.find((record) => record.medication_id === medicationId) ?? null;
  const state = location.state as { saved?: boolean } | null;
  const selectedUpcomingDoses = upcomingDoses.filter((dose) => dose.medication_id === medicationId);

  useEffect(() => {
    if (!medicationId) {
      return undefined;
    }

    const controller = new AbortController();
    void loadDoseEvents(medicationId, controller.signal).catch(() => undefined);
    return () => controller.abort();
  }, [loadDoseEvents, medicationId]);

  const handleDoseEvent = (dose: UpcomingDose, doseStatus: DoseEventStatus) => {
    void recordDoseEvent(dose.medication_id, dose.occurrence_key, {
      status: doseStatus,
      local_date: dose.due_date,
      slot_id: dose.slot_id,
      daypart: dose.daypart,
      time: dose.due_time,
      dose_text: dose.dose_text,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <WorkspacePageHeader eyebrow="Medications" title="Medication Detail" body="Review the schedule and treatment details saved for this medication." />
        <BackAction to="/app/medications" label="Back to Medications" />
      </div>
      {state?.saved ? <p className="rounded-full bg-primary/10 px-4 py-2 text-sm font-semibold text-primary">Medication saved</p> : null}
      {error ? (
        <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">
          {error}
        </p>
      ) : null}
      {status === "loading" ? (
        <BrandedLoader compact label="Preparing medication record" />
      ) : selectedRecord ? (
        <MedicationDetail
          record={selectedRecord}
          upcomingDoses={selectedUpcomingDoses}
          onEdit={() => void navigate(`/app/medications/${encodeURIComponent(selectedRecord.medication_id)}/edit`)}
          onLifecycle={(nextStatus) => void setMedicationLifecycle(selectedRecord.medication_id, nextStatus)}
          onRecordDose={handleDoseEvent}
          saving={status === "saving"}
        />
      ) : (
        <article className="rounded-2xl border border-primary/10 bg-white p-6 shadow-soft">
          <h2 className="text-base font-semibold">Medication not found</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">We could not find that medication record in your Care Hub.</p>
        </article>
      )}
    </div>
  );
}

export function MedicationEditPage() {
  const navigate = useNavigate();
  const { medicationId } = useParams();
  const { medications, status, updateMedication } = useMedications();
  const selectedRecord = medications.find((record) => record.medication_id === medicationId) ?? null;
  const [draft, setDraft] = useState<MedicationDraft>(() => emptyMedicationDraft());
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedRecord) {
      setDraft(medicationDraftFromRecord(selectedRecord));
    }
  }, [selectedRecord]);

  const submitForm = async () => {
    if (!selectedRecord || status === "saving") {
      return;
    }

    const { payload, error: nextValidationError } = medicationPayloadFromDraft(draft);
    if (nextValidationError) {
      setValidationError(nextValidationError);
      return;
    }

    setValidationError(null);
    const record = await updateMedication(selectedRecord.medication_id, payload);
    if (record) {
      void navigate(`/app/medications/${encodeURIComponent(record.medication_id)}`, { replace: true, state: { saved: true } });
    }
  };

  return (
    <div className="space-y-6">
      <BackAction to="/app/medications" label="Back to Medications" />
      <WorkspacePageHeader eyebrow="Medications" title="Edit Medication" body="Update medication details, schedule or instructions." />
      {status === "loading" ? (
        <BrandedLoader compact label="Preparing medication record" />
      ) : selectedRecord ? (
        <MedicationForm
          draft={draft}
          onCancel={() => void navigate(`/app/medications/${encodeURIComponent(selectedRecord.medication_id)}`)}
          onChange={setDraft}
          onSubmit={() => void submitForm()}
          saving={status === "saving"}
          validationError={validationError}
        />
      ) : (
        <article className="rounded-2xl border border-primary/10 bg-white p-6 shadow-soft">
          <h2 className="text-base font-semibold">Medication not found</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">We could not find that medication record in your Care Hub.</p>
        </article>
      )}
    </div>
  );
}

function EmergencyInfoRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value?.trim()) {
    return null;
  }

  const contactHref = emergencyContactHref(label, value);

  return (
    <div className="border-b border-border py-2.5 last:border-b-0">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
      {contactHref ? (
        <a
          href={contactHref}
          className="mt-1 block whitespace-pre-line break-words text-sm font-semibold text-primary underline-offset-4 transition hover:underline focus:outline-none focus:ring-4 focus:ring-primary/10"
        >
          {value}
        </a>
      ) : (
        <p className="mt-1 whitespace-pre-line break-words text-sm font-semibold text-foreground">{value}</p>
      )}
    </div>
  );
}

function emergencyContactHref(label: string, value: string) {
  const normalizedLabel = label.toLowerCase();
  const trimmedValue = value.trim();

  if (normalizedLabel.includes("email") && trimmedValue.includes("@")) {
    return `mailto:${trimmedValue}`;
  }

  const isPhoneLabel =
    normalizedLabel.includes("phone") ||
    normalizedLabel.includes("mobile") ||
    normalizedLabel.includes("whatsapp") ||
    normalizedLabel.includes("ambulance") ||
    normalizedLabel === "emergency number" ||
    normalizedLabel === "emergency contact";

  if (isPhoneLabel && /\d/.test(trimmedValue)) {
    return `tel:${trimmedValue.replace(/[^\d+]/g, "")}`;
  }

  return null;
}

function EmergencySection({
  children,
  icon: Icon,
  onAction,
  title,
  to,
}: {
  children: ReactNode;
  icon: LucideIcon;
  onAction?: () => void;
  title: string;
  to?: string;
}) {
  return (
    <section className="rounded-[1.35rem] bg-white px-4 py-3.5 shadow-soft sm:py-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="grid size-9 shrink-0 place-items-center rounded-2xl bg-blue-50 text-primary">
            <Icon className="size-4" aria-hidden="true" />
          </span>
          <h2 className="text-base font-semibold text-foreground">{title}</h2>
        </div>
        {to ? (
          <Link
            to={to}
            aria-label={`Open ${title}`}
            className="inline-flex size-9 shrink-0 items-center justify-center rounded-full border border-primary/10 bg-white text-primary transition hover:border-primary/25 hover:bg-blue-50 focus:outline-none focus:ring-4 focus:ring-primary/10 motion-reduce:transition-none"
          >
            <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        ) : null}
        {onAction ? (
          <button
            type="button"
            onClick={onAction}
            aria-label={`Edit ${title}`}
            className="inline-flex size-9 shrink-0 items-center justify-center rounded-full border border-primary/10 bg-white text-primary transition hover:border-primary/25 hover:bg-blue-50 focus:outline-none focus:ring-4 focus:ring-primary/10 motion-reduce:transition-none"
          >
            <ArrowRight className="size-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function emergencyMedicationSummary(record: Medication) {
  if (record.schedule.slots.length) {
    return record.schedule.slots
      .slice(0, 3)
      .map((slot) =>
        [
          slot.dose_text ?? record.dose_text,
          slot.daypart ? daypartLabel(slot.daypart) : null,
          slot.time ? formatTime(slot.time) : null,
        ]
          .filter(Boolean)
          .join(" · "),
      )
      .join("; ");
  }

  return [record.dose_text, record.form, record.strength].filter(Boolean).join(" · ");
}

type EmergencyEditorSection = "contacts" | "doctor" | "hospital";

function EmergencyEditorModal({
  onClose,
  profile,
  saveProfile,
  saving,
  section,
}: {
  onClose: () => void;
  profile: AccountProfile | null;
  saveProfile: (patch: AccountProfilePatch) => Promise<AccountProfile | null>;
  saving: boolean;
  section: EmergencyEditorSection;
}) {
  const [contacts, setContacts] = useState<EmergencyContactDraft[]>(() => emergencyContactDraftsFromProfile(profile?.emergency_contacts));
  const [doctor, setDoctor] = useState<PrimaryDoctorDraft>(() => doctorDraftFromProfile(profile?.primary_doctor));
  const [hospitals, setHospitals] = useState<PreferredHospitalsDraft>(() => hospitalsDraftFromProfile(profile?.preferred_hospitals));
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    setContacts(emergencyContactDraftsFromProfile(profile?.emergency_contacts));
    setDoctor(doctorDraftFromProfile(profile?.primary_doctor));
    setHospitals(hospitalsDraftFromProfile(profile?.preferred_hospitals));
    setValidationError(null);
  }, [profile, section]);

  const title =
    section === "contacts" ? "Emergency Contacts" : section === "doctor" ? "Primary / Family Doctor" : "Preferred Hospital";

  const basePatch: Pick<AccountProfilePatch, "first_name" | "last_name" | "contact_phone_e164" | "contact_phone_country_code"> = {
    first_name: profile?.first_name ?? null,
    last_name: profile?.last_name ?? null,
    contact_phone_e164: profile?.contact_phone_e164 ?? null,
    contact_phone_country_code: profile?.contact_phone_country_code ?? null,
  };

  const handleSave = async () => {
    setValidationError(null);

    let patch: AccountProfilePatch;
    if (section === "contacts") {
      const { contacts: nextContacts, error } = emergencyContactsPayloadFromDrafts(contacts);
      if (error) {
        setValidationError(error);
        return;
      }
      patch = { ...basePatch, emergency_contacts: nextContacts };
    } else if (section === "doctor") {
      patch = { ...basePatch, primary_doctor: doctorPayloadFromDraft(doctor) };
    } else {
      const { hospitals: nextHospitals, error } = preferredHospitalsPayloadFromDraft(hospitals);
      if (error) {
        setValidationError(error);
        return;
      }
      patch = { ...basePatch, preferred_hospitals: nextHospitals };
    }

    const nextProfile = await saveProfile(patch);
    if (nextProfile) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-end bg-slate-950/35 px-0 py-0 backdrop-blur-sm sm:place-items-center sm:px-4 sm:py-8">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="emergency-editor-title"
        className="max-h-[92vh] w-full overflow-y-auto rounded-t-[1.75rem] border border-primary/10 bg-white p-5 shadow-[0_30px_100px_-45px_rgba(15,23,42,0.75)] sm:max-w-3xl sm:rounded-[1.75rem] sm:p-6"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-primary">Emergency Mode</p>
            <h2 id="emergency-editor-title" className="mt-2 text-xl font-semibold text-foreground">
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid size-10 shrink-0 place-items-center rounded-full border border-border bg-white text-muted-foreground transition hover:border-primary/30 hover:text-foreground"
            aria-label="Close Emergency Editor"
            disabled={saving}
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>

        <div className="mt-5 grid gap-4">
          {section === "contacts"
            ? contacts.map((contact, index) => (
                <div key={index} className="rounded-2xl border border-border bg-surface/70 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="font-semibold text-foreground">Contact {index + 1}</h3>
                  </div>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <TextField label="Name" value={contact.name} onChange={(value) => setContacts((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, name: value } : item)))} maxLength={80} disabled={saving} />
                    <TextField label="Relationship" value={contact.relationship} onChange={(value) => setContacts((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, relationship: value } : item)))} maxLength={80} disabled={saving} />
                    <TextField label="Primary Mobile Number" value={contact.primary_mobile_number} onChange={(value) => setContacts((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, primary_mobile_number: value } : item)))} maxLength={40} inputMode="tel" disabled={saving} />
                    <MoreDetails className="sm:col-span-2">
                      <label className="block text-sm font-semibold text-foreground">
                        Priority
                        <select
                          value={contact.priority}
                          onChange={(event) =>
                            setContacts((current) =>
                              current.map((item, itemIndex) => (itemIndex === index ? { ...item, priority: event.target.value } : item)),
                            )
                          }
                          className="mt-2 h-10 rounded-xl border border-border bg-white px-3 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                          disabled={saving}
                        >
                          {[1, 2, 3].map((priority) => (
                            <option key={priority} value={priority}>
                              {priority}
                            </option>
                          ))}
                        </select>
                      </label>
                      <TextField label="Alternate Mobile" value={contact.alternate_mobile_number} onChange={(value) => setContacts((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, alternate_mobile_number: value } : item)))} maxLength={40} inputMode="tel" disabled={saving} />
                      <TextField label="WhatsApp" value={contact.whatsapp_number} onChange={(value) => setContacts((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, whatsapp_number: value } : item)))} maxLength={40} inputMode="tel" disabled={saving} />
                      <TextAreaField label="Address" value={contact.address} onChange={(value) => setContacts((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, address: value } : item)))} maxLength={240} disabled={saving} />
                    </MoreDetails>
                  </div>
                </div>
              ))
            : null}

          {section === "doctor" ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField label="Name" value={doctor.name} onChange={(value) => setDoctor((current) => ({ ...current, name: value }))} maxLength={80} disabled={saving} />
              <TextField label="Speciality" value={doctor.speciality} onChange={(value) => setDoctor((current) => ({ ...current, speciality: value }))} maxLength={120} disabled={saving} />
              <TextField label="Hospital / Clinic Name" value={doctor.hospital_clinic_name} onChange={(value) => setDoctor((current) => ({ ...current, hospital_clinic_name: value }))} maxLength={120} disabled={saving} />
              <TextField label="Mobile Number" value={doctor.mobile_number} onChange={(value) => setDoctor((current) => ({ ...current, mobile_number: value }))} maxLength={40} inputMode="tel" disabled={saving} />
              <TextField label="Emergency Contact Number" value={doctor.emergency_contact_number} onChange={(value) => setDoctor((current) => ({ ...current, emergency_contact_number: value }))} maxLength={40} inputMode="tel" disabled={saving} />
              <TextField label="Email" value={doctor.email} onChange={(value) => setDoctor((current) => ({ ...current, email: value }))} maxLength={254} inputMode="email" disabled={saving} />
              <TextAreaField label="Address" value={doctor.address} onChange={(value) => setDoctor((current) => ({ ...current, address: value }))} maxLength={240} disabled={saving} />
              <label className="flex min-w-0 items-start gap-3 rounded-2xl border border-border bg-surface/70 px-4 py-3 text-sm font-semibold text-foreground">
                <input
                  type="checkbox"
                  checked={doctor.preferred_for_emergency}
                  onChange={(event) => setDoctor((current) => ({ ...current, preferred_for_emergency: event.target.checked }))}
                  className="mt-1 size-4 accent-primary"
                  disabled={saving}
                />
                <span>
                  Preferred For Emergency
                  <span className="mt-1 block font-normal leading-6 text-muted-foreground">Show this doctor prominently in Emergency Mode.</span>
                </span>
              </label>
            </div>
          ) : null}

          {section === "hospital" ? (
            <div className="grid gap-4">
              {(["primary", "alternate"] as const).map((kind) => (
                <div key={kind} className="rounded-2xl border border-border bg-surface/70 p-4">
                  <h3 className="font-semibold text-foreground">{kind === "primary" ? "Primary Hospital" : "Alternate Hospital"}</h3>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <TextField label="Name" value={hospitals[kind].name} onChange={(value) => setHospitals((current) => ({ ...current, [kind]: { ...current[kind], name: value } }))} maxLength={120} disabled={saving} />
                    <TextField label="Emergency Number" value={hospitals[kind].emergency_number} onChange={(value) => setHospitals((current) => ({ ...current, [kind]: { ...current[kind], emergency_number: value } }))} maxLength={40} inputMode="tel" disabled={saving} />
                    <TextField label="Ambulance Contact Number" value={hospitals[kind].ambulance_contact_number} onChange={(value) => setHospitals((current) => ({ ...current, [kind]: { ...current[kind], ambulance_contact_number: value } }))} maxLength={40} inputMode="tel" disabled={saving} />
                    <TextAreaField label="Address" value={hospitals[kind].address} onChange={(value) => setHospitals((current) => ({ ...current, [kind]: { ...current[kind], address: value } }))} maxLength={240} disabled={saving} />
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {validationError ? (
          <p className="mt-4 rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">
            {validationError}
          </p>
        ) : null}

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="inline-flex min-h-10 items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? "Saving" : "Save"}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex min-h-10 items-center justify-center rounded-full border border-border bg-white px-5 py-2.5 text-sm font-semibold text-muted-foreground transition hover:border-primary/30 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>
        </div>
      </section>
    </div>
  );
}

export function EmergencyPage() {
  const { error: profileError, profile, saveProfile, status: profileStatus } = useAccountProfile();
  // Allergies + conditions come from the shared GET /v1/overview load; the dedicated Allergies and
  // Known Health Conditions pages keep their own hooks.
  const { allergies, conditions, error: overviewError, status: overviewStatus } = useHealthOverview();
  // Emergency only renders the active medication list -- no upcoming-dose request.
  const { error: medicationsError, medications, status: medicationsStatus } = useMedicationList();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editingSection, setEditingSection] = useState<EmergencyEditorSection | null>(null);
  const displayName = getProfileDisplayName(profile);
  const importantAllergies = useMemo(() => getImportantAllergies(allergies).slice(0, 5), [allergies]);
  const activeConditions = useMemo(() => getActiveConditions(conditions).slice(0, 5), [conditions]);
  const activeMedications = useMemo(
    () => getLatestMedications(medications).filter((record) => record.status === "active"),
    [medications],
  );
  const healthInsurance = profile?.health_insurance && hasHealthInsurance(profile.health_insurance) ? profile.health_insurance : null;
  const isLoading =
    profileStatus === "loading" ||
    profileStatus === "idle" ||
    medicationsStatus === "loading" ||
    medicationsStatus === "idle" ||
    overviewStatus === "loading" ||
    overviewStatus === "idle";

  useEffect(() => {
    if (searchParams.get("edit") === "contacts") {
      setEditingSection("contacts");
    }
  }, [searchParams]);

  const closeEditor = () => {
    setEditingSection(null);
    if (searchParams.has("edit")) {
      setSearchParams({}, { replace: true });
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <PageHero icon={Cross} title="Emergency Health Info" />

      {isLoading ? <BrandedLoader compact label="Preparing emergency details" /> : null}
      {profileError || medicationsError || overviewError ? (
        <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">
          {profileError ?? medicationsError ?? overviewError}
        </p>
      ) : null}

      {!isLoading ? (
        <div className="grid gap-3.5 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
          <EmergencySection icon={UserRound} title="Person">
            <EmergencyInfoRow label="Name" value={displayName} />
            <EmergencyInfoRow label="Date of birth / age" value={formatDateOfBirthWithAge(profile?.date_of_birth)} />
            <EmergencyInfoRow label="Blood group" value={profile?.blood_group} />
            <EmergencyInfoRow label="MedoPilot ID" value={profile?.medopilot_user_id} />
            {!displayName && !profile?.date_of_birth && !profile?.blood_group && !profile?.medopilot_user_id ? (
              <p className="text-sm leading-6 text-muted-foreground">No emergency profile details have been added yet.</p>
            ) : null}
          </EmergencySection>

          <EmergencySection icon={AlertTriangle} title="Active Allergies" to="/app/allergies">
            {importantAllergies.length ? (
              <div className="grid gap-2">
                {importantAllergies.map((record) => (
                  <div key={record.allergy_id} className="rounded-2xl bg-rose-50/80 px-3 py-2.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-foreground">{record.substance_name}</span>
                      <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-rose-700 shadow-soft">
                        {allergySeverityLabel(record.severity)}
                      </span>
                    </div>
                    <span className="mt-1 block text-sm leading-6 text-muted-foreground">
                      {[allergyCategoryLabel(record.category), record.reaction].filter(Boolean).join(" · ") || "Active allergy"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm leading-6 text-muted-foreground">No active allergies have been added yet.</p>
            )}
          </EmergencySection>

          <EmergencySection icon={Pill} title="Current Active Medications" to="/app/medications">
            {activeMedications.length ? (
              <div className="grid gap-2">
                {activeMedications.map((record) => (
                  <div
                    key={record.medication_id}
                    className="rounded-2xl bg-blue-50/70 px-3 py-2.5"
                  >
                    <span className="block font-semibold text-foreground">{record.name}</span>
                    <span className="mt-1 block text-sm leading-6 text-muted-foreground">
                      {emergencyMedicationSummary(record) || "Medication details saved"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm leading-6 text-muted-foreground">No active medications have been added yet.</p>
            )}
          </EmergencySection>

          <EmergencySection icon={HeartPulse} title="Known Health Conditions" to="/app/known-health-conditions">
            {activeConditions.length ? (
              <div className="grid gap-2">
                {activeConditions.map((record) => (
                  <div key={record.condition_id} className="rounded-2xl bg-blue-50/70 px-3 py-2.5">
                    <span className="block font-semibold text-foreground">{record.condition_name}</span>
                    <span className="mt-1 block text-sm leading-6 text-muted-foreground">
                      {record.diagnosed_date ? `Diagnosed ${formatDate(record.diagnosed_date) ?? record.diagnosed_date}` : "Active condition"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm leading-6 text-muted-foreground">No active known health conditions have been added yet.</p>
            )}
          </EmergencySection>

          <EmergencySection icon={ShieldCheck} title="Emergency Contacts" onAction={() => setEditingSection("contacts")}>
            {profile?.emergency_contacts.length ? (
              <div className="grid gap-2.5">
                {profile.emergency_contacts.map((contact) => (
                  <div key={`${contact.priority}-${contact.name}`} className="rounded-2xl bg-blue-50/70 px-3 py-2.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-primary shadow-soft">Priority {contact.priority}</span>
                      <h3 className="font-semibold text-foreground">{contact.name}</h3>
                      <span className="text-sm text-muted-foreground">{contact.relationship}</span>
                    </div>
                    <div className="mt-2 grid gap-x-5 sm:grid-cols-2">
                      <EmergencyInfoRow label="Primary mobile" value={contact.primary_mobile_number} />
                      <EmergencyInfoRow label="Alternate mobile" value={contact.alternate_mobile_number} />
                      <EmergencyInfoRow label="WhatsApp number" value={contact.whatsapp_number} />
                      <EmergencyInfoRow label="Address" value={formatAddress(contact.address)} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm leading-6 text-muted-foreground">No emergency contacts have been added yet.</p>
            )}
          </EmergencySection>

          {healthInsurance ? (
            <EmergencySection icon={ShieldCheck} title="Health Insurance" to="/app/profile/insurance">
              <div className="grid gap-x-6 sm:grid-cols-2">
                <EmergencyInfoRow label="Insurance company" value={healthInsurance.insurance_company} />
                <EmergencyInfoRow label="Policy name" value={healthInsurance.policy_name} />
                <EmergencyInfoRow label="Policy number" value={healthInsurance.policy_number} />
                <EmergencyInfoRow label="Contact phone" value={healthInsurance.insurance_company_contact_phone} />
                <EmergencyInfoRow label="Contact email" value={healthInsurance.insurance_company_contact_email} />
              </div>
            </EmergencySection>
          ) : null}

          {profile?.primary_doctor ? (
            <EmergencySection icon={Stethoscope} title="Primary Doctor" onAction={() => setEditingSection("doctor")}>
              <div className="grid gap-x-6 sm:grid-cols-2">
                <EmergencyInfoRow label="Name" value={profile.primary_doctor.name} />
                <EmergencyInfoRow label="Speciality" value={profile.primary_doctor.speciality} />
                <EmergencyInfoRow label="Hospital / clinic" value={profile.primary_doctor.hospital_clinic_name} />
                <EmergencyInfoRow label="Mobile number" value={profile.primary_doctor.mobile_number} />
                <EmergencyInfoRow label="Emergency contact" value={profile.primary_doctor.emergency_contact_number} />
                <EmergencyInfoRow label="Email" value={profile.primary_doctor.email} />
                <EmergencyInfoRow label="Address" value={formatAddress(profile.primary_doctor.address)} />
              </div>
            </EmergencySection>
          ) : (
            <EmergencySection icon={Stethoscope} title="Primary Doctor" onAction={() => setEditingSection("doctor")}>
              <p className="text-sm leading-6 text-muted-foreground">No primary or family doctor has been added yet.</p>
            </EmergencySection>
          )}

          {profile?.preferred_hospitals?.primary ? (
            <EmergencySection icon={Cross} title="Preferred Hospital" onAction={() => setEditingSection("hospital")}>
              <div className="grid gap-x-6 sm:grid-cols-2">
                <EmergencyInfoRow label="Name" value={profile.preferred_hospitals.primary.name} />
                <EmergencyInfoRow label="Emergency number" value={profile.preferred_hospitals.primary.emergency_number} />
                <EmergencyInfoRow label="Ambulance contact" value={profile.preferred_hospitals.primary.ambulance_contact_number} />
                <EmergencyInfoRow label="Address" value={formatAddress(profile.preferred_hospitals.primary.address)} />
              </div>
            </EmergencySection>
          ) : (
            <EmergencySection icon={Cross} title="Preferred Hospital" onAction={() => setEditingSection("hospital")}>
              <p className="text-sm leading-6 text-muted-foreground">No preferred hospital has been added yet.</p>
            </EmergencySection>
          )}
        </div>
      ) : null}
      {editingSection ? (
        <EmergencyEditorModal
          profile={profile}
          section={editingSection}
          saveProfile={saveProfile}
          saving={profileStatus === "saving"}
          onClose={closeEditor}
        />
      ) : null}
    </div>
  );
}

function MoreNavRow({
  icon: Icon,
  label,
  onClick,
  tone = "blue",
  to,
}: {
  icon: LucideIcon;
  label: string;
  onClick?: () => void;
  tone?: ClinicalTone;
  to?: string;
}) {
  const className =
    "group flex min-h-14 w-full items-center gap-3 border-b border-border/70 py-3 text-left text-base font-semibold text-foreground transition last:border-b-0 hover:text-primary focus:outline-none focus:ring-4 focus:ring-primary/10 motion-reduce:transition-none";
  const content = (
    <>
      <span className={cn("grid size-10 shrink-0 place-items-center rounded-2xl", clinicalToneClass(tone))}>
        <Icon className="size-4" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">{label}</span>
      <ArrowRight className="size-4 shrink-0 text-primary transition group-hover:translate-x-0.5 motion-reduce:transition-none" aria-hidden="true" />
    </>
  );

  if (to) {
    return (
      <Link to={to} className={className}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className={className}>
      {content}
    </button>
  );
}

export function MorePage() {
  const auth = useAuth();

  return (
    <div className="mx-auto max-w-3xl pt-1">
      <section className="rounded-[1.5rem] bg-white px-4 shadow-soft">
        <MoreNavRow icon={UserRound} label="Profile" to="/app/profile" tone="blue" />
        <MoreNavRow icon={Lock} label="Account & Security" to="/app/account-security" tone="blue" />
        <MoreNavRow icon={ShieldCheck} label="Health Insurance" to="/app/profile/insurance" tone="emerald" />
        <MoreNavRow icon={FileText} label="My Files" to="/app/documents" tone="blue" />
        <MoreNavRow icon={ShoppingCart} label="Order Medicines" to="/app/orders" tone="amber" />
        <MoreNavRow icon={Download} label="Download Health Profile" to="/app/profile/export" tone="lavender" />
        <MoreNavRow icon={Settings} label="Settings" to="/app/settings" tone="blue" />
        <MoreNavRow icon={LogOut} label="Sign Out" onClick={() => void auth.signOut()} tone="rose" />
      </section>
    </div>
  );
}

export function SettingsPage() {
  return (
    <div className="space-y-6">
      <PageHero
        icon={Settings}
        title="Care Hub Settings"
        description="Manage your account, security, and care reminder preferences."
      />

      <Link
        to="/app/account-security"
        className="flex items-center gap-4 rounded-[1.75rem] border border-primary/10 bg-white p-5 shadow-premium transition hover:border-primary/25 focus:outline-none focus:ring-4 focus:ring-primary/10 sm:p-6"
      >
        <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-blue-50 text-primary">
          <Lock className="size-5" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-base font-semibold text-foreground">Account &amp; Security</span>
          <span className="mt-1 block text-sm leading-6 text-muted-foreground">
            Change your password, set up two-step verification, or delete your account.
          </span>
        </span>
        <ArrowRight className="size-4 shrink-0 text-primary" aria-hidden="true" />
      </Link>

      <button
        type="button"
        onClick={() => openCareRemindersDialog()}
        className="flex w-full items-center gap-4 rounded-[1.75rem] border border-primary/10 bg-white p-5 text-left shadow-premium transition hover:border-primary/25 focus:outline-none focus:ring-4 focus:ring-primary/10 sm:p-6"
      >
        <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-emerald-50 text-emerald-700">
          <Bell className="size-5" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-base font-semibold text-foreground">Care Reminders</span>
          <span className="mt-1 block text-sm leading-6 text-muted-foreground">
            Manage reminder delivery, notifications, and device settings.
          </span>
        </span>
        <ChevronRight className="size-4 shrink-0 text-primary" aria-hidden="true" />
      </button>

      <Link
        to="/app/settings/emergency-access"
        className="flex items-center gap-4 rounded-[1.75rem] border border-primary/10 bg-white p-5 shadow-premium transition hover:border-primary/25 focus:outline-none focus:ring-4 focus:ring-primary/10 sm:p-6"
      >
        <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-rose-50 text-rose-600">
          <Siren className="size-5" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-base font-semibold text-foreground">Emergency Access</span>
          <span className="mt-1 block text-sm leading-6 text-muted-foreground">
            Optionally share selected information with first responders — no sign-in required.
          </span>
        </span>
        <ArrowRight className="size-4 shrink-0 text-primary" aria-hidden="true" />
      </Link>
    </div>
  );
}

// --- Account & Security -------------------------------------------------------------------------

const securityFieldClassName =
  "mt-1 w-full rounded-xl border border-border bg-white px-3 py-2.5 text-sm text-foreground shadow-soft focus:border-primary/40 focus:outline-none focus:ring-4 focus:ring-primary/10";
const securityPrimaryButtonClassName =
  "inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:cursor-not-allowed disabled:opacity-60";
const securitySecondaryButtonClassName =
  "inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-primary/25 px-5 py-2.5 text-sm font-semibold text-primary transition hover:bg-primary/5 focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:cursor-not-allowed disabled:opacity-60";
const securityDangerButtonClassName =
  "inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-500 focus:outline-none focus:ring-4 focus:ring-rose-500/20 disabled:cursor-not-allowed disabled:opacity-60";

function SecurityCard({
  icon: Icon,
  title,
  description,
  tone = "blue",
  children,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  tone?: "blue" | "rose";
  children: ReactNode;
}) {
  return (
    <article
      className={cn(
        "min-w-0 rounded-[1.75rem] border bg-white p-5 shadow-premium sm:p-6",
        tone === "rose" ? "border-rose-200" : "border-primary/10",
      )}
    >
      <span
        className={cn(
          "grid size-11 place-items-center rounded-2xl",
          tone === "rose" ? "bg-rose-50 text-rose-600" : "bg-blue-50 text-primary",
        )}
      >
        <Icon className="size-5" aria-hidden="true" />
      </span>
      <h2 className="mt-5 text-base font-semibold text-foreground">{title}</h2>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p>
      <div className="mt-5">{children}</div>
    </article>
  );
}

function SecurityFeedback({ tone, children }: { tone: "success" | "error"; children: ReactNode }) {
  return (
    <p
      role={tone === "error" ? "alert" : "status"}
      className={cn(
        "mt-4 rounded-xl px-3.5 py-2.5 text-sm font-medium",
        tone === "error" ? "bg-destructive/10 text-destructive" : "bg-emerald-50 text-emerald-700",
      )}
    >
      {children}
    </p>
  );
}

function ChangePasswordCard({ changePassword }: { changePassword: ReturnType<typeof useCognitoSecurity>["changePassword"] }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setDone(false);
    if (next !== confirm) {
      setError("The new password and confirmation do not match.");
      return;
    }
    if (!current || !next) {
      setError("Enter your current password and a new password.");
      return;
    }
    setBusy(true);
    const failure = await changePassword(current, next);
    setBusy(false);
    if (failure) {
      setError(failure);
      return;
    }
    setCurrent("");
    setNext("");
    setConfirm("");
    setDone(true);
  };

  return (
    <SecurityCard icon={KeyRound} title="Password" description="Update the password you use to sign in to MedoPilot.">
      <form className="space-y-4" onSubmit={(event) => void handleSubmit(event)}>
        <PasswordField
          id="current-password"
          label="Current password"
          autoComplete="current-password"
          value={current}
          onChange={setCurrent}
        />
        <PasswordField
          id="new-password"
          label="New password"
          autoComplete="new-password"
          value={next}
          onChange={setNext}
        />
        <PasswordField
          id="confirm-new-password"
          label="Confirm new password"
          autoComplete="new-password"
          value={confirm}
          onChange={setConfirm}
        />
        <p className="text-xs leading-5 text-muted-foreground">
          Use at least 12 characters with upper and lower case letters, a number and a symbol.
        </p>
        <button type="submit" disabled={busy} className={securityPrimaryButtonClassName}>
          {busy ? "Updating…" : "Update password"}
        </button>
      </form>
      {error ? <SecurityFeedback tone="error">{error}</SecurityFeedback> : null}
      {done ? <SecurityFeedback tone="success">Your password has been updated.</SecurityFeedback> : null}
    </SecurityCard>
  );
}

function TotpMfaCard({
  mfaState,
  mfaPhase,
  mfaLoadError,
  refreshMfa,
  beginTotpSetup,
  confirmTotpSetup,
  disableTotp,
}: Pick<
  ReturnType<typeof useCognitoSecurity>,
  "mfaState" | "mfaPhase" | "mfaLoadError" | "refreshMfa" | "beginTotpSetup" | "confirmTotpSetup" | "disableTotp"
>) {
  const [enrolment, setEnrolment] = useState<TotpEnrolment | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmingDisable, setConfirmingDisable] = useState(false);

  const enabled = mfaState?.softwareTokenMfaEnabled ?? false;

  const startSetup = async () => {
    setError(null);
    setNotice(null);
    setBusy(true);
    const result = await beginTotpSetup();
    setBusy(false);
    if ("error" in result) {
      setError(result.error);
      return;
    }
    setEnrolment(result);
    setCode("");
  };

  const confirmSetup = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    const failure = await confirmTotpSetup(code);
    setBusy(false);
    if (failure) {
      setError(failure);
      return;
    }
    setEnrolment(null);
    setCode("");
    setNotice("Two-step verification is now on. You'll enter a code from your authenticator app when you sign in.");
  };

  const turnOff = async () => {
    setError(null);
    setNotice(null);
    setBusy(true);
    const failure = await disableTotp();
    setBusy(false);
    setConfirmingDisable(false);
    if (failure) {
      setError(failure);
      return;
    }
    setNotice("Two-step verification has been turned off.");
  };

  const copySecret = async () => {
    if (!enrolment) {
      return;
    }
    try {
      await navigator.clipboard.writeText(enrolment.secret);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <SecurityCard
      icon={ShieldCheck}
      title="Two-step verification"
      description="Add a one-time code from an authenticator app to your sign-in for extra protection."
    >
      {mfaPhase === "loading" && !mfaState ? (
        <p className="text-sm text-muted-foreground">Checking your current settings…</p>
      ) : mfaPhase === "error" && !mfaState ? (
        <div className="min-w-0 space-y-3">
          <p className="text-sm text-muted-foreground">
            {mfaLoadError ?? "We could not load your two-step verification status."}
          </p>
          <button
            type="button"
            onClick={() => void refreshMfa()}
            className={cn(securitySecondaryButtonClassName, "w-full sm:w-auto")}
          >
            Try again
          </button>
        </div>
      ) : (
        <div className="min-w-0 space-y-5">
          <MfaStatusIndicator enabled={enabled} />

          <div className="border-t border-border pt-5">
            {!enabled && !enrolment ? (
              <button
                type="button"
                onClick={() => void startSetup()}
                disabled={busy}
                className={cn(securityPrimaryButtonClassName, "w-full sm:w-auto")}
              >
                {busy ? "Preparing…" : "Set up two-step verification"}
              </button>
            ) : null}

            {!enabled && enrolment ? (
              <div className="min-w-0 space-y-4 rounded-2xl border border-border bg-surface p-4">
                <ol className="min-w-0 space-y-4 text-sm text-foreground">
                  <li className="min-w-0">
                    <p>
                      <span className="font-semibold">1.</span> Scan this QR code with your authenticator app (Google
                      Authenticator, 1Password, Authy, and similar).
                    </p>
                    <div className="mx-auto mt-3 w-full max-w-[13rem] rounded-2xl bg-white p-3 shadow-soft">
                      <QRCodeSVG
                        value={enrolment.otpauthUri}
                        size={192}
                        marginSize={4}
                        level="M"
                        title="MedoPilot two-step verification setup QR code"
                        className="h-auto w-full"
                      />
                    </div>
                    <div className="mt-3 rounded-xl bg-white/70 px-3 py-2.5">
                      <p className="text-xs text-muted-foreground">
                        On this phone, or can't scan? Enter this setup key in your authenticator app instead:
                      </p>
                      <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                        <code className="min-w-0 break-words rounded-lg bg-white px-3 py-2 font-mono text-xs tracking-widest text-foreground">
                          {formatSecretForDisplay(enrolment.secret)}
                        </code>
                        <button
                          type="button"
                          onClick={() => void copySecret()}
                          className={cn(securitySecondaryButtonClassName, "w-full sm:w-auto")}
                        >
                          {copied ? <Check className="size-4" aria-hidden="true" /> : <Copy className="size-4" aria-hidden="true" />}
                          {copied ? "Copied" : "Copy key"}
                        </button>
                      </div>
                    </div>
                  </li>
                  <li className="min-w-0">
                    <span className="font-semibold">2.</span> Enter the 6-digit code your authenticator app shows now.
                  </li>
                </ol>
                <form
                  className="flex min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end"
                  onSubmit={(event) => void confirmSetup(event)}
                >
                  <label className="min-w-0 text-sm font-medium text-foreground">
                    Authentication code
                    <input
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={code}
                      onChange={(event) => setCode(event.target.value)}
                      className={cn(securityFieldClassName, "font-mono tracking-widest sm:w-44")}
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={busy || code.trim().length < 6}
                    className={cn(securityPrimaryButtonClassName, "w-full sm:w-auto")}
                  >
                    {busy ? "Verifying…" : "Verify and turn on"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEnrolment(null);
                      setError(null);
                    }}
                    className={cn(securitySecondaryButtonClassName, "w-full sm:w-auto")}
                  >
                    Cancel
                  </button>
                </form>
              </div>
            ) : null}

            {enabled ? (
              confirmingDisable ? (
                <div className="min-w-0 space-y-3 rounded-2xl border border-rose-200 bg-rose-50/60 p-4">
                  <p className="text-sm text-foreground">
                    Turn off two-step verification? You'll sign in with just your password until you set it up again.
                  </p>
                  <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                    <button
                      type="button"
                      onClick={() => void turnOff()}
                      disabled={busy}
                      className={cn(securityDangerButtonClassName, "w-full sm:w-auto")}
                    >
                      {busy ? "Turning off…" : "Turn off"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmingDisable(false)}
                      className={cn(securitySecondaryButtonClassName, "w-full sm:w-auto")}
                    >
                      Keep it on
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmingDisable(true)}
                  className={cn(securitySecondaryButtonClassName, "w-full sm:w-auto")}
                >
                  Turn off two-step verification
                </button>
              )
            ) : null}
          </div>
        </div>
      )}
      {error ? <SecurityFeedback tone="error">{error}</SecurityFeedback> : null}
      {notice ? <SecurityFeedback tone="success">{notice}</SecurityFeedback> : null}
    </SecurityCard>
  );
}

function formatDeletionDate(value: string | null): string {
  if (!value) {
    return "about 30 days from now";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "about 30 days from now";
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: "long" }).format(parsed);
}


function DeleteAccountCard() {
  const { lifecycle, phase, error, requestDeletion } = useAccountLifecycle();
  const [confirming, setConfirming] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const pending = lifecycle?.status === "PENDING_DELETION";

  const handleRequest = async () => {
    const ok = await requestDeletion();
    if (ok) {
      // Drop straight into the suspended-account experience; the next profile read will 403.
      assignLocation("/app");
    }
  };

  return (
    <SecurityCard
      icon={Trash2}
      tone="rose"
      title="Delete account"
      description="Permanently delete your MedoPilot account and all of your health data."
    >
      {phase === "loading" && !lifecycle ? (
        <p className="text-sm text-muted-foreground">Checking your account status…</p>
      ) : pending ? (
        <p className="text-sm text-foreground">
          This account is already scheduled for permanent deletion on{" "}
          <span className="font-semibold">{formatDeletionDate(lifecycle?.deletion?.purge_after ?? null)}</span>. Reopen MedoPilot to
          see the suspended-account screen, where you can restore it during the grace period.
        </p>
      ) : !confirming ? (
        <button type="button" onClick={() => setConfirming(true)} className={securityDangerButtonClassName}>
          <Trash2 className="size-4" aria-hidden="true" />
          Delete account
        </button>
      ) : (
        <div className="space-y-4 rounded-2xl border border-rose-200 bg-rose-50/60 p-4">
          <div className="flex gap-3">
            <ShieldAlert className="mt-0.5 size-5 shrink-0 text-rose-600" aria-hidden="true" />
            <div className="space-y-2 text-sm text-foreground">
              <p className="font-semibold">This suspends your account now.</p>
              <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                <li>You'll be signed out and can't use MedoPilot while deletion is pending.</li>
                <li>Permanent deletion is scheduled 30 days later. Nothing is deleted before then.</li>
                <li>You can restore your account any time in those 30 days by signing back in.</li>
              </ul>
            </div>
          </div>
          <label className="flex items-start gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
              className="mt-0.5 size-4 rounded border-border text-rose-600 focus:ring-rose-500/30"
            />
            I understand my account will be suspended and permanently deleted after 30 days.
          </label>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => void handleRequest()}
              disabled={!acknowledged || phase === "working"}
              className={securityDangerButtonClassName}
            >
              {phase === "working" ? "Scheduling…" : "Delete my account"}
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirming(false);
                setAcknowledged(false);
              }}
              className={securitySecondaryButtonClassName}
            >
              Keep my account
            </button>
          </div>
        </div>
      )}
      {error ? <SecurityFeedback tone="error">{error}</SecurityFeedback> : null}
    </SecurityCard>
  );
}

export function AccountSecurityPage() {
  const auth = useAuth();
  const account = useMemo(() => getAccountSummary(auth.user), [auth.user]);
  const security = useCognitoSecurity();

  return (
    <div className="space-y-5">
      <BackAction to="/app/more" label="Back" />
      <PageHero
        icon={Lock}
        title="Account & Security"
        description="Manage your MedoPilot password, two-step verification and account. Passwords and two-step verification are handled securely by our sign-in provider."
      />
      <section className="rounded-2xl border border-primary/10 bg-primary/5 px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Signed in as</p>
        <p className="mt-1 font-semibold text-foreground">{account.email ?? account.displayName}</p>
      </section>
      <div className="grid gap-4 lg:grid-cols-2">
        <ChangePasswordCard changePassword={security.changePassword} />
        <TotpMfaCard
          mfaState={security.mfaState}
          mfaPhase={security.mfaPhase}
          mfaLoadError={security.mfaLoadError}
          refreshMfa={security.refreshMfa}
          beginTotpSetup={security.beginTotpSetup}
          confirmTotpSetup={security.confirmTotpSetup}
          disableTotp={security.disableTotp}
        />
      </div>
      <DeleteAccountCard />
    </div>
  );
}

// --- Emergency Access -----------------------------------------------------------------------

function sameEmergencySelection(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const bSet = new Set(b);
  return a.every((item) => bSet.has(item));
}

function toggleEmergencyValue<T extends string>(setter: Dispatch<SetStateAction<T[]>>, value: T) {
  setter((current) => (current.includes(value) ? current.filter((item) => item !== value) : [...current, value]));
}

function formatEmergencyTimestamp(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(parsed);
}

function EmergencyOptionCheckbox({
  checked,
  description,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  description?: string;
  disabled?: boolean;
  label: string;
  onChange: () => void;
}) {
  return (
    <label
      className={cn(
        "flex min-h-14 min-w-0 items-start gap-3 rounded-2xl border px-4 py-3 text-left shadow-soft transition motion-reduce:transition-none",
        checked ? "border-primary/25 bg-blue-50/70" : "border-primary/10 bg-white",
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:-translate-y-0.5 hover:border-primary/20 focus-within:ring-4 focus-within:ring-primary/10",
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="mt-0.5 size-5 shrink-0 accent-primary"
      />
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-foreground">{label}</span>
        {description ? <span className="mt-0.5 block text-xs text-muted-foreground">{description}</span> : null}
      </span>
    </label>
  );
}

export function EmergencyAccessPage() {
  const emergencyAccessState = useEmergencyAccess();
  const { profile } = useAccountProfile();
  const { allergies, conditions } = useClinicalRecords();
  const { medications } = useMedicationList();

  const [identityFields, setIdentityFields] = useState<EmergencyAccessIdentityField[]>([]);
  const [contactFields, setContactFields] = useState<EmergencyAccessContactField[]>([]);
  const [selectedAllergyIds, setSelectedAllergyIds] = useState<string[]>([]);
  const [selectedConditionIds, setSelectedConditionIds] = useState<string[]>([]);
  const [selectedMedicationIds, setSelectedMedicationIds] = useState<string[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewCard, setPreviewCard] = useState<EmergencyCardData | null>(null);
  const [confirmingEnable, setConfirmingEnable] = useState(false);
  const [confirmingDisable, setConfirmingDisable] = useState(false);
  const [confirmingDeviceEnable, setConfirmingDeviceEnable] = useState(false);
  const [acknowledgedEnable, setAcknowledgedEnable] = useState(false);

  const resetEmergencyDraft = useCallback(() => {
    if (!emergencyAccessState.emergencyAccess) {
      return;
    }
    setIdentityFields(emergencyAccessState.emergencyAccess.identity_fields);
    setContactFields(emergencyAccessState.emergencyAccess.contact_fields);
    setSelectedAllergyIds(emergencyAccessState.emergencyAccess.selected_allergy_ids);
    setSelectedConditionIds(emergencyAccessState.emergencyAccess.selected_condition_ids);
    setSelectedMedicationIds(emergencyAccessState.emergencyAccess.selected_medication_ids);
  }, [emergencyAccessState.emergencyAccess]);

  useEffect(() => {
    resetEmergencyDraft();
  }, [resetEmergencyDraft]);

  const isBusy = emergencyAccessState.status === "loading" || emergencyAccessState.status === "saving";
  const enabled = emergencyAccessState.emergencyAccess?.enabled ?? false;

  const selectionsDirty = useMemo(() => {
    const saved = emergencyAccessState.emergencyAccess;
    if (!saved) {
      return false;
    }
    return (
      !sameEmergencySelection(identityFields, saved.identity_fields) ||
      !sameEmergencySelection(contactFields, saved.contact_fields) ||
      !sameEmergencySelection(selectedAllergyIds, saved.selected_allergy_ids) ||
      !sameEmergencySelection(selectedConditionIds, saved.selected_condition_ids) ||
      !sameEmergencySelection(selectedMedicationIds, saved.selected_medication_ids)
    );
  }, [emergencyAccessState.emergencyAccess, identityFields, contactFields, selectedAllergyIds, selectedConditionIds, selectedMedicationIds]);

  const hasEmergencyContact = Boolean(profile?.emergency_contacts?.length);
  const hasPrimaryDoctor = Boolean(profile?.primary_doctor);
  const hasPreferredHospital = Boolean(profile?.preferred_hospitals?.primary || profile?.preferred_hospitals?.alternate);
  const hasInsurance = Boolean(profile?.health_insurance);

  const handleSave = async () => {
    await emergencyAccessState.saveSelections({
      identity_fields: identityFields,
      contact_fields: contactFields,
      selected_allergy_ids: selectedAllergyIds,
      selected_condition_ids: selectedConditionIds,
      selected_medication_ids: selectedMedicationIds,
    });
  };

  const handlePreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    const result = await emergencyAccessState.preview();
    setPreviewCard(result);
    setPreviewLoading(false);
  };

  const handleEnable = async () => {
    const result = await emergencyAccessState.enable();
    if (result) {
      setConfirmingEnable(false);
      setAcknowledgedEnable(false);
    }
  };

  const handleDisable = async () => {
    const ok = await emergencyAccessState.disable();
    if (ok) {
      setConfirmingDisable(false);
      setConfirmingDeviceEnable(false);
    }
  };

  const handleDeviceEnable = async () => {
    const ok = await emergencyAccessState.enableCurrentDevice();
    if (ok) {
      setConfirmingDeviceEnable(false);
    }
  };

  return (
    <div className="min-w-0 max-w-full space-y-5">
      <BackAction to="/app/settings" label="Back to Settings" />
      <PageHero
        icon={Siren}
        title="Emergency Access"
        description="Choose the health information available in an emergency."
      />

      <SecurityCard
        icon={Siren}
        tone={enabled ? "rose" : "blue"}
        title="Emergency Access"
        description={
          enabled
            ? "Your selected emergency information can be viewed without signing in on devices you authorize."
            : "Allow your selected emergency information to be viewed without signing in on devices you authorize."
        }
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span
            className={cn(
              "inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-bold",
              enabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600",
            )}
          >
            {enabled ? <Check className="size-4" aria-hidden="true" /> : <X className="size-4" aria-hidden="true" />}
            {enabled ? "On" : "Off"}
          </span>
          {emergencyAccessState.emergencyAccess?.updated_at ? (
            <p className="text-xs text-muted-foreground">
              Selections last saved {formatEmergencyTimestamp(emergencyAccessState.emergencyAccess.updated_at)}
            </p>
          ) : null}
        </div>

        <div className="mt-5">
          {enabled ? (
            !confirmingDisable ? (
              <button type="button" onClick={() => setConfirmingDisable(true)} className={securityDangerButtonClassName}>
                <Siren className="size-4" aria-hidden="true" />
                Disable Emergency Access
              </button>
            ) : (
              <div className="space-y-4 rounded-2xl border border-rose-200 bg-rose-50/60 p-4">
                <div className="flex gap-3">
                  <ShieldAlert className="mt-0.5 size-5 shrink-0 text-rose-600" aria-hidden="true" />
                  <p className="text-sm font-semibold text-foreground">
                    This turns off Emergency Access and revokes Emergency Info on every authorized device. You are not
                    asked to confirm again once you proceed.
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => void handleDisable()}
                    disabled={emergencyAccessState.status === "saving"}
                    className={securityDangerButtonClassName}
                  >
                    {emergencyAccessState.status === "saving" ? "Disabling…" : "Disable"}
                  </button>
                  <button type="button" onClick={() => setConfirmingDisable(false)} className={securitySecondaryButtonClassName}>
                    Cancel
                  </button>
                </div>
              </div>
            )
          ) : !confirmingEnable ? (
            <button type="button" onClick={() => setConfirmingEnable(true)} className={securityPrimaryButtonClassName}>
              <Siren className="size-4" aria-hidden="true" />
              Enable Emergency Access
            </button>
          ) : (
            <div className="space-y-4 rounded-2xl border border-primary/15 bg-blue-50/50 p-4">
              <div className="flex gap-3">
                <ShieldAlert className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden="true" />
                <div className="space-y-2 text-sm text-foreground">
                  <p className="font-semibold">
                    Turning this on allows Emergency Info on devices you authorize without signing in to MedoPilot.
                  </p>
                  <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                    <li>Only the items checked below are available. Nothing else in MedoPilot becomes public.</li>
                    <li>This device will be allowed automatically after Emergency Access is enabled.</li>
                    <li>You can disable Emergency Access at any time to revoke every authorized device.</li>
                  </ul>
                </div>
              </div>
              <label className="flex items-start gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={acknowledgedEnable}
                  onChange={(event) => setAcknowledgedEnable(event.target.checked)}
                  className="mt-0.5 size-4 rounded border-border text-primary focus:ring-primary/30"
                />
                I understand my selected information will be viewable without signing in to MedoPilot.
              </label>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => void handleEnable()}
                  disabled={!acknowledgedEnable || emergencyAccessState.status === "saving"}
                  className={securityPrimaryButtonClassName}
                >
                  {emergencyAccessState.status === "saving" ? "Enabling…" : "Enable Emergency Access"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmingEnable(false);
                    setAcknowledgedEnable(false);
                  }}
                  className={securitySecondaryButtonClassName}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {emergencyAccessState.error ? <SecurityFeedback tone="error">{emergencyAccessState.error}</SecurityFeedback> : null}
      </SecurityCard>

      <SecurityCard
        icon={Smartphone}
        tone="blue"
        title="Emergency access on this device"
        description={
          enabled
            ? "Allow Emergency access from this device without signing in."
            : "Turn on Emergency Access before allowing emergency viewing from this device."
        }
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
            {emergencyAccessState.deviceEmergencyAccessEnabled ? (
              <Check className="size-4 text-emerald-700" aria-hidden="true" />
            ) : (
              <X className="size-4 text-muted-foreground" aria-hidden="true" />
            )}
            {emergencyAccessState.deviceEmergencyAccessEnabled ? "Allowed on this device" : "Not allowed on this device"}
          </span>
          {emergencyAccessState.deviceStatus === "loading" ? (
            <span className="text-sm text-muted-foreground">Checking device…</span>
          ) : null}
        </div>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">
          Emergency Access is enabled separately on each device. To use Emergency Info on another phone, tablet, or
          computer, sign in to MedoPilot on that device and allow Emergency Access there.
        </p>
        {emergencyAccessState.deviceEmergencyAccessEnabled ? (
          <button
            type="button"
            onClick={() => void emergencyAccessState.disableCurrentDevice()}
            disabled={emergencyAccessState.deviceStatus === "saving"}
            className={securitySecondaryButtonClassName}
          >
            <Smartphone className="size-4" aria-hidden="true" />
            {emergencyAccessState.deviceStatus === "saving" ? "Forgetting…" : "Forget this device"}
          </button>
        ) : !confirmingDeviceEnable ? (
          <button
            type="button"
            onClick={() => setConfirmingDeviceEnable(true)}
            disabled={!enabled || emergencyAccessState.deviceStatus === "saving"}
            className={securityPrimaryButtonClassName}
          >
            <Smartphone className="size-4" aria-hidden="true" />
            Allow on this device
          </button>
        ) : (
          <div className="space-y-4 rounded-2xl border border-primary/15 bg-blue-50/50 p-4">
            <p className="text-sm font-semibold leading-6 text-foreground">
              This allows Emergency access from this browser or installed app without signing in.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => void handleDeviceEnable()}
                disabled={!enabled || emergencyAccessState.deviceStatus === "saving"}
                className={securityPrimaryButtonClassName}
              >
                {emergencyAccessState.deviceStatus === "saving" ? "Allowing…" : "Allow Emergency access"}
              </button>
              <button type="button" onClick={() => setConfirmingDeviceEnable(false)} className={securitySecondaryButtonClassName}>
                Cancel
              </button>
            </div>
          </div>
        )}
        {emergencyAccessState.deviceError ? <SecurityFeedback tone="error">{emergencyAccessState.deviceError}</SecurityFeedback> : null}
      </SecurityCard>

      <section className="rounded-[1.75rem] border border-primary/10 bg-white p-5 shadow-premium sm:p-6">
        <h2 className="text-base font-semibold text-foreground">Information included</h2>
        <p className="mt-1 text-sm leading-6 text-muted-foreground">
          Only information you deliberately select below is included in Emergency Access.
        </p>
      </section>

      <section className="rounded-[1.75rem] border border-primary/10 bg-white p-5 shadow-premium sm:p-6">
        <h2 className="text-base font-semibold text-foreground">Identity</h2>
        <p className="mt-1 text-sm text-muted-foreground">Choose what appears at the top of your card.</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          <EmergencyOptionCheckbox
            label="Full name"
            checked={identityFields.includes("full_name")}
            onChange={() => toggleEmergencyValue<EmergencyAccessIdentityField>(setIdentityFields, "full_name")}
            disabled={isBusy || !(profile?.first_name || profile?.last_name)}
          />
          <EmergencyOptionCheckbox
            label="Age / date of birth"
            checked={identityFields.includes("date_of_birth")}
            onChange={() => toggleEmergencyValue<EmergencyAccessIdentityField>(setIdentityFields, "date_of_birth")}
            disabled={isBusy || !profile?.date_of_birth}
          />
          <EmergencyOptionCheckbox
            label="Blood group"
            checked={identityFields.includes("blood_group")}
            onChange={() => toggleEmergencyValue<EmergencyAccessIdentityField>(setIdentityFields, "blood_group")}
            disabled={isBusy || !profile?.blood_group}
          />
        </div>
      </section>

      <section className="rounded-[1.75rem] border border-primary/10 bg-white p-5 shadow-premium sm:p-6">
        <h2 className="text-base font-semibold text-foreground">Emergency &amp; Care Contacts</h2>
        <p className="mt-1 text-sm text-muted-foreground">Reflects the contacts already saved on your profile.</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <EmergencyOptionCheckbox
            label="Emergency contact"
            checked={contactFields.includes("emergency_contact")}
            onChange={() => toggleEmergencyValue<EmergencyAccessContactField>(setContactFields, "emergency_contact")}
            disabled={isBusy || !hasEmergencyContact}
          />
          <EmergencyOptionCheckbox
            label="Primary doctor"
            checked={contactFields.includes("primary_doctor")}
            onChange={() => toggleEmergencyValue<EmergencyAccessContactField>(setContactFields, "primary_doctor")}
            disabled={isBusy || !hasPrimaryDoctor}
          />
          <EmergencyOptionCheckbox
            label="Preferred hospital"
            checked={contactFields.includes("preferred_hospital")}
            onChange={() => toggleEmergencyValue<EmergencyAccessContactField>(setContactFields, "preferred_hospital")}
            disabled={isBusy || !hasPreferredHospital}
          />
          <EmergencyOptionCheckbox
            label="Insurance"
            checked={contactFields.includes("insurance")}
            onChange={() => toggleEmergencyValue<EmergencyAccessContactField>(setContactFields, "insurance")}
            disabled={isBusy || !hasInsurance}
          />
        </div>
        {!hasEmergencyContact && !hasPrimaryDoctor && !hasPreferredHospital && !hasInsurance ? (
          <p className="mt-3 text-sm text-muted-foreground">
            Add these details to your{" "}
            <Link to="/app/profile/edit" className="font-semibold text-primary">
              profile
            </Link>{" "}
            to make them selectable here.
          </p>
        ) : null}
      </section>

      <section className="rounded-[1.75rem] border border-primary/10 bg-white p-5 shadow-premium sm:p-6">
        <h2 className="text-base font-semibold text-foreground">Allergies</h2>
        <p className="mt-1 text-sm text-muted-foreground">Choose allergies to include.</p>
        {allergies.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">No allergy records yet.</p>
        ) : (
          <div className="mt-3 space-y-2">
            {allergies.map((allergy) => (
              <EmergencyOptionCheckbox
                key={allergy.allergy_id}
                label={allergy.substance_name}
                description={allergy.reaction ?? undefined}
                checked={selectedAllergyIds.includes(allergy.allergy_id)}
                onChange={() => toggleEmergencyValue(setSelectedAllergyIds, allergy.allergy_id)}
                disabled={isBusy}
              />
            ))}
          </div>
        )}
      </section>

      <section className="rounded-[1.75rem] border border-primary/10 bg-white p-5 shadow-premium sm:p-6">
        <h2 className="text-base font-semibold text-foreground">Known Health Conditions</h2>
        <p className="mt-1 text-sm text-muted-foreground">Choose conditions to include.</p>
        {conditions.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">No known health conditions yet.</p>
        ) : (
          <div className="mt-3 space-y-2">
            {conditions.map((condition) => (
              <EmergencyOptionCheckbox
                key={condition.condition_id}
                label={condition.condition_name}
                checked={selectedConditionIds.includes(condition.condition_id)}
                onChange={() => toggleEmergencyValue(setSelectedConditionIds, condition.condition_id)}
                disabled={isBusy}
              />
            ))}
          </div>
        )}
      </section>

      <section className="rounded-[1.75rem] border border-primary/10 bg-white p-5 shadow-premium sm:p-6">
        <h2 className="text-base font-semibold text-foreground">Medications</h2>
        <p className="mt-1 text-sm text-muted-foreground">Choose medications to include.</p>
        {medications.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">No medication records yet.</p>
        ) : (
          <div className="mt-3 space-y-2">
            {medications.map((medication) => (
              <EmergencyOptionCheckbox
                key={medication.medication_id}
                label={medication.name}
                description={[medication.strength, medication.dose_text].filter(Boolean).join(" · ") || undefined}
                checked={selectedMedicationIds.includes(medication.medication_id)}
                onChange={() => toggleEmergencyValue(setSelectedMedicationIds, medication.medication_id)}
                disabled={isBusy}
              />
            ))}
          </div>
        )}
      </section>

      <div className="flex flex-col gap-3 rounded-[1.75rem] border border-primary/10 bg-white p-4 shadow-premium sm:flex-row sm:items-center sm:justify-between sm:p-5">
        <button type="button" onClick={() => void handlePreview()} className={securitySecondaryButtonClassName}>
          <Eye className="size-4" aria-hidden="true" />
          Preview Emergency Card
        </button>
        <button type="button" onClick={() => void handleSave()} disabled={!selectionsDirty || isBusy} className={securityPrimaryButtonClassName}>
          {emergencyAccessState.status === "saving" ? "Saving…" : "Save Selections"}
        </button>
      </div>

      {previewOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Emergency Card preview"
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
        >
          <div className="max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-t-[1.75rem] bg-surface p-4 shadow-premium sm:rounded-[1.75rem] sm:p-6">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base font-semibold text-foreground">Emergency Card Preview</h2>
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                aria-label="Close preview"
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="size-5" aria-hidden="true" />
              </button>
            </div>
            {previewLoading ? (
              <p className="py-8 text-center text-sm text-muted-foreground">Loading preview…</p>
            ) : previewCard ? (
              <EmergencyCard card={previewCard} variant="preview" />
            ) : (
              <p className="py-8 text-center text-sm text-muted-foreground">We could not load the preview. Please try again.</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SuspendedAccountExperience() {
  const auth = useAuth();
  const { lifecycle, phase, error, cancelDeletion } = useAccountLifecycle();
  const [restoreError, setRestoreError] = useState<string | null>(null);

  const handleRestore = async () => {
    setRestoreError(null);
    const ok = await cancelDeletion();
    if (ok) {
      // Full reload -- the next profile read now returns 200 and the normal workspace loads.
      assignLocation("/app");
      return;
    }
    setRestoreError("We could not restore your account. Please try again.");
  };

  const purgeAfter = lifecycle?.deletion?.purge_after ?? null;
  const deletionDate = formatDeletionDate(purgeAfter);
  const daysRemaining = graceDaysRemaining(purgeAfter);
  const loading = phase === "loading" && !lifecycle;
  const graceEnded = daysRemaining !== null && daysRemaining <= 0;

  return (
    <div className="flex min-h-dvh items-center justify-center bg-[linear-gradient(135deg,#f8fbff,#eef4fb_58%,#f9fbff)] px-4 py-10 text-foreground">
      <div className="w-full max-w-lg rounded-[2rem] border border-primary/10 bg-white p-6 shadow-premium sm:p-8">
        <span className="grid size-12 place-items-center rounded-2xl bg-rose-50 text-rose-600">
          <ShieldAlert className="size-6" aria-hidden="true" />
        </span>
        <h1 className="mt-5 text-xl font-semibold text-foreground sm:text-2xl">Your account is scheduled for deletion</h1>

        {loading ? (
          <p className="mt-3 text-sm leading-6 text-muted-foreground">Loading your account status…</p>
        ) : (
          <>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              You asked us to delete this MedoPilot account, so access is suspended for now. Your health data is still
              here and nothing is deleted before the date below.
            </p>
            <dl className="mt-5 grid gap-3 rounded-2xl border border-border bg-surface p-4 sm:grid-cols-2">
              <div>
                <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Permanent deletion</dt>
                <dd className="mt-1 text-sm font-semibold text-foreground">{deletionDate}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Time to restore</dt>
                <dd className="mt-1 text-sm font-semibold text-foreground">
                  {graceEnded
                    ? "The grace period has ended"
                    : daysRemaining === null
                      ? "Available now"
                      : `${daysRemaining} ${daysRemaining === 1 ? "day" : "days"} remaining`}
                </dd>
              </div>
            </dl>
            {graceEnded ? (
              <p className="mt-4 text-sm leading-6 text-muted-foreground">
                The 30-day window to restore this account has passed, so it can no longer be recovered from here.
              </p>
            ) : null}
          </>
        )}

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          {!graceEnded ? (
            <button
              type="button"
              onClick={() => void handleRestore()}
              disabled={phase === "working" || loading}
              className={securityPrimaryButtonClassName}
            >
              {phase === "working" ? "Restoring…" : "Restore account"}
            </button>
          ) : null}
          <button type="button" onClick={() => void auth.signOut()} className={securitySecondaryButtonClassName}>
            <LogOut className="size-4" aria-hidden="true" />
            Sign out
          </button>
        </div>
        {(restoreError ?? (phase === "error" ? error : null)) ? (
          <SecurityFeedback tone="error">{restoreError ?? error}</SecurityFeedback>
        ) : null}
      </div>
    </div>
  );
}
